import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OccupancyButton, OccupancyModal, OccupancySelection } from './occupancy';
import { createRoot } from 'react-dom/client';
import { createPortal } from 'react-dom';

// Central fetch wrapper. Besides cookies+JSON headers it watches for session
// expiry: a 401 on a non-auth endpoint (with no flow-specific error code in
// the body) means the TeacherSession cookie died mid-use — we broadcast
// SESSION_EXPIRED_EVENT so the App can notify the teacher and route them back
// to the login page, instead of leaving them clicking into silent failures.
const SESSION_EXPIRED_EVENT = 'irgon:teacher-session-expired';

// 401s that are part of a normal flow (wrong SMS code, expired challenge…) —
// the calling component renders its own message; never treat as expiry.
const FLOW_401_CODES = new Set(['invalid_code', 'no_active_challenge', 'tz_not_in_igm', 'invalid_credentials']);

// How long before "שלח שוב" unlocks. It used to be 300 — the same as the life
// of the code — so a teacher whose SMS was slow watched their code expire with
// no way to ask for another one.
const RESEND_COOLDOWN_SECONDS = 60;

const api = async (path: string, init?: RequestInit) => {
  const res = await fetch(path, { ...init, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) } });
  if (res.status === 401 && !path.startsWith('/api/front/auth/') && !path.startsWith('/api/agent/') && path !== '/api/front/me') {
    let flowCode: string | null = null;
    try {
      const body = await res.clone().json();
      flowCode = typeof body?.code === 'string' ? body.code : null;
    } catch { /* empty body — the cookie-auth challenge itself */ }
    if (!flowCode || !FLOW_401_CODES.has(flowCode)) {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    }
  }
  return res;
};

// ============================================================================
// Shared types — mirror backend DTOs (JSON is camelCase via System.Text.Json)
// ============================================================================

// Matches GET /api/front/me (TeacherMeDto) — fields live at the TOP level.
// Earlier this assumed a nested `teacher` wrapper, which silently blanked
// every form pre-fill that read me.teacher.* — chips showed "—" because
// the data lived at me.fullName/me.tz/me.mobile, not under .teacher.
type Me = {
  tz: string;
  fullName: string;
  mobile?: string | null;
  email?: string | null;
  isMember?: boolean;
  eligibleYear?: number;
  entitlementUsed?: boolean;
  // Years in which the entitlement is already consumed. Drives the per-deal
  // eligibility check: subsidy lines only render when the deal's check-in
  // year is NOT in this set. Empty/missing → fall back to entitlementUsed
  // (current-year flag) for legacy payloads.
  entitlementUsedYears?: number[];
  // Set by the server for test accounts only (BookingOptions
  // .ContactPhoneEditableTzs). When true the checkout lets the phone be typed
  // instead of pinning it to the profile mobile — the client is told only
  // about itself, never which accounts are on the list.
  canEditContactPhone?: boolean;
  // Set when an agent is operating the site on this teacher's behalf. Nothing
  // about the booking changes; the screen has to say so.
  actingAgent?: string | null;
};
type LocalizedText = { he: string; en?: string | null; ru?: string | null };
type Category = {
  id: string;
  code: string;
  name: LocalizedText;
  description?: LocalizedText;
  displayOrder: number;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  // Set only for a category the admin marked "הצגה" AND attached an image to:
  // the show's poster. The hotel card's photo slot carries the HOTEL picture,
  // so the poster gets its own place inside the card (see HotelDealCard).
  showImageUrl?: string | null;
  showHomePrices?: boolean;
  fromGrossPrice?: number | null;
  fromNetPrice?: number | null;
  currency?: string | null;
  isSoldOut?: boolean;
  // The cooperative's yearly subsidy for this category, as the admin set it.
  // 0 for retiree categories. Spanned into the home hero's subsidy sentence.
  memberSubsidyAmount?: number;
};

type FrontHotelSummary = {
  id: string;
  name: LocalizedText;
  destination: string;
  starRating?: number | null;
  address?: string | null;
  thumbnailUrl?: string | null;
};

type FrontDealCombo = { showTitle: LocalizedText; addOnPrice: number };

type DealVariantSummary = {
  id: string;
  categoryId: string;
  hotel: FrontHotelSummary;
  templateTitle?: LocalizedText | null;
  checkIn: string;   // ISO yyyy-MM-dd
  checkOut: string;
  nights: number;
  roomType: string;
  roomClass: string;
  // Localized names sourced from Atlantis at sourcing time (RoomTypeNameLoc /
  // RoomClassNameLoc) — already fall back to the raw code when Atlantis didn't
  // send a name. See FrontDealsApiController:178-179.
  roomTypeName?: string;
  roomClassName?: string;
  boardBasis: string;
  boardBasisName?: string;
  // Supplier rate-list letter ("D" standard, "F" productions) — carried into the
  // deal link so a shared URL identifies which of two same-hotel/same-date price
  // lists it means. The server never reads it back; it works off the stored deal.
  hotelCategoryCode?: string | null;
  grossPrice: number;
  subsidyAmount: number;
  netPrice: number;
  currency: string;
  combo?: FrontDealCombo | null;
  // Stored room count from the last supplier sync. NOT the remaining stock: it
  // is a snapshot of SearchHotels' AvailRooms, nothing decrements it when a room
  // sells, and it has read 25 on a hotel-night whose 25 contracted rooms were all
  // confirmed. Kept because the search screen still uses `<= 0` as a coarse
  // "this date is dead" hint, and never shown to a member as a number.
  allotment: number;
  // Rooms REALLY left, from the supplier's allotment ledger — the only number a
  // member may be shown. Absent on the server payload and filled in client-side
  // by the rooms-left fetch (see fillRoomsLeft); stays undefined when the ledger
  // could not answer, and the chip then renders nothing. See RoomsLeft.
  roomsLeft?: number | null;
  // When true, the date-picker cell for this variant's checkIn day renders as
  // disabled with an "אזלו המקומות" badge. OR of the per-variant admin flag,
  // the periodic-scan flip, and the parent category's sold-out flag.
  isSoldOut?: boolean;
  // Atlantis rate-level note for THIS date — the supplier's "הערה לאינטרנט"
  // (Rate_Remark type INT/NET, which carry identical text on the live
  // inventory). Shown on the card as "הערות למבצע". Deliberately NOT the
  // RateRemarkPax variant: that one carries internal tags such as "הפקה".
  dealRemark?: string | null;
  // Poster attached to THIS deal by the operator (admin → קטגוריות → טבלת
  // הדילים). In a "חופשה והופעה" category every date is a different show, so
  // this wins over the category-wide poster whenever it is set.
  showImageUrl?: string | null;
};

type DealVariantDetail = DealVariantSummary & {
  // The two prices BEFORE the category's subsidy came off, for the checkout
  // breakdown. Sent by the API rather than derived here — Deduct floors at
  // zero, so adding subsidyAmount back on would overstate a deal the subsidy
  // fully covers.
  originalGrossPrice?: number;
  originalNetPrice?: number;
  imageUrls?: string[];
  hotelDescription?: string | null;
  // When the category is flagged "for retirees", the subsidy mechanism is
  // bypassed entirely — no entitlement check, no 700 NIS discount; the teacher
  // pays the gross price like a regular tourist booking.
  categoryIsForRetirees?: boolean;
  // Yearly subsidy for this deal's category — the amount the booking carries
  // to Atlantis, printed on the checkout summary.
  categoryMemberSubsidy?: number;
};

// Returned by GET /api/front/deals/{id}/hotel-notes — the hotel-info popup the
// category card's image opens. Sourced from the Flying Carpet catalog with the
// deal's Atlantis snapshot as the fallback; cached server-side for 5 minutes
// per hotel. Addressed by deal id because the deal carries the (supplier, code)
// tuple the upstream lookup needs.
type HotelNotes = {
  hotelId: string;
  hotelName?: string | null;
  remark?: string | null;
  description?: string | null;
  facilities?: string[];
  // Catalog gallery for the popup, first = main.
  imageUrls?: string[];
  source: 'catalog' | 'atlantis' | 'none' | string;
};

// Returned by GET /api/front/deals/{id}/live-availability — the deal page hits
// this on mount to refresh the sold-out banner against Atlantis. Cached server-
// side for 20s (FrontDealsApiController.LiveAvailabilityCacheTtl).
type LiveAvailability = {
  isAvailable: boolean;
  isSoldOut: boolean;
  // Free-sale availability from SearchHotels — NOT a room count to show anyone.
  availRooms?: number | null;
  // Rooms left per the supplier's allotment ledger. Null when it could not tell
  // (no gateway, gateway down, or a free-sale night with no allotment), and the
  // page then shows no number at all.
  roomsLeft?: number | null;
  fetchedAt: string;
  source: 'live' | 'cache' | 'stored' | string;
};

type GuestForm = { firstName: string; lastName: string; tz: string; phone: string; email: string };

// Returned by GET /api/front/booking/pending when this teacher has an
// in-progress reservation (state in {AtlantisHeld, AwaitingPayment}, real PNR)
// on any deal. The deal page uses it to block the booking form whenever the
// pending reservation is on a *different* deal — the teacher must finish
// payment on the original deal page (or wait for the sweeper to auto-cancel)
// before starting a new booking.
type PendingBooking = {
  reservationId: string;
  dealVariantId: string;
  hotelName?: LocalizedText | null;
  checkIn: string;   // ISO yyyy-MM-dd
  checkOut: string;
  atlantisPnr?: string | null;
  publicNumber: number;
  state: string;
  createdAt: string;
};

// The resume path answers mode 'Paid' (no URL) when the open booking turns out
// to be paid already — the teacher goes to the confirmation, not to a second
// payment form.
function showConfirmationIfPaid(data: BookingInitResponse): boolean {
  if (data.mode !== 'Paid') return false;
  const ref = data.atlantisPnr || data.reservationId;
  window.location.hash = `#/booking-complete/${encodeURIComponent(ref)}`;
  return true;
}

type BookingInitResponse = {
  reservationId: string;
  mode: 'OK' | 'RQ' | 'Paid' | string;
  paymentUrl?: string | null;
  rqTokenUrl?: string | null;
  publicNumber?: number | null;
  atlantisPnr?: string | null;
  confirmationKey?: string | null;
  amount?: number | null;
  currency?: string | null;
};

type DestinationGroup = { destination: string; label?: string | null; items: DealVariantSummary[] };
type DealsByCategoryResponse = { categoryId: string; groups: DestinationGroup[] };

type MyBooking = {
  reservationId: string;
  publicNumber: number;
  state: string;
  atlantisPnr?: string | null;
  atlantisOrderStatus?: string | null;
  hotelName?: LocalizedText | null;
  checkIn: string;
  checkOut: string;
  netPrice: number;
  currency: string;
  createdAt: string;
  // Masked cardholder details echoed by Pelecard at completion. Both null
  // when the reservation hasn't completed a Pelecard transaction yet (e.g.
  // still in Initiated/Failed state).
  payerNameMasked?: string | null;
  payerTzMasked?: string | null;
};
type MyBookingsResponse = { items: MyBooking[] };

type View =
  | { kind: 'home' }
  | { kind: 'login' }
  | { kind: 'my-bookings' }
  | { kind: 'shabbat' }
  | { kind: 'accessibility' }
  | { kind: 'category-detail'; category: Category }
  | { kind: 'deal-detail'; dealVariantId: string; backCategory?: Category; params?: Record<string, string> }
  | { kind: 'booking-complete'; reservationId: string; params?: Record<string, string> };

// ============================================================================
// Small formatting helpers
// ============================================================================

const fmtNum = (n: number) => n.toLocaleString('he-IL');
const fmtMoney = (n: number, currency: string) => {
  const c = (currency === 'ILS' || currency === 'IS') ? '₪' : currency;
  return `${fmtNum(Math.round(n))} ${c}`;
};
const fmtDate = (iso: string) => {
  // backend sends yyyy-MM-dd; render as dd/MM/yyyy
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
};
// CSS `url(...)` with an UNQUOTED value is terminated by the first ")" — and
// Flying Carpet catalog filenames really do contain parentheses, e.g.
// …/Pages/GB-Exterior2-(L)(1).jpg. That one URL made the whole background-image
// declaration invalid, so Grand Court's card rendered blank while every other
// hotel worked. Quote the value and escape what would end the string.
const cssUrl = (url: string): string => `url("${url.replace(/["\\]/g, m => '\\' + m)}")`;

const fmtDateTime = (iso: string) => {
  try { return new Date(iso).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

// Mirrors the DestinationCode enum (IrgunHaMorim.Web/Models/Enums.cs) — keep the
// labels in sync when the enum changes. Codes the front receives but we haven't
// mapped fall back to showing the raw code, which is at least informative.
const DESTINATION_HE: Record<string, string> = {
  TLV: 'תל אביב',
  JER: 'ירושלים',
  ETH: 'אילת',
  TIB: 'טבריה',
  NOR: 'צפון',
  DES: 'ים המלח',
  HFA: 'חיפה',
  KAR: 'אירוח כפרי - צפון',
  NAT: 'נתניה',
  HER: 'הרצליה',
  CEN: 'מרכז',
  NAH: 'נהריה',
  NAZ: 'נצרת',
  MZR: 'מצפה רמון',
  KAS: 'אירוח כפרי - דרום',
  RMT: 'רמת גן',
  ACO: 'עכו',
  BAT: 'בת ים',
  ZEC: 'זיכרון יעקב',
  ARA: 'ערד',
  ASD: 'אשדוד',
  REH: 'רחובות',
  SOU: 'דרום',
  ZFA: 'צפת',
  ASH: 'אשקלון',
  BRS: 'באר שבע',
  CES: 'קיסריה',
  HED: 'חדרה',
  RAN: 'רעננה',
};
const destNameHe = (code: string | null | undefined): string => {
  if (!code) return '';
  const k = code.trim();
  if (!k) return '';
  return DESTINATION_HE[k] ?? k;
};

const PinIcon: React.FC<{ size?: number; className?: string }> = ({ size = 14, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    className={className}
    style={{ display: 'inline-block', verticalAlign: '-2px', flexShrink: 0 }}
  >
    <path
      d="M12 22s7-7.05 7-12a7 7 0 1 0-14 0c0 4.95 7 12 7 12z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.4" fill="currentColor" />
  </svg>
);

// Small stroke-style SVG icons (currentColor) — same pattern as PinIcon.
// Replace the emoji glyphs on the category cards/rows so the iconography is
// consistent across platforms (emoji render differently per OS).
const iconStyle: React.CSSProperties = { display: 'inline-block', verticalAlign: '-2px', flexShrink: 0 };

const CalendarIcon: React.FC<{ size?: number; className?: string }> = ({ size = 15, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className} style={iconStyle}>
    <rect x="3" y="5" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 9.5h18" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M7.5 13.5h3M13.5 13.5h3M7.5 17h3M13.5 17h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const MoonIcon: React.FC<{ size?: number; className?: string }> = ({ size = 15, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className} style={iconStyle}>
    <path
      d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DiningIcon: React.FC<{ size?: number; className?: string }> = ({ size = 15, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className} style={iconStyle}>
    <path d="M7 2.5v6.5M4.5 2.5V7a2.5 2.5 0 0 0 5 0V2.5M7 9v12.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M17 2.5c-1.7 1-2.5 3.2-2.5 5.5 0 2 .9 3 2.5 3v10.5M17 2.5V11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Account avatar — mobile header only, opens the name / bookings / logout menu.
const UserIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className} style={iconStyle}>
    <circle cx="12" cy="12" r="9.2" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="12" cy="9.6" r="3" stroke="currentColor" strokeWidth="1.7" />
    <path d="M5.6 19.2a6.9 6.9 0 0 1 12.8 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

// Day-of-week line ("רביעי - חמישי"). A clock face rather than a second
// calendar, so it is not mistaken for the date row directly above it.
const DayIcon: React.FC<{ size?: number; className?: string }> = ({ size = 15, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className} style={iconStyle}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7v5.4l3.4 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon: React.FC<{ size?: number; className?: string }> = ({ size = 15, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className} style={iconStyle}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 11v5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="7.8" r="1.15" fill="currentColor" />
  </svg>
);

const HotelIcon: React.FC<{ size?: number; className?: string }> = ({ size = 40, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className} style={iconStyle}>
    <rect x="4" y="3" width="16" height="18" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9.5 21v-4h5v4" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 14.5h2M14 14.5h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/**
 * "נותרו 3 חדרים" — or nothing at all.
 *
 * THE NUMBER IS THE SUPPLIER'S ALLOTMENT LEDGER, and only ever that: contracted
 * rooms minus the rooms already confirmed, fetched per deal by
 * `GET /api/front/deals/rooms-left` after the listing has rendered. It is
 * `undefined` until that answers, and stays `undefined` when it cannot — no
 * gateway, gateway down, or a free-sale night that has no allotment at all — and
 * then this renders NOTHING.
 *
 * That silence is the whole fix. This chip shipped on 31/08/2026 reading
 * `variant.allotment`, a stored snapshot of Atlantis SearchHotels' AvailRooms —
 * which is not the remaining stock, which nothing decrements when a room sells,
 * and which read 25 on a hotel-night whose 25 rooms were all confirmed. A card
 * offered "נותרו 3 חדרים" on a full דן בוטיק ירושלים while /init refused the
 * booking off the ledger. A member shown no number simply clicks through; a
 * member shown a wrong one is lied to. Never put a stored count back in here.
 *
 * Two further silences, each deliberate and unchanged. A sold-out deal says
 * nothing here because the card already carries its own "אזל" badge, and two
 * notices about one fact compete with each other. A deal with plenty says
 * nothing because "נותרו 47 חדרים" does not move anyone to book — it says there
 * is time — and printing a number on every card turns the sentence into
 * furniture the eye skips, which costs us the one case where it matters (Ofir,
 * 2026-08-31: option B, cap 10).
 *
 * `urgent` under 5 mirrors the deal page, which has read
 * "נותרו רק X חדרים זמינים!" at that threshold since long before this. One
 * threshold across the site, so a teacher is not told two different stories a
 * click apart.
 */
const SCARCITY_SHOW_BELOW = 10;
const SCARCITY_URGENT_BELOW = 5;

const roomsLeftLabel = (
  roomsLeft: number | null | undefined,
  soldOut: boolean,
): { text: string; urgent: boolean } | null => {
  if (soldOut) return null;
  // Not a number = the ledger did not answer. Say nothing rather than guess.
  if (typeof roomsLeft !== 'number' || !Number.isFinite(roomsLeft)) return null;
  const n = Math.max(0, Math.floor(roomsLeft));
  if (n <= 0 || n > SCARCITY_SHOW_BELOW) return null;
  return {
    text: `נותרו ${n} ${n === 1 ? 'חדר' : 'חדרים'}`,
    urgent: n < SCARCITY_URGENT_BELOW,
  };
};

/**
 * The rooms-left chip. One component so the two teacher surfaces cannot drift
 * apart on wording or threshold. Renders nothing when there is nothing worth
 * saying, so callers need no condition of their own — including the common case
 * of the ledger answer not having arrived yet.
 *
 * Not used on the agent screens: the desk was explicitly left alone (Ofir,
 * 2026-08-31).
 */
const RoomsLeft: React.FC<{ roomsLeft: number | null | undefined; soldOut: boolean }> = ({ roomsLeft, soldOut }) => {
  const label = roomsLeftLabel(roomsLeft, soldOut);
  if (!label) return null;
  return (
    <span className={`rooms-left${label.urgent ? ' rooms-left--urgent' : ''}`}>
      {label.text}
    </span>
  );
};

const boardBasisHe = (bb: string): string => {
  const map: Record<string, string> = {
    RoomOnly: 'ללא ארוחות',
    BedAndBreakfast: 'ארוחת בוקר',
    BB: 'ארוחת בוקר',
    HalfBoard: 'חצי פנסיון',
    HB: 'חצי פנסיון',
    FullBoard: 'פנסיון מלא',
    FB: 'פנסיון מלא',
    AllInclusive: 'הכל כלול',
    AI: 'הכל כלול',
  };
  return map[bb] ?? bb;
};

// Legacy policy copy from
// Irgonim/Views/HotelH/_getRoomPriceForBookPartial.cshtml — used when the admin
// hasn't customised the booking-policy block via Site Settings. The string is
// raw HTML so the admin's WYSIWYG output renders without escaping.
const DEFAULT_BOOKING_TIMEOUT_MINUTES = 15;

const buildDefaultBookingPolicyHtml = (timeoutMinutes: number): string => `
<div style="font-size:14px;font-weight:500">
  <strong>לקוח יקר,</strong><br/>
  ℹ️ <u style="font-weight:bold">פרטי המזמין (שם, טלפון וכרטיס אשראי)</u>
  חייבים להיות תואמים <u style="font-weight:bold">למספר תעודת הזהות</u>
  שאיתו בוצעה הכניסה לאתר ודרכו בוצעה ההזמנה.<br/>
  ℹ️ במידה וההזמנה או כרטיס האשראי יירשמו על שם שונה — ההזמנה לא תאושר.<br/>
  ℹ️ למשלמים בכרטיס תמורה — ההנחה תינתן במעמד החיוב בכרטיס.<br/>
  ℹ️ בלחיצה על "המשך לתשלום" תיווצר ההזמנה במערכת.<br/>
  ⏲️ לידיעתכם, הזמנה שלא הושלמה בתוך ${timeoutMinutes} דקות תבוטל אוטומטית.
</div>
`.trim();

const stateHe = (state: string): { label: string; cls: string } => {
  const map: Record<string, { label: string; cls: string }> = {
    Initiated: { label: 'חדש', cls: 'status-badge--muted' },
    AtlantisHeld: { label: 'משוריין', cls: 'status-badge--primary' },
    AwaitingPayment: { label: 'ממתין לתשלום', cls: 'status-badge--warning' },
    Confirmed: { label: 'מאושר', cls: 'status-badge--success' },
    RQ_Saved: { label: 'ממתין לאישור מלון', cls: 'status-badge--warning' },
    RqSaved: { label: 'ממתין לאישור מלון', cls: 'status-badge--warning' },
    Failed: { label: 'נכשל', cls: 'status-badge--danger' },
    Cancelled: { label: 'מבוטל', cls: 'status-badge--muted' },
  };
  return map[state] ?? { label: state, cls: 'status-badge--muted' };
};

// ============================================================================
// Brand mark — uses the real legacy logo asset (wwwroot/images/irgon/logo.png)
// ============================================================================

const BrandLogo: React.FC<{ height?: number; className?: string }> = ({ height = 50, className }) => (
  <img
    src="/images/irgon/logo.png"
    alt="ארגון המורים"
    className={className}
    style={{ height, width: 'auto', display: 'block' }}
  />
);

// ============================================================================
// Top navbar — ADomestic-style: white bg, brand right, links centered, user left
// ============================================================================

const TopNav: React.FC<{
  me: Me | null;
  current: View['kind'];
  onLoginClick: () => void;
  onLogout: () => void;
  onNav: (kind: View['kind']) => void;
}> = ({ me, current, onLoginClick, onLogout, onNav }) => {
  const isHomeActive = current === 'home' || current === 'category-detail' || current === 'deal-detail';
  const itemCls = (active: boolean) =>
    `irgon-header__nav-item${active ? ' irgon-header__nav-item--active' : ''}`;

  // Mobile account menu. On a phone the header could not fit the name, the
  // logout button and three nav links, so the identity items collapse behind
  // an avatar and the logo row keeps only the two destinations. Desktop is
  // untouched — the CSS swaps which of the two blocks is visible.
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!accountOpen) return;
    const onDown = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) setAccountOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAccountOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountOpen]);
  // A route change closes it — otherwise the menu hangs over the new page.
  useEffect(() => { setAccountOpen(false); }, [current]);

  return (
    <header className="irgon-header">
      <div className="container irgon-header__inner">
        <a
          className="irgon-header__brand"
          href="#"
          onClick={e => { e.preventDefault(); onNav('home'); }}
          aria-label="ארגון המורים"
        >
          <BrandLogo />
        </a>

        <nav className="irgon-header__nav">
          {/* On a phone this nav item is replaced by the call button below.
              "נופש מסובסד" only names the page already open, while a thumb on a
              phone is one tap from a person who can answer. */}
          <button
            type="button"
            className={`${itemCls(isHomeActive)} irgon-header__nav-item--desktop`}
            onClick={() => onNav('home')}
          >
            נופש מסובסד
          </button>
          <a href="tel:0509995241" className="irgon-header__call" aria-label="מרכז הזמנות 050-9995241">
            <span className="irgon-header__call-label">מרכז הזמנות</span>
            <span className="irgon-header__call-number">050-9995241</span>
          </a>
          {/* The "נופש בחו\"ל" item was removed from the nav at the operator's
              request. The endpoint behind it (/api/front/morim-redirect) is
              untouched and still works — the FLYINGPACK category card routes to
              it, and any link already sent to a teacher keeps working. */}
          {me && (
            <button
              type="button"
              className={`${itemCls(current === 'my-bookings')} irgon-header__nav-item--desktop`}
              onClick={() => onNav('my-bookings')}
            >
              ההזמנות שלי
            </button>
          )}
        </nav>

        <div className="irgon-header__user">
          {me ? (
            <>
              <span className="irgon-header__user-name">
                {me.actingAgent && <span className="irgon-header__acting">בשם</span>}
                {me.fullName}
              </span>
              <button type="button" className="irgon-header__logout-btn" onClick={onLogout}>
                יציאה
              </button>
            </>
          ) : (
            <button type="button" className="irgon-header__login-btn" onClick={onLoginClick}>
              התחבר לחשבון
            </button>
          )}
        </div>

        {/* Mobile-only identity control. Rendered always, revealed by the
            ≤991px media query, which simultaneously hides .irgon-header__user
            and the "ההזמנות שלי" nav item that moves in here. */}
        <div className="irgon-header__account" ref={accountRef}>
          {me ? (
            <>
              <button
                type="button"
                className={`irgon-header__avatar${accountOpen ? ' is-open' : ''}`}
                onClick={() => setAccountOpen(o => !o)}
                aria-expanded={accountOpen}
                aria-haspopup="menu"
                aria-label="החשבון שלי"
              >
                <UserIcon size={26} />
              </button>
              {accountOpen && (
                <div className="irgon-header__account-menu" role="menu">
                  <div className="irgon-header__account-name">{me.fullName}</div>
                  <button
                    type="button"
                    className="irgon-header__account-item"
                    role="menuitem"
                    onClick={() => { setAccountOpen(false); onNav('my-bookings'); }}
                  >
                    ההזמנות שלי
                  </button>
                  <button
                    type="button"
                    className="irgon-header__account-item irgon-header__account-item--danger"
                    role="menuitem"
                    onClick={() => { setAccountOpen(false); onLogout(); }}
                  >
                    התנתק
                  </button>
                </div>
              )}
            </>
          ) : (
            <button type="button" className="irgon-header__avatar" onClick={onLoginClick} aria-label="התחבר לחשבון">
              <UserIcon size={26} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

// ============================================================================
// Footer — dark, ADomestic palette + Heebo. The contact block is editable from
// Admin → ניהול תוכן (SiteSettings.FooterContactHtml). Null/empty falls back to
// the default copy below so the panel is never blank.
// ============================================================================

const DEFAULT_FOOTER_CONTACT_HTML = `
<div class="irgon-footer__heading">ניתן להתקשר אלינו</div>
<a href="tel:0509995241" class="irgon-footer__phone-btn">
  <span aria-hidden="true">📞</span>
  <span>050-9995241</span>
</a>
<div class="irgon-footer__notes">
  <div>לבקשות שינוי או ביטול הזמנה נא לפנות למייל&nbsp;<a href="mailto:servicepnim@flying.co.il?cc=mirid@flying.co.il">servicepnim@flying.co.il</a></div>
  <div>ימי עבודה <strong>08:30 - 16:30</strong></div>
  <div class="irgon-footer__emergency">למקרי חירום בלבד מחוץ לשעות העבודה - לא להזמנות חדרים בבתי מלון</div>
</div>
`.trim();

const Footer: React.FC = () => {
  const [footerContactHtml, setFooterContactHtml] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/api/front/site-settings');
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setFooterContactHtml(data?.footerContactHtml || null);
      } catch { /* ignore — falls back to default copy */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <footer className="irgon-footer">
      <div
        className="irgon-footer__top container"
        dangerouslySetInnerHTML={{ __html: footerContactHtml ?? DEFAULT_FOOTER_CONTACT_HTML }}
      />
      <div className="irgon-footer__copyright">
        © {new Date().getFullYear()} ארגון המורים בישראל — כל הזכויות שמורות
        {' · '}
        <a href="#/accessibility" className="irgon-footer__a11y-link">הצהרת נגישות</a>
      </div>
    </footer>
  );
};

// ============================================================================
// Accessibility widget + statement page (in-house, no third-party script)
// ============================================================================
// Floating ♿ button → panel with the standard aids: text size, contrast
// modes (high / inverted / grayscale), link highlighting, readable font,
// stop animations, visible keyboard focus. Preferences persist per browser
// (localStorage) and are applied as classes on <html> — see the a11y-* rules
// in site.css. The panel links to the accessibility-statement page, whose
// body is admin-editable (SiteSettings.AccessibilityStatementHtml).

const A11Y_PREFS_KEY = 'irgon-a11y-v1';

type A11yPrefs = {
  font: 0 | 1 | 2;
  contrast: 'none' | 'high' | 'invert' | 'gray';
  links: boolean;
  readable: boolean;
  noAnim: boolean;
  focus: boolean;
};

const A11Y_DEFAULTS: A11yPrefs = { font: 0, contrast: 'none', links: false, readable: false, noAnim: false, focus: false };

const loadA11yPrefs = (): A11yPrefs => {
  try {
    const raw = window.localStorage.getItem(A11Y_PREFS_KEY);
    if (!raw) return A11Y_DEFAULTS;
    return { ...A11Y_DEFAULTS, ...JSON.parse(raw) };
  } catch { return A11Y_DEFAULTS; }
};

const applyA11yPrefs = (p: A11yPrefs) => {
  const el = document.documentElement;
  el.classList.toggle('a11y-font-1', p.font === 1);
  el.classList.toggle('a11y-font-2', p.font === 2);
  el.classList.toggle('a11y-contrast', p.contrast === 'high');
  el.classList.toggle('a11y-invert', p.contrast === 'invert');
  el.classList.toggle('a11y-gray', p.contrast === 'gray');
  el.classList.toggle('a11y-links', p.links);
  el.classList.toggle('a11y-readable', p.readable);
  el.classList.toggle('a11y-noanim', p.noAnim);
  el.classList.toggle('a11y-focus', p.focus);
};

const AccessibilityWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<A11yPrefs>(loadA11yPrefs);

  // Apply on mount (restores persisted prefs) and on every change.
  useEffect(() => {
    applyA11yPrefs(prefs);
    try { window.localStorage.setItem(A11Y_PREFS_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
  }, [prefs]);

  // ESC closes the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const set = <K extends keyof A11yPrefs>(k: K, v: A11yPrefs[K]) => setPrefs(p => ({ ...p, [k]: v }));
  const anyActive = JSON.stringify(prefs) !== JSON.stringify(A11Y_DEFAULTS);

  const Toggle: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
    <button
      type="button"
      className={`a11y-panel__btn${active ? ' is-on' : ''}`}
      aria-pressed={active}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <>
      <button
        type="button"
        className={`a11y-fab${anyActive ? ' is-active' : ''}`}
        aria-label="תפריט נגישות"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="תפריט נגישות"
        onClick={() => setOpen(o => !o)}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="4.5" r="2" fill="currentColor" />
          <path d="M4.5 8.2c5 1.3 10 1.3 15 0" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          <path d="M12 8.8v4.4l-3.2 6.2M12 13.2l3.2 6.2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="a11y-panel" role="dialog" aria-label="הגדרות נגישות">
          <div className="a11y-panel__header">
            <span>הגדרות נגישות</span>
            <button type="button" className="a11y-panel__close" aria-label="סגור" onClick={() => setOpen(false)}>✕</button>
          </div>

          <div className="a11y-panel__group" role="group" aria-label="גודל טקסט">
            <div className="a11y-panel__group-title">גודל טקסט</div>
            <div className="a11y-panel__row">
              <Toggle label="רגיל" active={prefs.font === 0} onClick={() => set('font', 0)} />
              <Toggle label="גדול" active={prefs.font === 1} onClick={() => set('font', 1)} />
              <Toggle label="גדול מאוד" active={prefs.font === 2} onClick={() => set('font', 2)} />
            </div>
          </div>

          <div className="a11y-panel__group" role="group" aria-label="ניגודיות">
            <div className="a11y-panel__group-title">ניגודיות וצבע</div>
            <div className="a11y-panel__row">
              <Toggle label="רגיל" active={prefs.contrast === 'none'} onClick={() => set('contrast', 'none')} />
              <Toggle label="ניגודיות גבוהה" active={prefs.contrast === 'high'} onClick={() => set('contrast', 'high')} />
              <Toggle label="ניגודיות הפוכה" active={prefs.contrast === 'invert'} onClick={() => set('contrast', 'invert')} />
              <Toggle label="גווני אפור" active={prefs.contrast === 'gray'} onClick={() => set('contrast', 'gray')} />
            </div>
          </div>

          <div className="a11y-panel__group" role="group" aria-label="עזרי קריאה">
            <div className="a11y-panel__group-title">עזרי קריאה</div>
            <div className="a11y-panel__row">
              <Toggle label="הדגשת קישורים" active={prefs.links} onClick={() => set('links', !prefs.links)} />
              <Toggle label="פונט קריא" active={prefs.readable} onClick={() => set('readable', !prefs.readable)} />
              <Toggle label="עצירת אנימציות" active={prefs.noAnim} onClick={() => set('noAnim', !prefs.noAnim)} />
              <Toggle label="הדגשת מיקוד מקלדת" active={prefs.focus} onClick={() => set('focus', !prefs.focus)} />
            </div>
          </div>

          <div className="a11y-panel__footer">
            <button
              type="button"
              className="a11y-panel__reset"
              onClick={() => setPrefs(A11Y_DEFAULTS)}
              disabled={!anyActive}
            >
              איפוס הגדרות
            </button>
            <a href="#/accessibility" onClick={() => setOpen(false)}>הצהרת נגישות</a>
          </div>
        </div>
      )}
    </>
  );
};

// Built-in accessibility-statement template — shown until the admin fills the
// real details (coordinator name/phone/email) via Site Settings.
const DEFAULT_ACCESSIBILITY_STATEMENT_HTML = `
<h2>הצהרת נגישות</h2>
<p>ארגון המורים בישראל רואה חשיבות רבה במתן שירות שוויוני לכלל הגולשים ובשיפור נגישות האתר לאנשים עם מוגבלות, בהתאם לחוק שוויון זכויות לאנשים עם מוגבלות התשנ"ח-1998 ולתקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות) התשע"ג-2013, ובשאיפה לעמידה בהנחיות התקן הישראלי (ת"י 5568) לנגישות תכנים באינטרנט ברמה AA.</p>
<h3>אמצעי הנגישות באתר</h3>
<ul>
  <li>תפריט נגישות הכולל: הגדלת טקסט, ניגודיות גבוהה/הפוכה, גווני אפור, הדגשת קישורים, פונט קריא, עצירת אנימציות והדגשת מיקוד מקלדת.</li>
  <li>האתר ניתן לניווט באמצעות מקלדת.</li>
  <li>מבנה עמודים סמנטי התומך בקוראי מסך.</li>
</ul>
<h3>נתקלתם בבעיה? ספרו לנו</h3>
<p>אנו ממשיכים לשפר את נגישות האתר. אם נתקלתם בקושי או בתקלה בנושא נגישות, נשמח שתיצרו קשר עם רכז הנגישות:</p>
<ul>
  <li>שם: ___________</li>
  <li>טלפון: 050-9995241</li>
  <li>דוא"ל: ___________</li>
</ul>
<p>עדכון אחרון של ההצהרה: ${new Date().toLocaleDateString('he-IL')}</p>`;

const AccessibilityStatementPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/api/front/site-settings');
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setHtml(data?.accessibilityStatementHtml || null);
      } catch { /* fall back to the built-in template */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>
      <a href="#" className="irgon-back" onClick={e => { e.preventDefault(); onBack(); }}>
        ← חזרה
      </a>
      <div
        className="a11y-statement"
        dangerouslySetInnerHTML={{ __html: html ?? DEFAULT_ACCESSIBILITY_STATEMENT_HTML }}
      />
    </div>
  );
};

// ============================================================================
// Login page — ADomestic soft surface, blue primary CTA
// ============================================================================

// LoginPage drives the 2-step SMS verification flow:
//   step 'start'  → user types TZ + phone → POST /api/front/auth/start
//   step 'verify' → user types 6-digit code → POST /api/front/auth/verify
// Error states are Hebrew and mirror the backend error codes.
const LoginPage: React.FC<{ onLogin: () => void }> = ({ onLogin }) => {
  const [step, setStep] = useState<'start' | 'verify'>('start');
  const [tz, setTz] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Re-send cooldown timer (5 minutes — matches the server-side challenge TTL).
  // Prevents accidental double-sends + abuse of the Inforu SMS budget.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = window.setTimeout(() => setResendCooldown(s => Math.max(0, s - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [resendCooldown]);

  // People write their id without the leading zero — it is how it appears on
  // most documents — and the stored key is always nine digits. Padding here
  // means "29522869" logs in as the same person as "029522869" instead of
  // being told they are not a member.
  const padTz = (value: string) => (value.length > 0 && value.length < 9 ? value.padStart(9, '0') : value);

  // Some teachers carry a kosher handset that receives no SMS at all, so email
  // is a first-class way in rather than a fallback. SMS stays the default — it
  // is what nearly everyone uses, and a default nobody has to think about is
  // worth more than a symmetric menu.
  const [channel, setChannel] = useState<'sms' | 'email'>('sms');
  const [email, setEmail] = useState('');

  // Remembered per id, in this browser. A teacher on a kosher phone would
  // otherwise pick "email" on every login, forever. It lives here and not on
  // the profile because the screen must know the answer BEFORE anyone is
  // signed in — and an endpoint answering "which channel does this id use"
  // would also answer "does this id exist".
  const channelKey = (id: string) => `irgon:codeChannel:${id}`;
  useEffect(() => {
    if (tz.length < 9) return;
    try {
      const saved = window.localStorage.getItem(channelKey(tz));
      if (saved === 'email' || saved === 'sms') setChannel(saved);
    } catch { /* private mode — the default is fine */ }
  }, [tz]);

  const submitStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const paddedTz = padTz(tz);
    if (paddedTz !== tz) setTz(paddedTz);
    const r = await api('/api/front/auth/start', {
      method: 'POST',
      body: JSON.stringify({ tz: paddedTz, phone, channel, email }),
    });
    setBusy(false);
    if (r.status === 202) {
      const body = await r.json().catch(() => ({}));
      // Remembered only once a code actually went out — a choice that failed is
      // not a preference.
      try { window.localStorage.setItem(channelKey(paddedTz), channel); } catch { /* ignore */ }
      setMaskedPhone(body?.maskedPhone || '');
      setStep('verify');
      setCode('');
      setErr(null);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } else if (r.status === 429) {
      setErr('יותר מדי ניסיונות. נסה שוב בעוד דקה.');
    } else if (r.status === 401) {
      const body = await r.json().catch(() => ({}));
      setErr(body?.message || 'תעודת זהות לא נמצאה במאגר ארגון המורים');
    } else {
      // Three unrelated things used to share one sentence: the teachers'
      // organisation being unreachable, the SMS gateway refusing, and our own
      // write failing. Naming them tells the teacher whether to wait, to check
      // the number, or to call support.
      const body = await r.json().catch(() => ({}));
      if (body?.code === 'identity_upstream_unavailable') {
        setErr('לא ניתן לאמת את תעודת הזהות מול ארגון המורים כרגע. נסו שוב בעוד רגע.');
      } else if (body?.code === 'sms_send_failed') {
        setErr('שליחת ה-SMS נכשלה. בדקו את מספר הטלפון ונסו שוב.');
      } else {
        setErr('שליחת קוד נכשלה. נסה שוב.');
      }
    }
  };

  const submitVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const r = await api('/api/front/auth/verify', { method: 'POST', body: JSON.stringify({ tz, code }) });
    setBusy(false);
    if (r.ok) {
      onLogin();
      return;
    }
    if (r.status === 429) {
      setErr('יותר מדי ניסיונות. בקשו קוד חדש.');
      setResendCooldown(0);
      return;
    }
    if (r.status === 401) {
      const body = await r.json().catch(() => ({}));
      // An expired code and a mistyped one are different problems and the
      // server has always said which. Showing "קוד שגוי" for both sent people
      // hunting for a typo in a code that had simply died — and left them
      // waiting out a cooldown instead of asking for a new one.
      if (body?.code === 'no_active_challenge') {
        setErr('פג תוקף הקוד. בקשו קוד חדש.');
        setResendCooldown(0);
      } else {
        setErr('קוד שגוי');
      }
      return;
    }
    {
      const body = await r.json().catch(() => ({}));
      setErr(body?.code === 'identity_upstream_unavailable'
        ? 'לא ניתן לאמת את תעודת הזהות מול ארגון המורים כרגע. נסו שוב בעוד רגע.'
        : 'האימות נכשל. נסה שוב.');
    }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    setBusy(true);
    setErr(null);
    const r = await api('/api/front/auth/start', {
      method: 'POST',
      body: JSON.stringify({ tz, phone, channel, email }),
    });
    setBusy(false);
    if (r.status === 202) {
      const body = await r.json().catch(() => ({}));
      setMaskedPhone(body?.maskedPhone || maskedPhone);
      setCode('');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } else {
      setErr('שליחה חוזרת נכשלה. נסה שוב.');
    }
  };

  return (
    <div className="irgon-login-shell">
      <div className="container">
        <div className="irgon-login-card">
          <div className="irgon-login-card__brand">
            <BrandLogo height={90} />
          </div>
          <h2 className="irgon-login-card__title">כניסה לאזור האישי</h2>

          {step === 'start' && (
            <form onSubmit={submitStart}>
              <div className="irgon-field">
                <label htmlFor="tz">תעודת זהות</label>
                {/* Padded when the field loses focus, so the teacher sees the
                    zero appear rather than wondering what was sent. */}
                <input
                  id="tz"
                  value={tz}
                  onChange={e => setTz(e.target.value.replace(/\D/g, '').slice(0, 9))}
                  onBlur={() => setTz(padTz(tz))}
                  autoFocus
                  inputMode="numeric"
                  maxLength={9}
                />
              </div>
              <div className="irgon-field">
                <label>איך לקבל את הקוד?</label>
                <div className="login-channel" role="radiogroup" aria-label="ערוץ קבלת הקוד">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={channel === 'sms'}
                    className={`login-channel__opt${channel === 'sms' ? ' is-on' : ''}`}
                    onClick={() => setChannel('sms')}
                  >
                    📱 SMS
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={channel === 'email'}
                    className={`login-channel__opt${channel === 'email' ? ' is-on' : ''}`}
                    onClick={() => setChannel('email')}
                  >
                    ✉️ אימייל
                  </button>
                </div>
              </div>

              {/* One field, not two. A teacher whose handset cannot receive the
                  code should not have to type that number to get in — it is
                  asked for at checkout, where it is the booking's contact. */}
              {channel === 'sms' ? (
                <div className="irgon-field">
                  <label htmlFor="phone">מספר טלפון</label>
                  <input
                    id="phone"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    inputMode="numeric"
                    maxLength={10}
                  />
                </div>
              ) : (
                <div className="irgon-field">
                  <label htmlFor="login-email">כתובת אימייל</label>
                  <input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    dir="ltr"
                    autoComplete="email"
                    placeholder="name@example.com"
                  />
                </div>
              )}
              {err && <div className="irgon-alert-error">{err}</div>}
              <button
                type="submit"
                className="irgon-btn-primary"
                disabled={
                  busy || tz.length < 8
                  || (channel === 'sms'
                    ? phone.length < 9
                    : !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
                }
              >
                {busy ? 'שולח...' : 'שלח קוד'}
              </button>
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={submitVerify}>
              <div style={{ color: 'var(--color-muted)', fontSize: 14, marginBottom: 16, textAlign: 'center' }}>
                קוד נשלח ל-{maskedPhone || (channel === 'email' ? 'כתובת המייל שלך' : 'מספר הטלפון שלך')}
              </div>
              <div className="irgon-field">
                <label htmlFor="code">קוד אימות</label>
                <input
                  id="code"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  style={{ fontFamily: 'monospace', letterSpacing: '0.4em', textAlign: 'center' }}
                />
              </div>
              {err && <div className="irgon-alert-error">{err}</div>}
              <button
                type="submit"
                className="irgon-btn-primary"
                disabled={busy || code.length < 6}
              >
                {busy ? 'מאמת...' : 'התחבר'}
              </button>
              <div style={{ marginTop: 14, textAlign: 'center', fontSize: 13 }}>
                {resendCooldown > 0 ? (
                  <span style={{ color: 'var(--color-muted)' }}>
                    {`שלח קוד מחדש בעוד ${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, '0')}`}
                  </span>
                ) : (
                  <a
                    href="#"
                    onClick={e => { e.preventDefault(); resend(); }}
                    style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
                  >
                    לא קיבלת? שלח שוב
                  </a>
                )}
              </div>
              <div style={{ marginTop: 8, textAlign: 'center', fontSize: 12 }}>
                <a
                  href="#"
                  onClick={e => { e.preventDefault(); setStep('start'); setErr(null); setCode(''); }}
                  style={{ color: 'var(--color-muted)' }}
                >
                  ← חזור ושנה פרטים
                </a>
              </div>
            </form>
          )}

          <div className="irgon-login-card__divider" />
          <div className="irgon-help">
            לבעיות התחברות פנה לשירות הלקוחות 050-9995241
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Terms-and-conditions modal — shown once per browser (localStorage gate).
// Mirrors legacy iConfirm popup from Irgonim/Views/Home/Index.cshtml.
// ============================================================================

const TERMS_ACCEPTED_KEY = 'irgon-terms-accepted-v1';
const COOKIE_CONSENT_KEY = 'irgon-cookie-consent-v1';
// Trial-mode popup: once the teacher enters the code it won't pop again in this
// browser. Keyed by the code value so that if the admin rotates the code the
// popup shows again (a fresh code = a fresh acknowledgement).
const TRIAL_POPUP_ACK_KEY = 'irgon-trial-popup-ack-v1';

// Bottom-of-page cookie banner. Persists dismissal in localStorage under
// COOKIE_CONSENT_KEY — the banner re-appears for users on a different
// browser / after clearing storage. Ported visually from the Domestic site's
// CookieConsentBanner.vue (white-pill banner pinned to bottom).
const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState<boolean>(() => {
    try { return window.localStorage.getItem(COOKIE_CONSENT_KEY) !== '1'; }
    catch { return false; }
  });
  if (!visible) return null;
  const dismiss = () => {
    try { window.localStorage.setItem(COOKIE_CONSENT_KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  };
  return (
    <div className="irgon-cookie-banner" role="region" aria-label="הודעת עוגיות">
      <button type="button" className="irgon-cookie-banner__close" onClick={dismiss} aria-label="סגור">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div className="irgon-cookie-banner__inner">
        <p className="irgon-cookie-banner__msg">
          לידיעתך, באתר זה נעשה שימוש בקבצי Cookies. המשך גלישה באתר מהווה הסכמה לשימוש זה. למידע נוסף ניתן לעיין{' '}
          <a href="/Home/Privacy" target="_blank" rel="noreferrer" className="irgon-cookie-banner__link">
            במדיניות הפרטיות
          </a>.
        </p>
      </div>
    </div>
  );
};

// Default takanon (subsidised-vacation terms) body. Kept in sync with
// "תקנון להזמנת נופש מסובסד.docx" — the bold lines mirror the doc's emphasis.
// Admin can override the whole block via Site Settings → "תקנון" (TakanonHtml);
// this constant is the fallback so the popups are never blank.
const DEFAULT_TAKANON_HTML = `
<div class="irgon-modal__intro">
  <b><u>תקנון להזמנת נופש מסובסד:</u></b><br/>
  - ילדים מגיל 2-12, תינוק מגיל 0-2.<br/>
  - להוספת תינוק בחדר — יש להתקשר למוקד כדי לקבל אישור.
</div>
<div class="irgon-modal__highlight">
  <b><u>מורה חבר ארגון המורים יכול לצאת באופן אישי לנופש מסובסד (בארץ ו/או בחו"ל) פעם אחת בלבד בשנה קלנדרית (מורה אינו יכול להעביר זכות זו לשום גורם אחר).</u></b>
</div>
<div class="irgon-modal__warning">
  <div class="irgon-modal__warning-title">שימו לב!!!</div>
  <ul>
    <li><b>מורה שניצל את זכאותו לנופש מסובסד מתאריך 1.1 – 31.12 בשנה הקלנדרית הנוכחית אינו זכאי לסבסוד נוסף ויחויב במלוא הסכום.</b></li>
    <li><b>מורה היוצא לחופשה יותר מפעם אחת בשנה קלנדרית - יחויב באמצעות כרטיס האשראי במלוא המחיר וללא סבסוד.</b></li>
    <li><b>למען הסר ספק: המועד הקובע את השנה הקלנדרית הוא מועד מימוש החופשה ולא מועד ההזמנה.</b></li>
  </ul>
</div>
<div class="irgon-modal__rules">
  - ידוע לי כי הזכאות להזמנת חדרים היא עבור חבר/ת ארגון המורים בלבד.<br/>
  - על חבר/ת ארגון המורים להיות נוכח/ת בעת ההרשמה במלון ולהציג תעודה מזהה אישית.<br/>
  - ביטול חדר או שינוי הזמנה יחויב בדמי ביטול על סך 100 ₪ מרגע ההרשמה ועד 7 ימי עסקים לפני ההגעה.<br/>
  - ביטול חדר פחות מ-7 ימי עסקים קודם ליציאה לנופש יחויב בדמי ביטול.<br/>
  - ביטול החדרים יחויב לפי ימי העסקים בלבד.<br/>
  - אישור הזמנה סופי מותנה במינימום משתתפים.<br/>
  - השהות בבתי המלון מיועדת לחברי ארגון המורים בלבד ובחדרים סטנדרטיים אלא אם צוין אחרת.<br/>
  - בקשות מיוחדות יטופלו כבקשות בלבד ללא התחייבות למלאן.<br/>
  - בעת ההגעה יש להציג תעודה מזהה עם תמונה. תעודת הזהות תצולם בכל בתי המלון.<br/>
  - אורחים שיגיעו לבתי המלון ואינם רשומים בשמם כפי שמופיע בתעודת הזהות יידרשו לשלם עבור החדר ישירות למלון על פי מחיר המלון.<br/>
  - לא תתאפשר קבלת חדר ללא תעודה מזהה עם תמונה! במידה ולא תוצג תעודה מזהה, החדר יחויב במחיר מלא ללא החזר כספי.<br/>
  - כניסה לחדרים החל משעה 15:00.<br/>
  - באחריות האורח לבדוק פרטי ההזמנה לאחר אישורה.<br/>
  - באחריות האורח לברר פרטים על מתקני המלון ותנאי האירוח לפי הזמנת החדר.<br/>
  <b>- מובהר כי ספק הנופש ו/או ארגון המורים אינם אחראים לטיב ואיכות בתי המלון.</b><br/>
  <b>- הנני מצהיר כי השתתפותי/נו באירוע/נופש היא על אחריותי/נו המלאה, ולא יהיו לי/לנו תביעות מספק הנופש ו/או ארגון המורים עבור נזקים שעלולים להיגרם לי/לנו תוך כדי השתתפותי/נו בנופש.</b><br/>
  - לינה וארוחת בוקר BB · חצי פנסיון — ארוחת בוקר וערב HB · פנסיון מלא — ארוחות בוקר, צהריים וערב FB.<br/>
  - כל הקודם זוכה — מספר החדרים מוגבל.<br/>
  - ביטול הזמנה יבוצע לפי תנאי הביטול ישירות בטלפונים המפורסמים באתר.<br/>
  - פינוי חדרים בשבת — לפי תנאי הסכם בתי המלון המוזמנים למורים.<br/>
  - ט.ל.ח.
</div>`;

// The two conditions the takanon-confirmation popup surfaces UNDER the
// send-SMS button (per the product spec) — the once-per-year rule and the
// Tmura-card payment rule.
const TAKANON_KEY_CONDITIONS_HTML = `
<ul style="margin:0;padding-inline-start:18px">
  <li><b>* מורה חבר ארגון המורים יכול לצאת באופן אישי לנופש מסובסד (בארץ ו/או בחו"ל) פעם אחת בלבד בשנה קלנדרית (מורה אינו יכול להעביר זכות זו לשום גורם אחר).</b></li>
  <li style="margin-top:6px"><b>התשלום אך ורק בכרטיס אשראי תמורה ע"ש המורה ותינתן הנחה 5% נוספים במעמד החיוב.</b></li>
</ul>`;

const WelcomeTermsModal: React.FC<{ onAccept: () => void; takanonHtml?: string | null }> = ({ onAccept, takanonHtml }) => (
  <div className="irgon-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="irgon-terms-title">
    <div className="irgon-modal">
      <div className="irgon-modal__header">
        <button
          type="button"
          className="irgon-modal__accept-btn irgon-modal__accept-btn--header"
          onClick={onAccept}
        >
          אישור
        </button>
        <span id="irgon-terms-title" className="irgon-modal__header-text">
          אני מאשר/ת שקראתי את התנאים וההגבלות
        </span>
      </div>
      <div
        className="irgon-modal__body"
        dangerouslySetInnerHTML={{ __html: takanonHtml || DEFAULT_TAKANON_HTML }}
      />
      <div className="irgon-modal__footer">
        בלחיצה על אישור אני מאשר/ת שקראתי את התנאים וההגבלות
        <button type="button" className="irgon-modal__accept-btn" onClick={onAccept}>אישור</button>
      </div>
    </div>
  </div>
);

// ============================================================================
// Checkout takanon SMS-confirmation popup
// ============================================================================
// Opens when the teacher ticks the terms checkbox on the checkout page. The
// full takanon (admin-editable) with the two key conditions under it, and a
// confirm button gated on scrolling to the bottom. Pressing it records the
// acceptance server-side and only then does the checkbox turn on — the server
// enforces the same gate at /booking/init via the TermsAcceptance audit row,
// so a checkbox ticked without this popup buys nothing.
//
// This used to send a 6-digit SMS and ask for it back. The code step is gone;
// the audit row now carries the session (name, login time, IP, timestamp) in
// its place, since that is what identifies a click.
const TermsConfirmModal: React.FC<{
  takanonHtml: string | null;
  onConfirmed: () => void;
  onClose: () => void;
}> = ({ takanonHtml, onConfirmed, onClose }) => {
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Scroll gate — the send button unlocks only after the teacher reached the
  // bottom of the takanon. If the content happens to fit without scrolling
  // (large screens / short admin override) the gate opens immediately.
  const checkScrolledEnd = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 16) setScrolledEnd(true);
  }, []);
  useEffect(() => { checkScrolledEnd(); }, [checkScrolledEnd]);

  // The checkbox must not turn on until the row is written. If this call fails
  // the popup stays open with the error: a teacher who thinks they confirmed
  // and then gets refused at "המשך לתשלום" has no way to tell what went wrong.
  const confirmTerms = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      // Empty JSON body rather than none: IIS answers a POST that arrives
      // without Content-Length with 411 before the app ever sees it. Browsers
      // do send Content-Length: 0 for a bodyless POST, so this is belt and
      // braces — but it is the one call here that carries no payload, and the
      // failure it guards against blocks payment.
      const r = await api('/api/front/terms/accept', { method: 'POST', body: '{}' });
      if (r.ok) {
        onConfirmed();
        return;
      }
      const body = await r.json().catch(() => ({}));
      setErr(
        body?.code === 'not_authenticated' ? 'ההתחברות פגה. יש להתחבר מחדש ולנסות שוב.' :
        body?.code === 'rate_limited' ? 'יותר מדי ניסיונות. נסה שוב בעוד מספר דקות.' :
        'שמירת האישור נכשלה. נסה שוב.');
    } catch {
      setErr('שמירת האישור נכשלה. נסה שוב.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="irgon-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="irgon-terms-confirm-title">
      <div className="irgon-modal">
        <div className="irgon-modal__header">
          <button
            type="button"
            className="irgon-modal__accept-btn irgon-modal__accept-btn--header"
            onClick={onClose}
          >
            סגור
          </button>
          <span id="irgon-terms-confirm-title" className="irgon-modal__header-text">
            אישור תקנון נופש מסובסד
          </span>
        </div>

        <div
          ref={bodyRef}
          className="irgon-modal__body"
          onScroll={checkScrolledEnd}
          dangerouslySetInnerHTML={{ __html: takanonHtml || DEFAULT_TAKANON_HTML }}
        />
        <div className="irgon-modal__footer" style={{ display: 'block', textAlign: 'center' }}>
          {!scrolledEnd && (
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              יש לגלול ולקרוא את התקנון עד סופו כדי להמשיך ↓
            </div>
          )}
          <button
            type="button"
            className="irgon-modal__accept-btn"
            onClick={() => void confirmTerms()}
            disabled={!scrolledEnd || busy}
            style={!scrolledEnd ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            {busy ? 'שומר…' : 'אני מאשר/ת את התנאים'}
          </button>
          <div
            style={{ marginTop: 12, textAlign: 'start', fontSize: 13, lineHeight: 1.7 }}
            dangerouslySetInnerHTML={{ __html: TAKANON_KEY_CONDITIONS_HTML }}
          />
          {err && <div className="irgon-alert-error" style={{ marginTop: 10 }}>{err}</div>}
        </div>
      </div>
    </div>
  );
};

// Trial-mode notice — shown to every logged-in teacher while admin keeps it on
// (SiteSettings.TrialPopupEnabled). Blocking: the only way to close it is to enter
// the continue-code; a wrong code keeps it open. Dismiss is in-memory (re-appears
// on every fresh load) until an admin disables it.
const TrialPopupModal: React.FC<{ code: string; onContinue: () => void }> = ({ code, onContinue }) => {
  const [entered, setEntered] = useState('');
  const [error, setError] = useState(false);
  const submit = () => {
    if (entered.trim() === String(code).trim()) onContinue();
    else setError(true);
  };
  return (
    <div className="irgon-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="irgon-trial-title">
      <div className="irgon-modal" style={{ maxWidth: 520 }}>
        <div className="irgon-modal__header">
          <span id="irgon-trial-title" className="irgon-modal__header-text">הודעת מערכת</span>
        </div>
        <div className="irgon-modal__body">
          <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.8, textAlign: 'center', color: 'var(--color-primary-ink)' }}>
            מורה יקר, המערכת בהרצה.<br />
            נא לא לבצע הזמנות !!!<br />
            ההזמנות שייעשו לא יאושרו.<br />
            תודה, השטיח המעופף
          </div>
          <div style={{ marginTop: 22, textAlign: 'center' }}>
            <label htmlFor="irgon-trial-code" style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
              להמשך, הזינו קוד:
            </label>
            <input
              id="irgon-trial-code"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              value={entered}
              onChange={e => { setEntered(e.target.value); setError(false); }}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }}
              style={{
                width: 160, textAlign: 'center', fontSize: 20, letterSpacing: 4,
                padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-soft, #cbd5e1)',
              }}
            />
            {error && (
              <div style={{ color: '#dc2626', marginTop: 10, fontWeight: 600 }}>קוד שגוי, נסו שוב.</div>
            )}
          </div>
        </div>
        <div className="irgon-modal__footer">
          <button type="button" className="irgon-modal__accept-btn" onClick={submit}>המשך</button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Help boxes — right-side sidebar + floating left-side button.
// Mirror legacy box_style_2 boxes from Irgonim home page + layout.
// ============================================================================

// Default body of the right-side help sidebar — used when admin hasn't set
// SiteSettings.HelpSidebarHtml. Mirrors the legacy JSX (icon + heading +
// WhatsApp CTA + working-hours block + emergency block + two email blocks)
// and reuses the existing .help-box__* classes so the rendered output is
// pixel-identical when an admin clears the override.
const DEFAULT_HELP_SIDEBAR_HTML = `
<div class="help-box__icon" aria-hidden="true">💬</div>
<h4 class="help-box__title">צריך <span>עזרה?</span></h4>

<a
  href="https://wa.me/972522220869/?text=היי, אשמח לקבל שרות"
  target="_blank"
  rel="noopener noreferrer"
  class="help-box__whatsapp"
>
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
  <div>
    <div>מענה להודעות ווצאפ</div>
    <b>בימים א-ה בין השעות 08:00-16:00</b>
  </div>
</a>

<div class="help-box__section">
  <div class="help-box__note">
    הזמנות יש לעשות באתר בלבד! (נופש מסובסד, פנאי ונופש, נופש בארץ)
  </div>
  <div class="help-box__sub">לשירות לקוחות והזמנות בלבד</div>
  <b class="help-box__hours">בימים א-ה בין השעות 08:00-16:00</b>
  <div class="help-box__phone-row">
    ניתן להתקשר ל-
    <a href="tel:0509995241" class="help-box__phone-link">050-9995241</a>
  </div>
</div>

<div class="help-box__emergency">
  <div class="help-box__emergency-title">
    למקרי חירום בלבד מחוץ לשעות העבודה <br/>
    לא להזמנות חדרים בבתי מלון 🙏
  </div>
  <div>
    <span>צרו קשר</span>
    <a href="tel:0525995995" class="help-box__phone-link">052-599-5995</a>
  </div>
</div>

<div class="help-box__section">
  לבקשות שינוי או ביטול הזמנה נא לפנות למייל
  <a href="mailto:servicepnim@flying.co.il?cc=mirid@flying.co.il" class="help-box__email">servicepnim@flying.co.il</a>
  <div class="help-box__sub">בימי עבודה 08:30 - 16:30</div>
</div>

<div class="help-box__section">
  לשאלות ומענה בקשות נוספות ניתן לפנות למייל
  <a href="mailto:mirid@flying.co.il" class="help-box__email">mirid@flying.co.il</a>
</div>
`.trim();

// The takanon as something to READ. Same body as the checkout popup — the one
// the admin edits — but with nothing to accept: opened from the home page there
// is no booking to agree to, and an "אישור" button there would imply one.
const TakanonViewerModal: React.FC<{ takanonHtml?: string | null; onClose: () => void }> = ({ takanonHtml, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="irgon-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="irgon-takanon-title">
      <div className="irgon-modal">
        <div className="irgon-modal__header">
          <span id="irgon-takanon-title" className="irgon-modal__header-text">תקנון הזמנה וביטול</span>
          <button
            type="button"
            className="irgon-modal__accept-btn irgon-modal__accept-btn--header"
            onClick={onClose}
          >
            סגור
          </button>
        </div>
        <div
          className="irgon-modal__body"
          dangerouslySetInnerHTML={{ __html: takanonHtml || DEFAULT_TAKANON_HTML }}
        />
      </div>
    </div>
  );
};

const HelpSidebar: React.FC = () => {
  const [helpSidebarHtml, setHelpSidebarHtml] = useState<string | null>(null);
  const [takanonHtml, setTakanonHtml] = useState<string | null>(null);
  const [takanonOpen, setTakanonOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/api/front/site-settings');
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setHelpSidebarHtml(data?.helpSidebarHtml || null);
        // The same takanon the checkout popup shows, so editing it in the admin
        // changes both places at once.
        setTakanonHtml(data?.takanonHtml || null);
      } catch { /* ignore — falls back to default copy */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // The box is operator-editable HTML, so the trigger cannot be a React handler
  // on a specific element. Instead any element carrying .open-takanon opens the
  // popup — an operator writes <a href="#" class="open-takanon">…</a> wherever
  // they want it, with whatever wording, and never needs us to move it.
  const onBoxClick = (e: React.MouseEvent<HTMLElement>) => {
    const trigger = (e.target as HTMLElement)?.closest?.('.open-takanon');
    if (!trigger) return;
    e.preventDefault();
    setTakanonOpen(true);
  };

  return (
    <>
      <aside
        className="help-box help-box--sidebar"
        onClick={onBoxClick}
        dangerouslySetInnerHTML={{ __html: helpSidebarHtml ?? DEFAULT_HELP_SIDEBAR_HTML }}
      />
      {takanonOpen && (
        <TakanonViewerModal takanonHtml={takanonHtml} onClose={() => setTakanonOpen(false)} />
      )}
    </>
  );
};

// Default body of the "צריך עזרה?" help modal — used when admin hasn't set
// SiteSettings.HelpPopupHtml. Mirrors the legacy JSX layout (greeting, contact
// blocks, WhatsApp link, farewell) and uses the same .irgon-help-modal__*
// classes so the styling matches when an admin clears the override.
const DEFAULT_HELP_POPUP_HTML = `
<p class="irgon-help-modal__greeting">
  מורה יקר/ה,<br/>
  לרשותך מגוון בתי מלון ברחבי הארץ לנופש מסובסד.<br/>
  מורה יכול לצאת לסופ"ש או נופש אחר מסובסד פעם בשנה קלנדרית.
</p>

<div class="irgon-help-modal__contact">
  לפרטים נוספים — השטיח המעופף
</div>

<div class="irgon-help-modal__block">
  <div>הזמנות יש לעשות באתר בלבד! (נופש מסובסד, פנאי ונופש, נופש בארץ)</div>
  <div>לשירות לקוחות והזמנות בלבד</div>
  <b>בימים א-ה בין השעות 08:00-16:00</b>
  <div class="irgon-help-modal__phone-row">
    ניתן להתקשר ל-
    <a href="tel:0509995241" class="irgon-help-modal__phone">050-9995241</a>
  </div>
</div>

<div class="irgon-help-modal__block">
  לבקשות שינוי או ביטול הזמנה נא לפנות למייל<br/>
  <a href="mailto:servicepnim@flying.co.il?cc=mirid@flying.co.il" class="irgon-help-modal__email">servicepnim@flying.co.il</a>
  <div class="irgon-help-modal__hours">08:30 - 16:30 בימי עבודה</div>
</div>

<div class="irgon-help-modal__block">
  לשאלות ומענה בקשות נוספות ניתן לפנות למייל<br/>
  <a href="mailto:mirid@flying.co.il" class="irgon-help-modal__email">mirid@flying.co.il</a>
</div>

<div class="irgon-help-modal__emergency">
  <div class="irgon-help-modal__emergency-title">
    למקרי חירום בלבד מחוץ לשעות העבודה — לא להזמנות חדרים בבתי מלון
  </div>
  <div>
    <span>צרו קשר </span>
    <a href="tel:0525995995" class="irgon-help-modal__phone">052-599-5995</a>
  </div>
</div>

<a
  href="https://wa.me/972522220869/?text=היי, אשמח לקבל שרות"
  target="_blank"
  rel="noopener noreferrer"
  class="irgon-help-modal__whatsapp"
>
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
  פנייה בוואטסאפ
</a>

<div class="irgon-help-modal__farewell">חופשה נעימה</div>
`.trim();

const HelpModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [helpPopupHtml, setHelpPopupHtml] = useState<string | null>(null);
  const [takanonHtml, setTakanonHtml] = useState<string | null>(null);
  const [takanonOpen, setTakanonOpen] = useState(false);

  useEffect(() => {
    // While the takanon is up it owns Escape — otherwise one press closes both.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !takanonOpen) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, takanonOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/api/front/site-settings');
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setHelpPopupHtml(data?.helpPopupHtml || null);
        // Same response already carries the takanon, so the popup can open it
        // without a second request.
        setTakanonHtml(data?.takanonHtml || null);
      } catch { /* ignore — falls back to default copy */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // The popup is operator-editable HTML, so — exactly as in the sidebar — any
  // element carrying .open-takanon opens the takanon. Without this the class an
  // operator copies from the sidebar is inert here, and the link does nothing.
  const onBodyClick = (e: React.MouseEvent<HTMLElement>) => {
    const trigger = (e.target as HTMLElement)?.closest?.('.open-takanon');
    if (!trigger) return;
    e.preventDefault();
    setTakanonOpen(true);
  };

  return (
    <>
    <div
      className="irgon-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="irgon-help-title"
      onClick={onClose}
    >
      <div className="irgon-modal irgon-modal--help" onClick={e => e.stopPropagation()}>
        <div className="irgon-modal__header irgon-modal__header--simple">
          <button
            type="button"
            className="irgon-modal__close"
            aria-label="סגור"
            onClick={onClose}
          >
            ×
          </button>
          <span id="irgon-help-title" className="irgon-modal__header-text">צריך עזרה?</span>
        </div>

        <div
          className="irgon-modal__body irgon-help-modal__body"
          onClick={onBodyClick}
          dangerouslySetInnerHTML={{ __html: helpPopupHtml ?? DEFAULT_HELP_POPUP_HTML }}
        />
      </div>
    </div>
    {takanonOpen && (
      <TakanonViewerModal takanonHtml={takanonHtml} onClose={() => setTakanonOpen(false)} />
    )}
    </>
  );
};

const HelpFloater: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="help-floater"
    aria-label="צריך עזרה?"
  >
    <span className="help-floater__icon" aria-hidden="true">💬</span>
    <span className="help-floater__text">צריך עזרה?</span>
  </button>
);

// ============================================================================
// Skeleton placeholders — grey shimmering blocks that mirror the structure of
// the real cards. Shown instead of a spinner while page data is in flight so
// the layout stays stable (no jump when the data arrives).
// ============================================================================

const range = (n: number): number[] => Array.from({ length: n }, (_, i) => i);

const CategoryCardSkeleton: React.FC = () => (
  <div className="cat-card-skel" aria-hidden="true">
    <div className="skeleton cat-card-skel__media" />
    <div className="cat-card-skel__body">
      <div className="skeleton skeleton--title" style={{ width: '70%' }} />
      <div className="skeleton skeleton--text" style={{ width: '95%' }} />
      <div className="skeleton skeleton--text" style={{ width: '85%' }} />
      <div className="cat-card-skel__row">
        <div className="skeleton skeleton--text" style={{ width: 80 }} />
        <div className="skeleton skeleton--title" style={{ width: 110 }} />
      </div>
      <div className="skeleton skeleton--btn cat-card-skel__cta" />
    </div>
  </div>
);

const CategoryGridSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="category-grid" role="status" aria-live="polite" aria-label="טוען קטגוריות">
    {range(count).map(i => <CategoryCardSkeleton key={i} />)}
  </div>
);

const HotelRowSkeleton: React.FC = () => (
  <div className="hotel-row-skel" aria-hidden="true">
    <div className="skeleton hotel-row-skel__media" />
    <div className="hotel-row-skel__main">
      <div className="skeleton skeleton--title" style={{ width: '60%' }} />
      <div className="skeleton skeleton--text" style={{ width: '40%' }} />
      <div className="skeleton skeleton--pill" style={{ width: 160 }} />
    </div>
    <div className="hotel-row-skel__prices">
      <div className="skeleton skeleton--text" style={{ width: 90 }} />
      <div className="skeleton skeleton--lg" style={{ width: 130 }} />
      <div className="skeleton skeleton--btn" style={{ width: 130 }} />
    </div>
  </div>
);

const DealCardSkeleton: React.FC = () => (
  <div className="deal-card-skel" aria-hidden="true">
    <div className="skeleton deal-card-skel__media" />
    <div className="deal-card-skel__body">
      <div className="skeleton skeleton--title" style={{ width: '70%' }} />
      <div className="skeleton skeleton--text" style={{ width: '45%' }} />
      <div className="skeleton skeleton--text" style={{ width: '60%' }} />
      <div className="skeleton skeleton--text" style={{ width: '55%' }} />
      <div className="skeleton skeleton--lg" style={{ width: '50%' }} />
      <div className="skeleton skeleton--btn" />
    </div>
  </div>
);

const CategoryDetailSkeleton: React.FC<{ count?: number; viewMode?: 'tiles' | 'table' }> = ({ count = 5, viewMode = 'table' }) => (
  <div role="status" aria-live="polite" aria-label="טוען דילים">
    <div className="skeleton skeleton--title" style={{ width: 200, margin: '0 0 18px' }} />
    {viewMode === 'tiles' ? (
      <div className="deal-grid">
        {range(count).map(i => <DealCardSkeleton key={i} />)}
      </div>
    ) : (
      range(count).map(i => <HotelRowSkeleton key={i} />)
    )}
  </div>
);

const DealDetailSkeleton: React.FC = () => (
  <div role="status" aria-live="polite" aria-label="טוען פרטי דיל">
    <div className="skeleton skeleton--title" style={{ width: 220, margin: '8px 0 14px' }} />
    <div className="deal-detail-skel__hero">
      <div className="skeleton deal-detail-skel__gallery" />
      <div className="skeleton deal-detail-skel__map" />
    </div>
    <div className="deal-detail-skel__body">
      <div className="deal-detail-skel__form">
        <div className="skeleton skeleton--title" style={{ width: '40%' }} />
        <div className="skeleton skeleton--btn" />
        <div className="skeleton skeleton--btn" />
        <div className="skeleton skeleton--btn" />
        <div className="skeleton skeleton--btn" />
        <div className="skeleton skeleton--text" style={{ width: '80%' }} />
        <div className="skeleton skeleton--text" style={{ width: '65%' }} />
        <div className="skeleton skeleton--btn" style={{ marginTop: 10 }} />
      </div>
      <div className="deal-detail-skel__aside">
        <div className="skeleton skeleton--title" style={{ width: '55%' }} />
        <div className="skeleton skeleton--text" style={{ width: '90%' }} />
        <div className="skeleton skeleton--text" style={{ width: '70%' }} />
        <div className="skeleton skeleton--text" style={{ width: '80%' }} />
        <div className="skeleton skeleton--text" style={{ width: '60%' }} />
        <div className="skeleton skeleton--pill" style={{ width: '70%' }} />
        <div className="skeleton skeleton--lg" style={{ width: '60%', marginTop: 8 }} />
        <div className="skeleton skeleton--btn" style={{ marginTop: 6 }} />
      </div>
    </div>
  </div>
);

// ============================================================================
// Home — hero + grid of category cards
// ============================================================================

const CategoryCard: React.FC<{ cat: Category; onOpen: (cat: Category) => void }> = ({ cat, onOpen }) => {
  const showPrices = cat.showHomePrices === true
    && cat.fromGrossPrice != null
    && cat.fromNetPrice != null;
  const currency = cat.currency || 'ILS';
  const soldOut = cat.isSoldOut === true;
  // The "FLYINGPACK" category has no local deals — when present it routes to the
  // same "נופש בחו"ל" deep link as the header nav (/api/front/morim-redirect),
  // which mints the TZ + subsidy server-side from the session and opens the
  // partner site in a new tab.
  const isFlyingPack = (cat.code || '').trim().toUpperCase() === 'FLYINGPACK';
  const handleOpen = () => {
    if (soldOut) return;
    if (isFlyingPack) {
      window.open('/api/front/morim-redirect', '_blank', 'noopener,noreferrer');
      return;
    }
    onOpen(cat);
  };
  const [imagePopupOpen, setImagePopupOpen] = useState(false);

  // ESC closes the image popup without bubbling to the rest of the page.
  useEffect(() => {
    if (!imagePopupOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setImagePopupOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [imagePopupOpen]);

  return (
    <article
      className={`cat-card${soldOut ? ' cat-card--sold-out' : ''}`}
      role="button"
      tabIndex={soldOut ? -1 : 0}
      aria-disabled={soldOut || undefined}
      onClick={handleOpen}
      onKeyDown={e => { if (!soldOut && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleOpen(); } }}
    >
      {cat.imageUrl ? (
        <div
          className="cat-card__media"
          role="button"
          tabIndex={0}
          aria-label={isFlyingPack ? `${cat.name.he} — מעבר להזמנה` : `${cat.name.he} — לחץ להצגה מלאה`}
          title={isFlyingPack ? 'מעבר להזמנה' : 'לחץ להצגת התמונה המלאה'}
          style={{ backgroundImage: cssUrl(cat.imageUrl), cursor: isFlyingPack ? 'pointer' : 'zoom-in' }}
          onClick={e => {
            e.stopPropagation();
            // FLYINGPACK has no local deals — the image click follows the same
            // deep link as the card/CTA instead of opening the zoom lightbox.
            if (isFlyingPack) { handleOpen(); return; }
            setImagePopupOpen(true);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              if (isFlyingPack) { handleOpen(); return; }
              setImagePopupOpen(true);
            }
          }}
        />
      ) : (
        <div className="cat-card__media cat-card__media--placeholder" aria-hidden="true"><HotelIcon /></div>
      )}
      {soldOut && (
        <div className="cat-card__sold-out-banner" aria-label="אזל המלאי">אזל המלאי</div>
      )}
      <div className="cat-card__body">
        <h3 className="cat-card__title">{cat.name.he}</h3>
        {cat.description?.he && (
          <p className="cat-card__desc">{cat.description.he}</p>
        )}
        {showPrices && (
          <div className="cat-card__prices">
            <div className="cat-card__price-line">
              <span className="cat-card__price-label">החל מ-</span>
              <span className="cat-card__price">{fmtMoney(cat.fromGrossPrice!, currency)}</span>
            </div>
          </div>
        )}
        <button
          type="button"
          className="cat-card__cta"
          disabled={soldOut}
          onClick={e => { e.stopPropagation(); handleOpen(); }}
        >
          {soldOut ? 'אזל המלאי' : 'הזמן עכשיו »'}
        </button>
      </div>

      {/* Render the popup through a portal to document.body so it escapes
          .cat-card's `overflow: hidden` and the hover `transform` (which
          creates a containing block that traps position:fixed). React
          events still bubble through the React tree, so every handler in
          here stops propagation — otherwise clicking the backdrop would
          bubble up to the card's onClick and navigate to the category. */}
      {imagePopupOpen && cat.imageUrl && createPortal(
        <div
          className="cat-popup"
          role="dialog"
          aria-modal="true"
          aria-label={cat.name.he}
          onClick={e => { e.stopPropagation(); setImagePopupOpen(false); }}
        >
          <div className="cat-popup__frame" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              className="cat-popup__close"
              onClick={e => { e.stopPropagation(); setImagePopupOpen(false); }}
              aria-label="סגור"
            >×</button>
            <img className="cat-popup__img" src={cat.imageUrl} alt={cat.name.he} />
            <div className="cat-popup__footer">
              <h3 className="cat-popup__title">{cat.name.he}</h3>
              <button
                type="button"
                className="cat-popup__cta"
                disabled={soldOut}
                onClick={e => { e.stopPropagation(); setImagePopupOpen(false); handleOpen(); }}
              >
                {soldOut ? 'אזל המלאי' : 'להזמנה »'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </article>
  );
};


// ============================================================================
// Quick deal search — agents only
// ============================================================================
// A teacher browses: the categories are how the holiday is sold to them. An
// agent is on the phone with someone who has already named a hotel and a date,
// and walking a category, a filter and a scroll while that customer waits is
// the wrong shape entirely.
//
// Everything is fetched once and searched in the browser. A few hundred rows of
// text weigh less than one hotel photograph, and the result appears with no
// round trip at all.

type SearchDeal = {
  categoryCode: string;
  categoryName?: string | null;
  deal: DealVariantSummary;
};

// The search itself, with no opinion about how it looks. Two surfaces show
// it — the agents' compact strip and the teachers' banner — and the day they
// disagree about which dates a hotel has is the day one of them is wrong.
function useDealSearch() {
  const [all, setAll] = useState<SearchDeal[] | null>(null);
  const [hotelId, setHotelId] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [results, setResults] = useState<SearchDeal[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const r = await api('/api/front/categories/deal-search');
      if (!r.ok) { setErr('לא ניתן לטעון את רשימת הדילים.'); setAll([]); return; }
      const body = await r.json().catch(() => ({ items: [] }));
      setAll(body?.items ?? []);
    })();
  }, []);

  // Hotels that actually have a live deal, alphabetically — the list is derived
  // rather than fetched separately, so it can never disagree with the results.
  const hotels = useMemo(() => {
    const byId = new Map<string, { name: string; city: string }>();
    for (const s of all ?? []) {
      const h = s.deal.hotel;
      if (!h?.id) continue;
      if (!byId.has(h.id)) {
        byId.set(h.id, {
          name: h.name?.he || h.id,
          city: DESTINATION_HE[h.destination] || h.destination,
        });
      }
    }
    return [...byId.entries()]
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => a.name.localeCompare(b.name, 'he'));
  }, [all]);

  // The check-in dates this hotel actually sells. Offering a free calendar
  // would let an agent pick a date, press search and be told there is nothing —
  // the dates the hotel has are the answer, so they are the choices.
  // The allotment belongs to a date, not to a hotel: the same hotel can be
  // wide open one week and gone the next, and an agent choosing a date needs
  // to know which before they offer it.
  const dates = useMemo(() => {
    if (!hotelId) return [];
    const byDate = new Map<string, { nights: number; from: number; anyOpen: boolean }>();
    for (const s of all ?? []) {
      const d = s.deal;
      if (d.hotel?.id !== hotelId) continue;
      const soldOut = d.isSoldOut === true || d.allotment <= 0;
      const cur = byDate.get(d.checkIn);
      // "From": a hotel and date usually carry several deals — different
      // categories and boards at different prices — so a single price would
      // be a lie about at least one of them.
      //
      // The REGULAR price, not the Tmurah one (operator): it is what the card
      // is actually charged, so it is the number an agent can quote on the
      // phone without having to walk it back at checkout.
      byDate.set(d.checkIn, {
        nights: d.nights,
        from: cur ? Math.min(cur.from, d.grossPrice) : d.grossPrice,
        // Open or not, never how many. Summing d.allotment here printed a room
        // count on every date in this list — off the stored AvailRooms snapshot,
        // so "נותרו 14 חדרים" on a night the supplier had none. An agent quotes
        // this on the phone, which makes it the worst place on the site to be
        // approximately right. The real count needs a ledger call per hotel-night
        // and belongs where a member has picked one (see RoomsLeft).
        anyOpen: (cur?.anyOpen ?? false) || !soldOut,
      });
    }
    return [...byDate.entries()]
      .map(([checkIn, v]) => ({ checkIn, ...v }))
      .sort((a, b) => a.checkIn.localeCompare(b.checkIn));
  }, [all, hotelId]);

  const search = () => {
    if (!hotelId || !checkIn) return;
    const hits = (all ?? []).filter(s => s.deal.hotel?.id === hotelId && s.deal.checkIn === checkIn);
    setResults(hits);
    // One hotel, one night — so one gateway call covers every hit, and the rooms
    // left are the supplier's ledger rather than the stored snapshot the rest of
    // this list is built from. Fired after the results are on screen: the answer
    // is worth waiting a moment for, the results are not.
    void fillRoomsLeft(hits);
  };

  /**
   * Rooms actually left on these deals, patched in when the ledger answers.
   * A deal it cannot answer for keeps no number at all — see RoomsLeft — and a
   * failed call leaves the list exactly as it was.
   */
  async function fillRoomsLeft(hits: SearchDeal[]) {
    const ids = hits.map(h => h.deal.id).slice(0, 12);   // MaxRoomsLeftPerRequest
    if (ids.length === 0) return;
    let found: Array<{ dealId: string; left: number; soldOut: boolean }> = [];
    try {
      const res = await api(`/api/front/deals/rooms-left?dealIds=${encodeURIComponent(ids.join(','))}`);
      if (res.ok) found = (await res.json())?.items ?? [];
    } catch { /* no answer = no number */ }
    if (found.length === 0) return;
    const byDeal = new Map(found.map(x => [x.dealId, x]));
    setResults(prev => prev && prev.map(sd => {
      const answer = byDeal.get(sd.deal.id);
      if (!answer) return sd;
      return {
        ...sd,
        deal: {
          ...sd.deal,
          roomsLeft: answer.left,
          isSoldOut: sd.deal.isSoldOut === true || answer.soldOut,
        },
      };
    }));
  }

  const pickHotel = (id: string) => { setHotelId(id); setCheckIn(''); setResults(null); };
  const pickDate = (d: string) => { setCheckIn(d); setResults(null); };

  return { all, hotels, dates, hotelId, checkIn, results, err, search, pickHotel, pickDate };
}

const AgentDealSearch: React.FC<{ onOpenDeal: (d: SearchDeal) => void }> = ({ onOpenDeal }) => {
  const { all, hotels, dates, hotelId, checkIn, results, err, search, pickHotel, pickDate } = useDealSearch();

  return (
    <div className="agent-search">
      <div className="agent-search__title">
        <i className="bi bi-search" aria-hidden="true" /> חיפוש דיל מהיר
      </div>

      <div className="agent-search__row">
        <div className="agent-search__field">
          <label htmlFor="agent-search-hotel">שם המלון</label>
          <select
            id="agent-search-hotel"
            value={hotelId}
            onChange={e => pickHotel(e.target.value)}
            disabled={!all}
          >
            <option value="">{all ? 'בחרו מלון…' : 'טוען…'}</option>
            {hotels.map(h => (
              <option key={h.id} value={h.id}>{h.name} · {h.city}</option>
            ))}
          </select>
        </div>

        <div className="agent-search__field">
          <label htmlFor="agent-search-date">תאריך כניסה</label>
          <select
            id="agent-search-date"
            value={checkIn}
            onChange={e => pickDate(e.target.value)}
            disabled={!hotelId}
          >
            <option value="">{hotelId ? `בחרו תאריך (${dates.length})` : 'בחרו מלון קודם'}</option>
            {dates.map(d => (
              <option key={d.checkIn} value={d.checkIn}>
{dateOptionLabel(d, false)}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          className="irgon-btn-primary agent-search__go"
          onClick={search}
          disabled={!hotelId || !checkIn}
        >
          חפש
        </button>
      </div>

      {err && <div className="irgon-alert-error" style={{ marginTop: 12 }}>{err}</div>}

      {results && results.length === 0 && (
        <div className="agent-search__empty">לא נמצאו דילים למלון ולתאריך שנבחרו.</div>
      )}

      {results && results.length > 0 && (
        <div className="agent-search__results">
          {results.map(s => {
            const d = s.deal;
            const soldOut = d.isSoldOut === true || d.allotment <= 0;
            return (
              <div key={d.id} className={`agent-search__result${soldOut ? ' is-sold-out' : ''}`}>
                <div className="agent-search__result-main">
                  <div className="agent-search__result-hotel">
                    {d.hotel?.name?.he}
                    {s.categoryName && <span className="agent-search__result-cat">{s.categoryName}</span>}
                  </div>
                  <div className="agent-search__result-meta">
                    {fmtDate(d.checkIn)} – {fmtDate(d.checkOut)} · {d.nights} לילות
                    {' · '}{d.boardBasisName || d.boardBasis}
                    {' · '}{d.roomTypeName || d.roomType}
                  </div>
                </div>
                <div className="agent-search__result-price">
                  <span className="agent-search__result-gross">מחיר רגיל {fmtMoney(d.grossPrice, d.currency)}</span>
                  <span className="agent-search__result-net">מחיר תמורה {fmtMoney(d.netPrice, d.currency)}</span>
                </div>
                {soldOut ? (
                  // Shown rather than hidden: "that date is taken" is an answer
                  // an agent can give a customer. "I could not find it" is not.
                  <span className="agent-search__soldout">אזל</span>
                ) : (
                  <button type="button" className="irgon-btn-primary" onClick={() => onOpenDeal(s)}>
                    הזמן
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// "1 לילות" is wrong in Hebrew and the option text is the one place it shows.
// "24.9" — day and month. The year is the same for nearly every deal, so it
// spends four characters saying nothing on the screen with the least room.
const shortDate = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${Number(m[3])}.${Number(m[2])}` : iso;
};

const nightsLabel = (n: number) => (n === 1 ? 'לילה אחד' : `${n} לילות`);

// A phone cannot hold "25/09/2026 · 2 לילות · מ-3,188 ₪ · 15 חדרים" — it
// overflowed the control instead of fitting in it. Same facts, fewer glyphs.
const useNarrow = () => {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
};

const dateOptionLabel = (
  d: { checkIn: string; nights: number; from: number; anyOpen: boolean },
  narrow: boolean,
) => {
  const price = `מ-${Math.round(d.from).toLocaleString('he-IL')} ₪`;
  // "אזל" or nothing. A date that still sells says nothing about how much is
  // left, because the only count available here is the stored one and it is not
  // the remaining stock.
  const state = d.anyOpen ? '' : ' · אזל';
  return narrow
    ? `${fmtDate(d.checkIn).slice(0, 5)} · ${d.nights} ל' · ${price}${state}`
    : `${fmtDate(d.checkIn)} · ${nightsLabel(d.nights)} · ${price}${state}`;
};

// The same search, dressed for the storefront: a banner a member notices
// rather than a strip an agent works through. Identical logic — both call
// useDealSearch — so the two can never disagree about what a hotel has.
const TeacherDealSearch: React.FC<{ onOpenDeal: (d: SearchDeal) => void }> = ({ onOpenDeal }) => {
  const { all, hotels, dates, hotelId, checkIn, results, err, search, pickHotel, pickDate } = useDealSearch();
  const narrow = useNarrow();

  return (
    <section className="deal-finder" aria-labelledby="deal-finder-title">
      <h2 className="deal-finder__title" id="deal-finder-title">מצאו את החופשה שלכם</h2>

      <div className="deal-finder__card">
        <div className="deal-finder__field">
          <label htmlFor="finder-hotel">מלון</label>
          <div className="deal-finder__control">
            <span className="deal-finder__icon" aria-hidden="true">📍</span>
            <select
              id="finder-hotel"
              value={hotelId}
              onChange={e => pickHotel(e.target.value)}
              disabled={!all}
            >
              <option value="">{all ? 'בחרו מלון' : 'טוען…'}</option>
              {hotels.map(h => (
                <option key={h.id} value={h.id}>{h.name} · {h.city}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="deal-finder__field deal-finder__field--date">
          <label htmlFor="finder-date">תאריך</label>
          {/* A native <option> is a single line of plain text — the browser
              flattens anything else, and on a phone the picker belongs to the
              operating system entirely. So the phone gets a list it can
              actually read instead of a dropdown it cannot. */}
          {narrow ? (
            hotelId ? (
              <div className="deal-finder__dates" role="radiogroup" aria-label="תאריך כניסה">
                {dates.map(d => (
                  <button
                    key={d.checkIn}
                    type="button"
                    role="radio"
                    aria-checked={d.checkIn === checkIn}
                    className={`deal-finder__date${d.checkIn === checkIn ? ' is-selected' : ''}`}
                    onClick={() => pickDate(d.checkIn)}
                  >
                    <span className="deal-finder__date-when">
                      {shortDate(d.checkIn)} · {nightsLabel(d.nights)}
                    </span>
                    <span className="deal-finder__date-what">
                      {Math.round(d.from).toLocaleString('he-IL')} ₪
                      {!d.anyOpen && ' · אזל'}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="deal-finder__dates-empty">בחרו מלון קודם</div>
            )
          ) : (
            <div className="deal-finder__control">
              <span className="deal-finder__icon" aria-hidden="true">📅</span>
              <select
                id="finder-date"
                value={checkIn}
                onChange={e => pickDate(e.target.value)}
                disabled={!hotelId}
              >
                <option value="">{hotelId ? `בחרו תאריך (${dates.length})` : 'בחרו מלון קודם'}</option>
                {dates.map(d => (
                  <option key={d.checkIn} value={d.checkIn}>{dateOptionLabel(d, false)}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <button
          type="button"
          className="deal-finder__go"
          onClick={search}
          disabled={!hotelId || !checkIn}
        >
          חפש
        </button>
      </div>

      {err && <div className="irgon-alert-error deal-finder__msg">{err}</div>}

      {results && results.length === 0 && (
        <div className="deal-finder__msg">לא נמצאו חבילות למלון ולתאריך שנבחרו.</div>
      )}

      {results && results.length > 0 && (
        <div className="deal-finder__results">
          {results.map(s => {
            const d = s.deal;
            const soldOut = d.isSoldOut === true || d.allotment <= 0;
            return (
              <div key={d.id} className={`deal-finder__result${soldOut ? ' is-sold-out' : ''}`}>
                <div className="deal-finder__result-main">
                  <div className="deal-finder__result-hotel">
                    {d.hotel?.name?.he}
                    {s.categoryName && <span className="deal-finder__result-cat">{s.categoryName}</span>}
                    <RoomsLeft roomsLeft={d.roomsLeft} soldOut={soldOut} />
                  </div>
                  <div className="deal-finder__result-meta">
                    {fmtDate(d.checkIn)} – {fmtDate(d.checkOut)} · {d.nights} לילות
                    {' · '}{d.boardBasisName || d.boardBasis}
                    {' · '}{d.roomTypeName || d.roomType}
                  </div>
                </div>
                <div className="deal-finder__result-price">
                  <span className="deal-finder__result-gross">מחיר רגיל {fmtMoney(d.grossPrice, d.currency)}</span>
                  <span className="deal-finder__result-net">מחיר תמורה {fmtMoney(d.netPrice, d.currency)}</span>
                </div>
                {soldOut ? (
                  <span className="deal-finder__soldout">אזל</span>
                ) : (
                  <button type="button" className="irgon-btn-primary" onClick={() => onOpenDeal(s)}>
                    להזמנה
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

const HomePage: React.FC<{ me: Me; onOpenCategory: (cat: Category) => void; onOpenSearchDeal: (d: SearchDeal) => void }> = ({ me, onOpenCategory, onOpenSearchDeal }) => {
  const [cats, setCats] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const r = await api('/api/front/categories');
      if (r.ok) {
        const data = await r.json();
        setCats(data.items || []);
      }
      setLoading(false);
    })();
  }, []);

  return (
    <>
      <section className="irgon-hero">
        <div className="container irgon-hero__frame">
          <div className="irgon-hero__cover">
            <div className="irgon-hero__inner">
              <h1>אתר הנופש של ארגון המורים</h1>
              <p style={{ fontSize: 16, opacity: 0.92 }}>שלום {me.fullName}, נעים לראותך</p>
              {me.entitlementUsed && (
                <div className="irgon-hero__alert">
                  זכאות הסבסוד לשנת {me.eligibleYear} כבר נוצלה — לא ניתן לבצע הזמנה חדשה השנה.
                </div>
              )}
              {!me.isMember && (
                <div className="irgon-hero__alert">
                  אינך רשום כחבר ארגון המורים — לא תוכל לקבל סבסוד על הזמנות.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="container">
        <div className="home-layout">
          <HelpSidebar />
          <div className="home-layout__main">
            {/* One or the other, never both. The agent gets a working strip
                they move through all day; a member gets the banner. Same
                search underneath — see useDealSearch. */}
            {me.actingAgent
              ? <AgentDealSearch onOpenDeal={onOpenSearchDeal} />
              : <TeacherDealSearch onOpenDeal={onOpenSearchDeal} />}

            <div className="section-heading">
              <h2>חבילות נופש</h2>
              <p>חבילות מסובסדות בבתי מלון מובילים ברחבי הארץ</p>
            </div>

            {loading && <CategoryGridSkeleton count={6} />}

            {!loading && cats.length === 0 && (
              <div className="irgon-empty">
                <h5>אין דילים פעילים כרגע</h5>
                <p>המנהל יוסיף קטגוריות בקרוב.</p>
              </div>
            )}

            {!loading && cats.length > 0 && (
              <div className="category-grid">
                {cats.map(c => <CategoryCard key={c.id} cat={c} onOpen={onOpenCategory} />)}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

// ============================================================================
// Deal Card (used inside category detail)
// ============================================================================

// Eligibility-aware subsidy resolver — mirrors the gating used in
// DealDetailPage. Eligibility is anchored to deal.checkIn's year (a teacher
// who used their 2026 entitlement is still eligible for a Jan-2027 vacation).
// When ineligible we suppress the strike-through gross and treat net = gross,
// so the listing matches the price the teacher will actually be charged at
// /booking/init (the backend re-resolves there).
const DealCard: React.FC<{ deal: DealVariantSummary; onOpen: (d: DealVariantSummary) => void; compact?: boolean }> = ({ deal, onOpen, compact }) => {
  // Show both prices whenever the variant carries a subsidy (gross > net),
  // independent of personal eligibility — same rule used in the calendar.
  const hasTmurah = deal.netPrice < deal.grossPrice;
  const thumb = deal.hotel.thumbnailUrl;
  const shortDesc = deal.templateTitle?.he?.trim() || '';
  return (
    <article className={`deal-card ${compact ? 'deal-card--compact' : ''}`}>
      <HotelMedia
        hotel={deal.hotel}
        dealId={deal.id}
        image={thumb || null}
        dealRemark={deal.dealRemark}
        className="deal-card__media"
      />
      <div className="deal-card__body">
        <header className="deal-card__head">
          <h3 className="deal-card__title">{deal.hotel.name.he || 'מלון'}</h3>
          {deal.hotel.destination && (
            <div className="deal-card__loc"><PinIcon size={13} /> {destNameHe(deal.hotel.destination)}</div>
          )}
        </header>
        <ul className="deal-card__rows">
          <li className="deal-card__row"><span className="deal-card__ico" aria-hidden="true"><CalendarIcon /></span> כניסה: <strong>{fmtDate(deal.checkIn)}</strong></li>
          <li className="deal-card__row"><span className="deal-card__ico" aria-hidden="true"><CalendarIcon /></span> יציאה: <strong>{fmtDate(deal.checkOut)}</strong></li>
          <li className="deal-card__row"><span className="deal-card__ico" aria-hidden="true"><DiningIcon /></span> {boardBasisHe(deal.boardBasis)}</li>
        </ul>
        {shortDesc && <p className="deal-card__desc">{shortDesc}</p>}
        <div className="deal-card__prices">
          {hasTmurah && (
            <div className="deal-card__price-line">
              <span className="deal-card__price-label">מחיר רגיל</span>
              <span className="deal-card__price-regular">{fmtMoney(deal.grossPrice, deal.currency)}</span>
            </div>
          )}
          <div className="deal-card__price-line deal-card__price-line--final">
            <span className="deal-card__price-label">{hasTmurah ? 'מחיר תמורה' : 'מחיר'}</span>
            <span className="deal-card__price">{fmtMoney(hasTmurah ? deal.netPrice : deal.grossPrice, deal.currency)}</span>
          </div>
        </div>
        <button type="button" className="deal-card__cta deal-card__cta--full" onClick={() => onOpen(deal)}>
          הזמן עכשיו »
        </button>
      </div>
    </article>
  );
};

// ============================================================================
// Hotel List view — ONE row per hotel.
//   Image · Name+meta · Date chip (first date) · Prices · CTA.
//   When the hotel has multiple dates, a "+N more dates" link opens a date-
//   picker modal that lists every available range with its price. Picking a
//   date updates the row in place (and the CTA books that specific variant).
// Replaces the old multi-row-per-hotel "deal-table" layout — keeps rows
// scannable even for hotels with long date lists.
// ============================================================================

type HotelGroup = { hotelId: string; hotel: FrontHotelSummary; variants: DealVariantSummary[] };

const groupByHotel = (items: DealVariantSummary[]): HotelGroup[] => {
  const map = new Map<string, HotelGroup>();
  for (const it of items) {
    const key = it.hotel.id;
    let g = map.get(key);
    if (!g) { g = { hotelId: key, hotel: it.hotel, variants: [] }; map.set(key, g); }
    g.variants.push(it);
  }
  for (const g of map.values()) {
    g.variants.sort((a, b) => a.checkIn.localeCompare(b.checkIn));
  }
  return Array.from(map.values());
};

// One entry per check-in day PER SUPPLIER RATE LIST, earliest first. When two
// variants share both (different room class / board basis) prefer a bookable
// one, then the cheaper.
//
// The category is in the key because collapsing on the date alone hid real
// deals: Metropolitan sold the same night under D and under W — two prices, two
// different shows — and the page showed one of them. Romano's rule was "two
// room types on one night, show the cheaper", and that still holds; two rate
// lists were never the same thing.
const distinctByCheckIn = (variants: DealVariantSummary[]): DealVariantSummary[] => {
  const map = new Map<string, DealVariantSummary>();
  for (const v of variants) {
    const key = `${v.checkIn.slice(0, 10)}|${(v.hotelCategoryCode || '').trim().toUpperCase()}`;
    const cur = map.get(key);
    if (!cur) { map.set(key, v); continue; }
    const curSold = cur.isSoldOut === true;
    const vSold = v.isSoldOut === true;
    if (curSold && !vSold) { map.set(key, v); continue; }
    if (curSold === vSold && v.netPrice < cur.netPrice) map.set(key, v);
  }
  return Array.from(map.values()).sort((a, b) => a.checkIn.localeCompare(b.checkIn));
};

// Hebrew calendar labels — Sunday-first because Israel's work week starts on Sunday.
const HE_DOW_FULL = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

// Bare day name ("רביעי"). The row prints check-in and check-out together as
// "רביעי - חמישי" on their own line under the dates, so the "יום " prefix the
// old single-day label carried would only repeat itself.
const dowNameHe = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  return HE_DOW_FULL[d.getDay()];
};

const dowRangeHe = (checkIn: string, checkOut: string): string => {
  const a = dowNameHe(checkIn);
  const b = dowNameHe(checkOut);
  return a && b ? `${a} - ${b}` : a || b;
};

const nightsLabelHe = (n: number): string => (n === 1 ? 'לילה אחד' : `${n} לילות`);

// Anchor for a destination section, used by the mobile jump bar. Group codes
// are safe id characters already (region slugs / 3-letter city codes), but the
// prefix keeps them from colliding with anything else on the page.
const destAnchorId = (destination: string): string => `dest-${destination}`;

// The header is sticky; scrolling a section to y=0 would tuck its title
// underneath. Matches .irgon-header's mobile height plus a little air.
const STICKY_HEADER_OFFSET = 78;

// The row opens on the CHEAPEST date, not the earliest — teachers scan the
// category page by price, and the first date on the calendar is rarely the one
// they want quoted. Ties break towards a bookable date, then the earlier one.
// One card per hotel-and-date, CHEAPEST FIRST within the city. Room types
// collapse first (distinctByCheckIn keeps the bookable/cheaper of two rooms on
// the same night and rate list), so a hotel contributes one card per date PER
// RATE LIST — a night sold under two supplier categories is two deals at two
// prices and gets two cards. Grouping by hotel before that matters: the
// collapse is per hotel, and flattening first would drop one hotel's date
// because another hotel happened to sell the same night.
//
// Ordered by check-in until 2026-10-08, which was the layout Romano asked for.
// Price is what a teacher is actually scanning for ("אילת מהזול ליקר" — Ofir),
// and every card stands on its own: two dates of one hotel sit wherever their
// prices put them rather than travelling together. The outer grouping by city
// is unchanged, so this sorts inside אילת, inside ירושלים, and so on.
const cardsByPrice = (items: DealVariantSummary[]): DealVariantSummary[] =>
  groupByHotel(items)
    .flatMap(g => distinctByCheckIn(g.variants))
    .sort((a, b) =>
      // A deal the operator attached a poster to leads its section, whatever it
      // costs — attaching one IS the act of promoting it, and burying it under a
      // cheaper neighbour makes that act invisible. Every region, not only
      // ים המלח where it was first asked for (Ofir, 2026-10-20): a sort rule
      // that holds in one region and not another is indistinguishable from a bug
      // the first time somebody adds a poster in אילת.
      posterRank(a) - posterRank(b)
      // Then the number ON THE CARD, not netPrice: a deal with no תמורה shows
      // its gross, and sorting by a price the card never prints reads as broken.
      // Same expression DealCard and HotelDealCard use to choose what to print.
      || displayedPrice(a) - displayedPrice(b)
      || a.checkIn.localeCompare(b.checkIn));

/// 0 for a deal carrying its OWN poster, 1 for everything else.
///
/// Deliberately not "has a poster on screen": a category-wide poster falls back
/// onto every card in that category (see `poster` in HotelDealCard), so testing
/// the rendered image would rank the whole section equal and silently do
/// nothing. Only `showImageUrl` set on the deal itself is a choice about THIS
/// deal.
const posterRank = (v: DealVariantSummary): number =>
  v.showImageUrl && v.showImageUrl.trim().length > 0 ? 0 : 1;

/// The headline figure a card prints: the תמורה price when there is one,
/// otherwise the regular price.
const displayedPrice = (v: DealVariantSummary): number =>
  v.netPrice < v.grossPrice ? v.netPrice : v.grossPrice;

// ============================================================================
// Hotel info popup — opened by the card's photo
// ============================================================================

// Answers memoised per HOTEL for the life of the page, on top of the server's
// own five-minute per-hotel cache. Every date of the same hotel returns the same
// notes, so expanding the date list and opening a second card never re-fetches.
const hotelNotesCache = new Map<string, HotelNotes>();

// Keyed by hotel, addressed by deal: the deal is what carries the (supplier,
// code) tuple /hotel-notes needs upstream.
const fetchHotelNotes = async (hotelId: string, dealId: string): Promise<HotelNotes | null> => {
  const cached = hotelNotesCache.get(hotelId);
  if (cached) return cached;
  const r = await api(`/api/front/deals/${encodeURIComponent(dealId)}/hotel-notes`);
  if (!r.ok) return null;
  const data: HotelNotes = await r.json();
  hotelNotesCache.set(hotelId, data);
  return data;
};

// The hotel's photo gallery plus everything we know about it: the operator's
// notes, the facility chips, the marketing copy, and the supplier's note for
// the date the card that opened us is showing.
//
// Notes are fetched on OPEN, never on mount. An earlier version of this panel
// sat inline on every row and fetched as the category page loaded, fanning out
// one request per hotel; nothing here is readable without a click anyway, so
// the request waits for one.
const HotelInfoModal: React.FC<{
  hotelId: string;
  dealId: string;
  hotelName: string;
  fallbackImage?: string | null;
  dealRemark?: string | null;
  onClose: () => void;
}> = ({ hotelId, dealId, hotelName, fallbackImage, dealRemark, onClose }) => {
  const [notes, setNotes] = useState<HotelNotes | null>(() => hotelNotesCache.get(hotelId) ?? null);
  const [loading, setLoading] = useState(() => !hotelNotesCache.has(hotelId));

  useEffect(() => {
    if (hotelNotesCache.has(hotelId)) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchHotelNotes(hotelId, dealId);
        if (!cancelled && data) setNotes(data);
      } catch {
        /* A hotel the catalog doesn't cover still gets the popup — with the
           card's own photo and the deal note. Not worth an error banner. */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hotelId, dealId]);

  // Escape to close, like a native dialog. Bound while the popup is mounted.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const facilities = notes?.facilities ?? [];
  const catalogImages = (notes?.imageUrls ?? []).filter(u => !!u && u.trim().length > 0);
  // The card's own thumbnail is the last resort so the popup is never image-less
  // for a hotel the catalog carries no gallery for.
  const images = catalogImages.length > 0
    ? catalogImages
    : (fallbackImage ? [fallbackImage] : []);
  const remark = (dealRemark || '').trim();
  const hasAnyText = !!(remark || notes?.remark || notes?.description || facilities.length > 0);

  // Portal to document.body — same trap the category-card popup documents at
  // .cat-card: .hotel-row's hover `transform` creates a containing block, which
  // would pin our position:fixed overlay inside the card instead of covering the
  // viewport. Nothing above us in the React tree has an onClick, so events
  // bubbling back through the portal are harmless.
  return createPortal(
    <div
      className="irgon-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`מידע על ${hotelName}`}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="irgon-modal hotel-info-modal">
        <div className="irgon-modal__header irgon-modal__header--simple">
          <button type="button" className="irgon-modal__close" aria-label="סגור" onClick={onClose}>✕</button>
          <span className="irgon-modal__header-text">{hotelName}</span>
        </div>

        <div className="irgon-modal__body hotel-info-modal__body">
          {images.length > 0 && <HotelGallery images={images} alt={hotelName} />}

          {/* The supplier's note for THIS date. Comes from the card we were
              opened from, so it needs no fetch and renders as text, never HTML —
              it is free text typed by a supplier. */}
          {remark && (
            <>
              <h4 className="hotel-notes__section-title">הערות למבצע</h4>
              <p className="hotel-notes__plain">{remark}</p>
            </>
          )}

          {loading && (
            <div className="hotel-info-modal__loading" aria-hidden="true">
              <div className="skeleton skeleton--title" style={{ width: '40%' }} />
              <div className="skeleton skeleton--text" style={{ width: '95%' }} />
              <div className="skeleton skeleton--text" style={{ width: '88%' }} />
              <div className="skeleton skeleton--text" style={{ width: '60%' }} />
            </div>
          )}

          {notes?.remark && (
            <>
              <h4 className="hotel-notes__section-title">הערות למלון</h4>
              <div className="hotel-notes__html" dangerouslySetInnerHTML={{ __html: notes.remark }} />
            </>
          )}

          {facilities.length > 0 && (
            <>
              <h4 className="hotel-notes__section-title">שירותים במלון</h4>
              <ul className="hotel-notes__facilities">
                {facilities.map(f => <li key={f}>{f}</li>)}
              </ul>
            </>
          )}

          {notes?.description && (
            <>
              <h4 className="hotel-notes__section-title">אודות המלון</h4>
              <div className="hotel-notes__html" dangerouslySetInnerHTML={{ __html: notes.description }} />
            </>
          )}

          {!loading && !hasAnyText && (
            <p className="hotel-notes__empty">אין מידע נוסף על מלון זה.</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// The card's photo, as the trigger for the popup above. An ⓘ badge sits in the
// corner so the image reads as clickable — a plain photo doesn't.
//
// `className` is the card's own media class (.hotel-row__media / .deal-card__media)
// so the button keeps whatever sizing and grid placement that card gives it;
// .hotel-media only strips the browser's button chrome and anchors the badge.
const HotelMedia: React.FC<{
  hotel: FrontHotelSummary;
  dealId: string;
  image: string | null;
  dealRemark?: string | null;
  className: string;
}> = ({ hotel, dealId, image, dealRemark, className }) => {
  const [open, setOpen] = useState(false);
  const name = hotel.name.he || 'מלון';
  return (
    <>
      <button
        type="button"
        className={`${className} hotel-media${image ? '' : ` ${className}--placeholder`}`}
        style={image ? { backgroundImage: cssUrl(image) } : undefined}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={`תמונות ומידע על ${name}`}
        title={`תמונות ומידע על ${name}`}
      >
        {!image && <HotelIcon />}
        <span className="hotel-media__info" aria-hidden="true"><InfoIcon size={19} /></span>
      </button>

      {open && (
        <HotelInfoModal
          hotelId={hotel.id}
          dealId={dealId}
          hotelName={name}
          fallbackImage={image}
          dealRemark={dealRemark}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

// One card, one date. A hotel appears once per date it sells, so the same
// hotel can repeat down a destination — the list is ordered by check-in, not
// grouped by hotel (see cardsByPrice).
const HotelDealCard: React.FC<{
  hotel: FrontHotelSummary;
  variant: DealVariantSummary;
  fallbackImage: string | null;
  // The category-wide poster on a "הצגה" category, else null — the fallback for
  // deals with no picture of their own. The media slot on the right is the
  // hotel's photo, so on those categories the picture the deal is actually
  // about had nowhere to go; it lands in the free space beside the facts.
  showImage: string | null;
  onOpen: (d: DealVariantSummary) => void;
}> = ({ hotel, variant, fallbackImage, showImage, onOpen }) => {
  // Show both מחיר רגיל / מחיר תמורה whenever the variant itself has a subsidy
  // (gross > net) — independent of personal eligibility. Teachers whose 2026
  // entitlement is already used still need to see what the Tmurah price is.
  const hasTmurah = variant.netPrice < variant.grossPrice;
  const img = hotel.thumbnailUrl || fallbackImage || null;
  const star = hotel.starRating;
  const boardLabel = boardBasisHe(variant.boardBasis);
  const dealRemark = (variant.dealRemark || '').trim();
  const soldOut = variant.isSoldOut === true;
  // The deal's own poster first: the date strip swaps `variant`, so on a
  // "חופשה והופעה" hotel the picture re-points at the show playing on the
  // picked date. The category poster only stands in where none was attached.
  const poster = variant.showImageUrl || showImage || null;

  return (
    <>
      <article className="hotel-row">
        <HotelMedia
          hotel={hotel}
          dealId={variant.id}
          image={img}
          dealRemark={dealRemark}
          className="hotel-row__media"
        />

        <div className={`hotel-row__info${poster ? ' hotel-row__info--show' : ''}`}>
          <div className="hotel-row__title">
            <h4 className="hotel-row__name">{hotel.name.he || 'מלון'}</h4>
            {typeof star === 'number' && star > 0 && (
              <span className="hotel-row__stars" aria-label={`${star} כוכבים`}>
                {'★'.repeat(Math.round(star))}
              </span>
            )}
            {hotel.destination && (
              <span className="hotel-row__loc">
                <PinIcon size={13} /> {destNameHe(hotel.destination)}
              </span>
            )}
            <RoomsLeft roomsLeft={variant.roomsLeft} soldOut={soldOut} />
          </div>

          <ul className="hotel-row__facts">
            <li className="hotel-row__fact">
              <span className="hotel-row__fact-ico" aria-hidden="true"><CalendarIcon /></span>
              <span className="hotel-row__date-pill hotel-row__date-pill--static">
                <strong>{fmtDate(variant.checkIn)}</strong>
                <span className="hotel-row__date-sep">–</span>
                <strong>{fmtDate(variant.checkOut)}</strong>
              </span>
            </li>
            <li className="hotel-row__fact">
              <span className="hotel-row__fact-ico" aria-hidden="true"><DayIcon /></span>
              <span>{dowRangeHe(variant.checkIn, variant.checkOut)}</span>
            </li>
            <li className="hotel-row__fact">
              <span className="hotel-row__fact-ico" aria-hidden="true"><MoonIcon /></span>
              <span>{nightsLabelHe(variant.nights)}</span>
            </li>
            {boardLabel && (
              <li className="hotel-row__fact">
                <span className="hotel-row__fact-ico" aria-hidden="true"><DiningIcon /></span>
                <span>בסיס אירוח: <strong>{boardLabel}</strong></span>
              </li>
            )}
          </ul>

          {poster && (
            <img
              className="hotel-row__show"
              src={poster}
              alt="תמונת ההצגה"
              loading="lazy"
            />
          )}

          {dealRemark && (
            <div className="deal-remark">
              <span className="deal-remark__head">
                <span className="deal-remark__head-ico" aria-hidden="true"><InfoIcon size={15} /></span>
                <span className="deal-remark__head-text">הערות למבצע</span>
              </span>
              {/* Supplier free text, rendered as text — never injected as HTML. */}
              <span className="deal-remark__text">{dealRemark}</span>
            </div>
          )}

        </div>

        <div className="hotel-row__action">
          {hasTmurah && (
            <div className="hotel-row__price-line hotel-row__price-line--regular">
              <span className="hotel-row__price-label">מחיר רגיל</span>
              <span className="hotel-row__price-amount">
                {fmtMoney(variant.grossPrice, variant.currency)}
              </span>
            </div>
          )}
          <div className="hotel-row__price-line hotel-row__price-line--main">
            <span className="hotel-row__price-label">{hasTmurah ? 'מחיר תמורה' : 'מחיר'}</span>
            <span className="hotel-row__price-amount">
              {fmtMoney(hasTmurah ? variant.netPrice : variant.grossPrice, variant.currency)}
            </span>
          </div>
          <button
            type="button"
            className="hotel-row__cta"
            onClick={() => onOpen(variant)}
            disabled={soldOut}
          >
            {soldOut ? 'אזלו המקומות' : 'הזמן עכשיו »'}
          </button>
        </div>
      </article>
    </>
  );
};



const DealTableGroup: React.FC<{
  items: DealVariantSummary[];
  fallbackImage: string | null;
  showImage: string | null;
  onOpen: (d: DealVariantSummary) => void;
}> = ({ items, fallbackImage, showImage, onOpen }) => (
  <div className="hotel-list">
    {cardsByPrice(items).map(v => (
      <HotelDealCard
        key={v.id}
        hotel={v.hotel}
        variant={v}
        fallbackImage={fallbackImage}
        showImage={showImage}
        onOpen={onOpen}
      />
    ))}
  </div>
);

// ============================================================================
// Category Detail page
// ============================================================================

const CategoryDetailPage: React.FC<{
  category: Category;
  onBack: () => void;
  onOpenDeal: (deal: DealVariantSummary) => void;
}> = ({ category, onBack, onOpenDeal }) => {
  const [resp, setResp] = useState<DealsByCategoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [teacherInstructionsHtml, setTeacherInstructionsHtml] = useState<string | null>(null);
  // Persist the chosen view across reloads — teachers tend to settle on one.
  // Default is the compact one-row-per-hotel table; tiles is opt-in. The key
  // is suffixed ":v2" so the previous default ('tiles') stored under the old
  // key doesn't override the new default for users who never explicitly chose.
  const [viewMode, setViewMode] = useState<'tiles' | 'table'>(() => {
    try {
      const saved = window.localStorage.getItem('irgon:deals:view:v2');
      return saved === 'tiles' ? 'tiles' : 'table';
    } catch { return 'table'; }
  });
  useEffect(() => {
    try { window.localStorage.setItem('irgon:deals:view:v2', viewMode); } catch { /* ignore */ }
  }, [viewMode]);

  // Choosing a city jumps down to that section, and the only way back to the
  // city buttons was to scroll all of it by hand. Appears once the strip is off
  // screen, so it never sits on top of a page that is already at the top.
  const [showToTop, setShowToTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowToTop(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // How many rooms already have their picture, out of how many there are. The
  // page used to wait on one request that resolved every hotel's picture against
  // the catalog before answering, so a large category showed nothing at all for
  // as long as that took. Now the rooms arrive first and the pictures follow in
  // small batches — which is also the only way this counter can be honest.
  const [imgProgress, setImgProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      setImgProgress(null);
      const r = await api(`/api/front/categories/${encodeURIComponent(category.code)}/deals?images=false`);
      if (cancelled) return;
      if (r.ok) {
        const data: DealsByCategoryResponse = await r.json();
        setResp(data);
        // Both fill in behind the listing. Rooms-left is the one that matters —
        // the pictures are cosmetic, the room count is a promise to a member —
        // so it is not made to wait for them.
        void fillRoomsLeft(data);
        void fillHotelImages(data);
      } else {
        setErr('טעינת הדילים נכשלה. נסה שוב.');
      }
      setLoading(false);
      // After data is in the DOM, snap to the top — users land here mid-page when
      // the previous view was scrolled (e.g. opening a category from the home grid).
      requestAnimationFrame(() => {
        try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch { /* ignore */ }
      });
    })();

    /**
     * How many rooms are really left on each deal, from the supplier's allotment
     * ledger — and which deals it has just closed.
     *
     * A third phase, alongside the pictures and for the same reason: the rooms
     * must reach the member at once, and this costs one gateway call per
     * hotel-night. The card shows no room count until this lands, which is
     * deliberate — the number it used to show came from the stored deal and was
     * wrong on exactly the nights where it mattered (see RoomsLeft).
     *
     * Deals the ledger cannot answer for come back missing from the response and
     * keep no number and no flag — a free-sale night, or a gateway that is down,
     * must not read as "sold out".
     */
    async function fillRoomsLeft(data: DealsByCategoryResponse) {
      const ids = data.groups.flatMap(g => g.items).map(d => d.id);
      const CHUNK = 12;   // matches MaxRoomsLeftPerRequest on the server
      for (let i = 0; i < ids.length; i += CHUNK) {
        if (cancelled) return;
        const batch = ids.slice(i, i + CHUNK);
        let found: Array<{ dealId: string; left: number; soldOut: boolean }> = [];
        try {
          const res = await api(
            `/api/front/deals/rooms-left?dealIds=${encodeURIComponent(batch.join(','))}`);
          if (res.ok) found = (await res.json())?.items ?? [];
        } catch { /* no answer = no number, which is the safe direction */ }
        if (cancelled || found.length === 0) continue;

        const byDeal = new Map(found.map(x => [x.dealId, x]));
        setResp(prev => prev && ({
          ...prev,
          groups: prev.groups.map(g => ({
            ...g,
            items: g.items.map(d => {
              const answer = byDeal.get(d.id);
              if (!answer) return d;
              return {
                ...d,
                roomsLeft: answer.left,
                // The ledger closes a deal; it never reopens one the operator
                // closed (the server keeps that flag set), so OR is right here.
                isSoldOut: d.isSoldOut === true || answer.soldOut,
              };
            }),
          })),
        }));
      }
    }

    async function fillHotelImages(data: DealsByCategoryResponse) {
      const all = data.groups.flatMap(g => g.items);
      // A room whose hotel already carries a picture is finished the moment it
      // arrives; only the rest are waited on, so the bar starts where it truly is.
      const pending = Array.from(new Set(
        all.filter(d => !d.hotel.thumbnailUrl).map(d => d.hotel.id)));
      const totalRooms = all.length;
      const roomsWithImage = all.filter(d => !!d.hotel.thumbnailUrl).length;
      if (pending.length === 0 || totalRooms === 0) { setImgProgress(null); return; }

      let done = roomsWithImage;
      setImgProgress({ done, total: totalRooms });

      const CHUNK = 4;
      for (let i = 0; i < pending.length; i += CHUNK) {
        if (cancelled) return;
        const ids = pending.slice(i, i + CHUNK);
        let found: Array<{ hotelId: string; imageUrl: string }> = [];
        try {
          const res = await api(
            `/api/front/categories/${encodeURIComponent(category.code)}/hotel-images`
            + `?hotelIds=${encodeURIComponent(ids.join(','))}`);
          if (res.ok) found = (await res.json())?.items ?? [];
        } catch { /* a picture that never resolves leaves the card on its fallback */ }
        if (cancelled) return;

        // Every hotel in the batch is accounted for whether or not it had a
        // picture — otherwise a category of hotels with none would sit at 0%.
        const byHotel = new Map(found.map(x => [x.hotelId, x.imageUrl]));
        done += all.filter(d => ids.includes(d.hotel.id)).length;
        setImgProgress({ done: Math.min(done, totalRooms), total: totalRooms });
        if (byHotel.size > 0) {
          setResp(prev => prev && ({
            ...prev,
            groups: prev.groups.map(g => ({
              ...g,
              items: g.items.map(d => byHotel.has(d.hotel.id)
                ? { ...d, hotel: { ...d.hotel, thumbnailUrl: byHotel.get(d.hotel.id)! } }
                : d),
            })),
          }));
        }
      }
      if (!cancelled) setImgProgress(null);
    }

    return () => { cancelled = true; };
  }, [category.code]);

  // Global teacher-instructions HTML (set in Admin → Site Settings). Cached 5 min server-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/api/front/site-settings');
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setTeacherInstructionsHtml(data?.teacherInstructionsHtml || null);
      } catch { /* ignore — falls back to no panel */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const totalItems = resp?.groups.reduce((sum, g) => sum + g.items.length, 0) ?? 0;

  return (
    <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>
      <a href="#" className="irgon-back" onClick={e => { e.preventDefault(); onBack(); }}>
        ← חזרה לדף הבית
      </a>

      {/* Category hero banner + zoom popup removed per request — no image below the header on the category page. */}

      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontWeight: 800, color: 'var(--color-primary-ink)', margin: '0 0 6px', fontSize: 30 }}>
            {category.name.he}
          </h2>
          {category.description?.he && (
            <p style={{ color: 'var(--color-muted)', margin: 0, fontSize: 15 }}>{category.description.he}</p>
          )}
          {/* Shows and plays only: the ticket does not come with the booking
              confirmation, and a guest who does not know that arrives at the
              venue looking for one. */}
          {category.code === 'SHAVHOT' && (
            <p style={{ color: 'var(--color-primary-ink)', margin: '8px 0 0', fontSize: 15, fontWeight: 700 }}>
              כרטיסים להצגות/הופעות ישלחו באמצעות הודעות סמס בסמוך למועד
            </p>
          )}
        </div>
        {!loading && !err && totalItems > 0 && (
          <div className="view-toggle" role="group" aria-label="תצוגה">
            <button
              type="button"
              className={`view-toggle__btn ${viewMode === 'table' ? 'is-active' : ''}`}
              onClick={() => setViewMode('table')}
              aria-pressed={viewMode === 'table'}
              title="טבלה"
            >☰ טבלה</button>
            <button
              type="button"
              className={`view-toggle__btn ${viewMode === 'tiles' ? 'is-active' : ''}`}
              onClick={() => setViewMode('tiles')}
              aria-pressed={viewMode === 'tiles'}
              title="קוביות"
            >▦ קוביות</button>
          </div>
        )}
      </div>

      {/* Destination jump bar. The sections are far apart on any screen —
          reaching the third one means scrolling past every hotel above it.
          One chip per group ACTUALLY returned, in the order they render —
          never a hard-coded city list. Hidden when there is only one group:
          a jump bar to the single thing on screen is noise. */}
      {!loading && !err && (resp?.groups.length ?? 0) > 1 && (
        <nav className="dest-jump" aria-label="קפיצה ליעד">
          {resp!.groups.map(group => (
            <button
              key={group.destination}
              type="button"
              className="dest-jump__btn"
              onClick={() => {
                const el = document.getElementById(destAnchorId(group.destination));
                if (!el) return;
                // Offset the sticky header, otherwise it covers the heading we
                // just scrolled to.
                const top = el.getBoundingClientRect().top + window.scrollY - STICKY_HEADER_OFFSET;
                window.scrollTo({ top, behavior: 'smooth' });
              }}
            >
              {group.label || group.destination}
            </button>
          ))}
        </nav>
      )}

      {loading && (
        <>
          {/* Before the rooms land there is nothing real to count, so the bar
              says what it is doing and shows no figure it cannot stand behind. */}
          <div className="load-progress" role="status" aria-live="polite">
            <div className="load-progress__bar">
              <span className="load-progress__fill load-progress__fill--idle" />
            </div>
            <div className="load-progress__text">טוענים את החדרים בקטגוריה…</div>
          </div>
          <CategoryDetailSkeleton count={5} viewMode={viewMode} />
        </>
      )}

      {!loading && imgProgress && imgProgress.total > 0 && (
        <div className="load-progress" role="status" aria-live="polite">
          <div className="load-progress__bar">
            <span
              className="load-progress__fill"
              style={{ width: `${Math.round((imgProgress.done / imgProgress.total) * 100)}%` }}
            />
          </div>
          <div className="load-progress__text">
            נטענו {imgProgress.done} מתוך {imgProgress.total} חדרים
            {' · '}
            {Math.round((imgProgress.done / imgProgress.total) * 100)}%
          </div>
        </div>
      )}
      {err && <div className="irgon-alert-error">{err}</div>}

      {!loading && !err && totalItems === 0 && (
        <div className="irgon-empty">
          <h5>אין דילים זמינים בקטגוריה זו</h5>
          <p>דילים חדשים יתווספו בקרוב.</p>
        </div>
      )}

      {!loading && !err && resp?.groups.map(group => (
        <section key={group.destination} id={destAnchorId(group.destination)} className="dest-section">
          <h3 className="dest-section__title">
            <span>{group.label || group.destination}</span>
            <span className="dest-section__count">({group.items.length} דילים)</span>
          </h3>
          {viewMode === 'table' ? (
            <DealTableGroup
              items={group.items}
              fallbackImage={category.imageUrl || category.thumbnailUrl || null}
              // Non-null only on a "הצגה" category with an image attached — the
              // API withholds the generic banner here on purpose, so a show
              // without a poster simply renders the card as it always was.
              showImage={category.showImageUrl || null}
              onOpen={onOpenDeal}
            />
          ) : (
            <div className="deal-grid">
              {cardsByPrice(group.items).map(v => (
                <DealCard key={v.id} deal={v} onOpen={onOpenDeal} />
              ))}
            </div>
          )}
        </section>
      ))}

      {/* Global teacher-instructions panel — appears beneath the deals on every category page.
          Edited in Admin → "הגדרות אתר". Mirrors the legacy weeken_rules_text from the old site. */}
      {teacherInstructionsHtml && (
        <div className="teacher-instructions" title="הוראות למורה">
          <div dangerouslySetInnerHTML={{ __html: teacherInstructionsHtml }} />
        </div>
      )}

      {showToTop && (
        <button
          type="button"
          className="to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="חזרה לבחירת עיר"
          title="חזרה לבחירת עיר"
        >
          <span aria-hidden="true">↑</span>
          <span className="to-top__text">חזרה לבחירת עיר</span>
        </button>
      )}
    </div>
  );
};

// ============================================================================
// Deal Detail page
// ============================================================================

type LivePrice = {
  grossPrice: number;
  subsidyAmount: number;
  netPrice: number;
  currency: string;
  available: boolean;
  fetchedAt: string;
  source: 'live' | 'stored';
};

// Image gallery — main image + thumbnail rail. Click main to open full-screen.
const HotelGallery: React.FC<{ images: string[]; alt: string }> = ({ images, alt }) => {
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);

  if (images.length === 0) {
    return <div className="deal-hero deal-hero--empty" aria-hidden="true">🏨</div>;
  }

  const prev = () => setIdx(i => (i <= 0 ? images.length - 1 : i - 1));
  const next = () => setIdx(i => (i >= images.length - 1 ? 0 : i + 1));

  return (
    <>
      <div className="deal-gallery">
        <button
          type="button"
          className="deal-gallery__main"
          style={{ backgroundImage: cssUrl(images[idx]) }}
          onClick={() => setOpen(true)}
          aria-label="הצג תמונות"
        >
          {images.length > 1 && (
            <span className="deal-gallery__count">{idx + 1} / {images.length}</span>
          )}
        </button>
        {images.length > 1 && (
          <div className="deal-gallery__thumbs">
            {images.slice(0, 6).map((url, i) => (
              <button
                key={i}
                type="button"
                className={`deal-gallery__thumb${i === idx ? ' is-active' : ''}`}
                onClick={() => setIdx(i)}
                style={{ backgroundImage: cssUrl(url) }}
                aria-label={`תמונה ${i + 1}`}
              />
            ))}
            {images.length > 6 && (
              <button type="button" className="deal-gallery__thumb deal-gallery__thumb--more" onClick={() => setOpen(true)}>
                +{images.length - 6}
              </button>
            )}
          </div>
        )}
      </div>

      {open && (
        <div className="deal-lightbox" role="dialog" aria-modal="true" onClick={() => setOpen(false)}>
          <button type="button" className="deal-lightbox__close" aria-label="סגור" onClick={() => setOpen(false)}>✕</button>
          <button type="button" className="deal-lightbox__nav deal-lightbox__nav--prev" onClick={e => { e.stopPropagation(); prev(); }} aria-label="הקודם">‹</button>
          <img className="deal-lightbox__img" src={images[idx]} alt={alt} onClick={e => e.stopPropagation()} />
          <button type="button" className="deal-lightbox__nav deal-lightbox__nav--next" onClick={e => { e.stopPropagation(); next(); }} aria-label="הבא">›</button>
          <div className="deal-lightbox__counter">{idx + 1} / {images.length}</div>
        </div>
      )}
    </>
  );
};

const DealDetailPage: React.FC<{
  dealVariantId: string;
  me: Me;
  onBackToCategory: () => void;
  onBackToHome: () => void;
}> = ({ dealVariantId, me, onBackToCategory, onBackToHome }) => {
  const [deal, setDeal] = useState<DealVariantDetail | null>(null);
  const [livePrice, setLivePrice] = useState<LivePrice | null>(null);
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [liveAvailability, setLiveAvailability] = useState<LiveAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Booking form state — single lead guest. Pre-fill from /me so the teacher
  // only types once.
  const splitFullName = (full?: string): { firstName: string; lastName: string } => {
    if (!full) return { firstName: '', lastName: '' };
    const parts = full.trim().split(/\s+/);
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  };
  const initial = splitFullName(me.fullName);
  const [guest, setGuest] = useState<GuestForm>({
    firstName: initial.firstName,
    lastName: initial.lastName,
    tz: me.tz || '',
    phone: me.mobile || '',
    email: me.email || '',
  });
  // Whether these fields are typed is a property of the PROFILE, fixed when the
  // page loads — not of what is currently in them. Deriving it from the live
  // value swapped the input back for a label on the first keystroke, so a name
  // could be exactly one character long.
  const [nameEditable] = useState(() => !(me.fullName ?? '').trim());
  const [phoneEditable] = useState(() => !!me.canEditContactPhone || !(me.mobile ?? '').trim());
  // One field for a full name, split into first/last only when the booking is
  // built. Binding the input to firstName alone dropped everything after the
  // first space as it was typed.
  const [fullNameInput, setFullNameInput] = useState(
    `${initial.firstName} ${initial.lastName}`.trim());

  const [termsAccepted, setTermsAccepted] = useState(false);
  // Takanon confirmation popup — opens when the teacher ticks the terms
  // checkbox; only a recorded acceptance turns the checkbox on.
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  // Room composition. Null means the deal as imported — a couple — which is what
  // every price on the page describes until the member changes it.
  const [occupancyModalOpen, setOccupancyModalOpen] = useState(false);
  const [occupancy, setOccupancy] = useState<OccupancySelection | null>(null);
  const [installments, setInstallments] = useState(1);
  const [aboutOpen, setAboutOpen] = useState(false);

  // Admin-curated booking policy HTML shown above the terms checkbox. Loaded
  // from /api/front/site-settings (anonymous, cached 5 min server-side). Falls
  // back to the legacy default when the admin hasn't customised the field.
  const [bookingPolicyHtml, setBookingPolicyHtml] = useState<string | null>(null);
  const [takanonHtml, setTakanonHtml] = useState<string | null>(null);
  const [bookingTimeoutMinutes, setBookingTimeoutMinutes] = useState<number>(DEFAULT_BOOKING_TIMEOUT_MINUTES);

  // Payment state
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingErr, setBookingErr] = useState<string | null>(null);
  const [paymentInit, setPaymentInit] = useState<BookingInitResponse | null>(null);

  // Cross-deal pending booking — set when the teacher already has an in-progress
  // reservation on a DIFFERENT deal. Blocks the booking form and renders a
  // banner that routes back to the original deal so the per-deal resume probe
  // can rebuild the Pelecard iframe there. Mirrors the server-side gate in
  // FrontBookingApiController.Init ("pending_booking_exists" 409).
  const [pendingOther, setPendingOther] = useState<PendingBooking | null>(null);
  const [cancelPendingBusy, setCancelPendingBusy] = useState(false);
  const [cancelPendingErr, setCancelPendingErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      const r = await api(`/api/front/deals/${encodeURIComponent(dealVariantId)}`);
      if (cancelled) return;
      if (r.ok) setDeal(await r.json());
      else if (r.status === 404) setErr('הדיל לא נמצא או הוסר.');
      else setErr('טעינת הדיל נכשלה. נסה שוב.');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dealVariantId]);

  // Fetch live price after the deal loads. The backend caches per-deal for 5 min.
  useEffect(() => {
    if (!deal) return;
    let cancelled = false;
    (async () => {
      setLivePriceLoading(true);
      try {
        const r = await api(`/api/front/deals/${encodeURIComponent(dealVariantId)}/live-price`);
        if (cancelled) return;
        if (r.ok) setLivePrice(await r.json());
      } finally {
        if (!cancelled) setLivePriceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deal, dealVariantId]);

  // Fresh per-deal availability probe — server caches Atlantis response for 20s
  // and writes the sold-out flip back onto the variant so the storefront
  // categories listing (and the date picker) catches up on its next refresh.
  // Runs independently of livePrice so a price miss doesn't block the
  // sold-out banner.
  useEffect(() => {
    if (!deal) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api(`/api/front/deals/${encodeURIComponent(dealVariantId)}/live-availability`);
        if (cancelled) return;
        if (r.ok) setLiveAvailability(await r.json());
      } catch { /* non-fatal — banner falls back to the stored isSoldOut on the detail DTO */ }
    })();
    return () => { cancelled = true; };
  }, [deal, dealVariantId]);

  // Admin-curated booking policy text (Site Settings → "הודעה לפני אישור הזמנה").
  // The endpoint is anonymous and cached server-side for 5 min, so we hit it
  // optimistically on every checkout — the rendered fallback below means an
  // empty/failed response just shows the legacy default.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/api/front/site-settings');
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) {
          setBookingPolicyHtml(data?.bookingPolicyHtml || null);
          setTakanonHtml(data?.takanonHtml || null);
          if (typeof data?.bookingTimeoutMinutes === 'number' && data.bookingTimeoutMinutes > 0) {
            setBookingTimeoutMinutes(data.bookingTimeoutMinutes);
          }
        }
      } catch { /* ignore — falls back to the built-in default below */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resume probe — runs on page mount + on F5. If this teacher already has an
  // active reservation for this deal (PNR minted, hasn't paid yet) the server
  // returns a BookingInitResponse with a fresh Pelecard iframe URL; we set it
  // straight into paymentInit so the iframe renders immediately instead of
  // the form. 204 No Content means "nothing to resume, render the form".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api(`/api/front/booking/active-for-deal/${encodeURIComponent(dealVariantId)}`);
        if (cancelled) return;
        if (r.status === 200) {
          const data: BookingInitResponse = await r.json();
          if (showConfirmationIfPaid(data)) return;
          setPaymentInit(data);
        }
      } catch { /* non-fatal — fall back to the normal form flow */ }
    })();
    return () => { cancelled = true; };
  }, [dealVariantId]);

  // Cross-deal pending probe — runs on every deal page mount. If the teacher
  // already has an in-progress reservation on a DIFFERENT deal we surface a
  // blocking banner that routes them back to that deal so they can finish the
  // payment (or wait for the sweeper to auto-cancel). Same-deal pending is
  // handled by the resume probe above (renders the iframe in place).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/api/front/booking/pending');
        if (cancelled) return;
        if (r.status === 200) {
          const data: PendingBooking = await r.json();
          if (data.dealVariantId !== dealVariantId) {
            setPendingOther(data);
          } else {
            setPendingOther(null);
          }
        } else {
          setPendingOther(null);
        }
      } catch { /* non-fatal — proceed to the normal form flow */ }
    })();
    return () => { cancelled = true; };
  }, [dealVariantId]);

  // Field validators
  const isEmailValid = (s: string) => /.+@.+\..+/.test(s);
  // True when any signal (stored DTO flag, fresh 20s availability probe)
  // tells us this deal is sold out — used to gate the booking CTA + render
  // the banner. liveAvailability wins when present because it's <= 20s old.
  const isSoldOut =
    liveAvailability?.isSoldOut === true
    || (liveAvailability == null && deal?.isSoldOut === true);
  // The TZ / full-name / phone come from the authenticated session — they're
  // already trusted. Only the email is user-typed (since teachers don't have
  // to register it during login), so it's the only thing we validate.
  // Sold-out deals also block the CTA — the same flag is what disables the
  // date picker on the storefront, but a teacher could reach the page via a
  // direct link / refresh after sell-out, so we re-gate here.
  // Name and phone were always required — they just used to be guaranteed by
  // the profile. Now that they can be typed, they have to be checked: the name
  // is what reaches the hotel and the phone is where the confirmation goes.
  const formValid =
    isEmailValid(guest.email)
    && `${guest.firstName} ${guest.lastName}`.trim().length > 1
    && guest.phone.replace(/\D/g, '').length >= 9
    && termsAccepted
    && !isSoldOut;

  const startBooking = async () => {
    if (!deal || !formValid || bookingBusy) return;
    setBookingBusy(true);
    setBookingErr(null);
    try {
      // If the user typed a new email (or it changed since /me), persist it on
      // the teacher profile so the next booking pre-fills automatically.
      const trimmedEmail = guest.email.trim();
      if (trimmedEmail && trimmedEmail !== (me.email || '').trim()) {
        try {
          await api('/api/front/me', {
            method: 'PUT',
            body: JSON.stringify({ email: trimmedEmail }),
          });
        } catch {
          // Non-fatal — booking proceeds even if profile update fails.
        }
      }

      const r = await api('/api/front/booking/init', {
        method: 'POST',
        body: JSON.stringify({
          dealVariantId: deal.id,
          noOfPayments: installments,
          contactEmail: trimmedEmail,
          contactPhone: guest.phone,
          // The server re-prices this against the supplier — it is sent so the
          // booking describes the right room, never so the browser can name a
          // price.
          occupancyCode: occupancy?.code ?? '2,0,0',
          // One name on the booking — whoever made it. The children and infants
          // reach the supplier as the room's extra beds and cots, derived from
          // the composition server-side; inventing passenger names for minors
          // put rows reading "ילד" on a live PNR.
          passengers: [{
            firstName: guest.firstName.trim(),
            lastName: guest.lastName.trim(),
            tz: guest.tz.trim(),
            isLead: true,
          }],
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const code = body?.error || body?.code;
        // Server-side cross-deal pending guard — surface the banner so the
        // teacher can navigate to the dangling reservation's deal and finish
        // payment there. Details carries reservationId + dealVariantId.
        if (code === 'pending_booking_exists' && body?.details?.dealVariantId) {
          setPendingOther({
            reservationId: body.details.reservationId,
            dealVariantId: body.details.dealVariantId,
            hotelName: null,
            checkIn: deal!.checkIn,
            checkOut: deal!.checkOut,
            atlantisPnr: body.details.atlantisPnr ?? null,
            publicNumber: 0,
            state: 'AwaitingPayment',
            createdAt: new Date().toISOString(),
          });
          setBookingErr(null);
          return;
        }
        // Server-side takanon gate — the recorded acceptance is missing or aged
        // out. Reset the checkbox and re-open the confirmation popup.
        if (code === 'terms_not_confirmed') {
          setTermsAccepted(false);
          setTermsModalOpen(true);
          setBookingErr('יש לאשר את תקנון הנופש המסובסד לפני המעבר לתשלום.');
          return;
        }
        const msg =
          code === 'entitlement_used_this_year' ? 'זכאות הסבסוד נוצלה כבר השנה.' :
          // The supplier's allotment for this exact night is taken. Not a retry
          // case and not a "try again later" — the rooms are gone, so the message
          // has to send the teacher somewhere else rather than back to the button.
          code === 'deal_sold_out' ? 'אזלו המקומות בתאריך זה. אפשר לבחור תאריך אחר או מלון אחר.' :
          code === 'deal_unavailable' ? 'הדיל אינו זמין כעת.' :
          code === 'atlantis_unavailable' || code === 'atlantis_rejected' ? 'הספק אינו זמין כרגע. נסה שוב בעוד מספר דקות.' :
          code === 'pelecard_unavailable' ? 'מערכת התשלומים אינה זמינה כרגע.' :
          body?.message || 'יצירת ההזמנה נכשלה. נסה שוב.';
        setBookingErr(msg);
        return;
      }
      const data: BookingInitResponse = await r.json();
      if (showConfirmationIfPaid(data)) return;
      setPaymentInit(data);
    } catch {
      setBookingErr('שגיאה בלתי צפויה. נסה שוב.');
    } finally {
      setBookingBusy(false);
    }
  };

  const paymentIframeUrl = paymentInit?.paymentUrl || paymentInit?.rqTokenUrl || null;

  return (
    <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>
      <a href="#" className="irgon-back" onClick={e => { e.preventDefault(); onBackToCategory(); }}>
        ← חזור לקטגוריה
      </a>

      {loading && <DealDetailSkeleton />}
      {err && (
        <div className="irgon-alert-error">
          {err}
          <div style={{ marginTop: 10 }}>
            <button type="button" className="irgon-header__logout-btn" onClick={onBackToHome}>
              חזרה לדף הבית
            </button>
          </div>
        </div>
      )}

      {!loading && !err && deal && (() => {
        // Retiree categories opt out of the whole subsidy mechanism — the
        // booking proceeds at gross price like a regular tourist booking and
        // we don't gate the CTA on entitlement.
        const retireeCategory = deal.categoryIsForRetirees === true;
        // Every price on this page arrives from the API already post-subsidy —
        // the category's amount came off server-side (CategorySubsidy.Deduct),
        // which is the same figure FrontBookingApiController.Init charges at
        // Pelecard. Nothing here re-derives it, so the page cannot drift from
        // the charge.
        // hasTmurah = there is still a "מחיר תמורה" below gross (the ≈5%
        // display discount, applied before the subsidy came off both lines).
        const hasTmurah = !retireeCategory && deal.netPrice < deal.grossPrice;
        // Organization membership is the upstream-IGM truth (mirrored to
        // TeacherProfile.IsMember at login). Non-members cannot use the
        // entitlement slot but the *price* is the same — the gross.
        const isMember = me.isMember !== false;
        // Eligibility is anchored to the VACATION-START year (deal.checkIn),
        // not the current calendar year. A teacher who used their 2026
        // entitlement is still eligible for a January-2027 vacation.
        const checkInYear = deal.checkIn ? new Date(deal.checkIn).getFullYear() : (me.eligibleYear ?? new Date().getFullYear());
        const usedYears = me.entitlementUsedYears ?? (me.entitlementUsed ? [me.eligibleYear ?? new Date().getFullYear()] : []);
        const eligible = retireeCategory || (isMember && !usedYears.includes(checkInYear));
        // "לתשלום" = the actual amount charged at Pelecard. grossPrice is the
        // post-subsidy figure the API already resolved, so this stays a
        // straight read — never gross minus something computed here.
        // The composition the member picked replaces the deal's own price, because
        // the stored one only ever describes the composition the deal was
        // imported for. Displayed and charged from the same variable, so the
        // panel and the card can never show different numbers.
        const effectiveGross = occupancy ? occupancy.grossPrice : deal.grossPrice;
        const effectiveTmurah = occupancy ? occupancy.netPrice : deal.netPrice;
        const effectiveNet = effectiveGross;
        // Compositions with children were held back until a live test PNR could
        // confirm the passenger-type codes. Opened at the operator's decision so
        // that test can be made through the site itself; the price is re-quoted
        // server-side either way, so what is booked is what the supplier offered.
        const images = deal.imageUrls && deal.imageUrls.length > 0
          ? deal.imageUrls
          : (deal.hotel.thumbnailUrl ? [deal.hotel.thumbnailUrl] : []);
        const mapQuery = encodeURIComponent(`${deal.hotel.name.he || 'מלון'} ${deal.hotel.destination}`);

        // Cross-deal pending blocker — the teacher already has an in-progress
        // reservation on a DIFFERENT deal. Render a full-page banner that
        // routes them back to the dangling reservation's deal page (where the
        // per-deal resume probe rebuilds the Pelecard iframe). The form is
        // intentionally NOT rendered behind the banner — the server gate in
        // FrontBookingApiController.Init would refuse /init with 409 anyway,
        // and surfacing the form would just confuse the teacher. The sweeper
        // will auto-cancel the dangling reservation after the configured
        // BookingTimeoutMinutes (default 15), at which point the banner
        // disappears on the next page mount.
        if (pendingOther) {
          const pendingHotelHe = pendingOther.hotelName?.he || 'הזמנה קודמת';
          const goFinish = () => {
            window.location.hash = `#/deals/${encodeURIComponent(pendingOther.dealVariantId)}`;
          };
          return (
            <div className="ck-payment" style={{ maxWidth: 720, margin: '0 auto' }}>
              <div className="ck-payment__head">
                <h2>יש לך הזמנה פתוחה ⏳</h2>
                <p>לא ניתן להתחיל הזמנה חדשה לפני סיום או ביטול ההזמנה הקודמת.</p>
              </div>
              <div style={{ padding: 26 }}>
                <div className="irgon-alert-error" style={{ marginBottom: 16 }}>
                  <strong>{pendingHotelHe}</strong> · {fmtDate(pendingOther.checkIn)}{pendingOther.checkOut ? ` → ${fmtDate(pendingOther.checkOut)}` : ''}
                  {pendingOther.atlantisPnr && (
                    <div style={{ marginTop: 6, fontSize: 13 }}>
                      מס׳ הזמנה: <code dir="ltr">{pendingOther.atlantisPnr}</code>
                    </div>
                  )}
                </div>
                <p style={{ fontSize: 14, color: 'var(--color-muted)', marginBottom: 20 }}>
                  סיים את התשלום עכשיו, בטל את ההזמנה הקודמת, או חכה כ-{bookingTimeoutMinutes} דקות מרגע יצירתה — והיא תבוטל אוטומטית.
                </p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="ck-form__cta"
                    style={{ flex: '1 1 220px', minWidth: 220 }}
                    onClick={goFinish}
                  >
                    המשך לתשלום ←
                  </button>
                  {/* Manual escape from the block. Without it the only way out
                      of "you have an open booking" was to sit out the sweeper's
                      timeout. Cancels the SAME transition the sweeper would,
                      entitlement release included. */}
                  <button
                    type="button"
                    className="ck-cancel-pending"
                    style={{ flex: '1 1 200px', minWidth: 200 }}
                    disabled={cancelPendingBusy}
                    onClick={async () => {
                      if (cancelPendingBusy) return;
                      if (!window.confirm(`לבטל את ההזמנה ב${pendingHotelHe}? הפעולה אינה הפיכה.`)) return;
                      setCancelPendingBusy(true);
                      setCancelPendingErr(null);
                      try {
                        const r = await api(
                          `/api/front/booking/${encodeURIComponent(pendingOther.reservationId)}/cancel`,
                          { method: 'POST' });
                        if (!r.ok) {
                          const body = await r.json().catch(() => null);
                          // The payment had gone through after all — the server
                          // finished the booking instead of cancelling it. Show
                          // the teacher their confirmation, not an error.
                          if (body?.code === 'already_paid') {
                            const ref = body?.details?.atlantisPnr || pendingOther.reservationId;
                            window.location.hash = `#/booking-complete/${encodeURIComponent(ref)}`;
                            return;
                          }
                          setCancelPendingErr(body?.message || 'ביטול ההזמנה נכשל. נסה שוב או פנה אלינו.');
                          return;
                        }
                        // Clear the block and let the page fall through to the
                        // normal booking flow for the deal being viewed.
                        setPendingOther(null);
                      } catch {
                        setCancelPendingErr('ביטול ההזמנה נכשל. נסה שוב או פנה אלינו.');
                      } finally {
                        setCancelPendingBusy(false);
                      }
                    }}
                  >
                    {cancelPendingBusy ? 'מבטל…' : 'בטל את ההזמנה הקודמת'}
                  </button>
                  <button
                    type="button"
                    className="irgon-header__logout-btn"
                    style={{ flex: '0 0 auto' }}
                    onClick={onBackToHome}
                  >
                    חזרה לדף הבית
                  </button>
                </div>
                {cancelPendingErr && (
                  <div className="irgon-alert-error" style={{ marginTop: 14 }}>{cancelPendingErr}</div>
                )}
              </div>
            </div>
          );
        }

        // Free-booking confirmation screen — subsidy covered the whole price,
        // no Pelecard call. Backend has already confirmed the reservation.
        if (paymentInit && paymentInit.mode === 'Free') {
          return (
            <div className="ck-payment">
              <div className="ck-payment__head">
                <h2>ההזמנה אושרה 🎉</h2>
                <p>הסבסוד כיסה את עלות הנופש במלואה — לא נדרש תשלום בכרטיס אשראי.</p>
                <div className="ck-payment__amount">
                  סכום החיוב: <strong>{fmtMoney(0, deal.currency)}</strong>
                </div>
              </div>
              <div style={{ padding: 26, textAlign: 'center' }}>
                <div style={{ fontSize: 14, color: 'var(--color-muted)', marginBottom: 16 }}>
                  מס׳ הזמנה: <code>{paymentInit.publicNumber}</code>
                </div>
                <button type="button" className="ck-form__cta" style={{ maxWidth: 280 }} onClick={onBackToHome}>
                  חזרה לדף הבית
                </button>
              </div>
            </div>
          );
        }

        const fullName = `${guest.firstName} ${guest.lastName}`.trim();
        const mapEmbed = `https://maps.google.com/maps?q=${mapQuery}&hl=he&z=14&output=embed`;

        return (
          <>
            {/* Step indicator — synced with the funnel state:
                • no paymentInit          → step 1 (guest form on top)
                • paymentInit && iframe   → step 3 (Pelecard hosted iframe)
                Steps below the current one render as completed (✓). */}
            {(() => {
              const currentStep = paymentInit && paymentIframeUrl ? 3 : 1;
              return (
                <div className="ck-steps">
                  {[
                    { n: 1, label: 'פרטי האורחים' },
                    { n: 2, label: 'פרטי המזמין/ה ואישורים' },
                    { n: 3, label: 'תשלום' },
                  ].map((s, i, arr) => {
                    const cls = s.n === currentStep ? ' is-active'
                      : s.n < currentStep ? ' is-done'
                      : '';
                    return (
                      <React.Fragment key={s.n}>
                        <div className={`ck-step${cls}`}>
                          <span className="ck-step__num">{s.n < currentStep ? '✓' : s.n}</span>
                          <span className="ck-step__label">{s.label}</span>
                        </div>
                        {i < arr.length - 1 && <div className="ck-step__line" />}
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            })()}

            {/* Hotel media: gallery on right, map on left */}
            <div className="ck-hero">
              <div className="ck-hero__gallery">
                <HotelGallery images={images} alt={deal.hotel.name.he || 'מלון'} />
              </div>
              <div className="ck-hero__map">
                <iframe
                  title="מפה"
                  src={mapEmbed}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* About the hotel — directly under the media, open on arrival.
                It used to be a collapsed accordion buried at the bottom of the
                sidebar, where nobody read it. Long copy is clamped rather than
                hidden, with "קרא עוד" to expand; short copy shows in full and
                gets no button. */}
            {deal.hotelDescription && (() => {
              // Length of the copy with markup stripped — decides whether the
              // clamp (and therefore the button) is warranted at all.
              const plain = deal.hotelDescription.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
              const long = plain.length > 420;
              return (
                <section className="ck-about" aria-label="אודות המלון">
                  <h3 className="ck-about__title">אודות המלון</h3>
                  <div
                    className={`ck-about__body${long && !aboutOpen ? ' is-clamped' : ''}`}
                    dangerouslySetInnerHTML={{ __html: deal.hotelDescription }}
                  />
                  {long && (
                    <button
                      type="button"
                      className="ck-about__more"
                      onClick={() => setAboutOpen(v => !v)}
                      aria-expanded={aboutOpen}
                    >
                      {aboutOpen ? 'הצג פחות ‹' : 'קרא עוד ›'}
                    </button>
                  )}
                </section>
              );
            })()}

            {/* Subsidy status banner — ALWAYS rendered, four mutually
                exclusive variants so the teacher never reaches the booking
                page without an explicit statement about their subsidy.
                  • retiree-category deal       → "לדיל זה אין סבסוד" (gross-only)
                  • non-member                  → red "אינך רשום"
                  • eligible (slot free)        → nothing at all
                  • slot used for the year      → red "הזכאות נוצלה"
                Eligibility = IGM truth (FrontMeApiController:55-115). The
                subsidy IS off the prices shown here (server-side, see
                CategorySubsidy.Deduct); the slot is what the entitlement
                tracks, and a used slot blocks the booking outright. */}
            {/* Only the cases that STOP a booking are announced. A teacher who
                is entitled needs no banner telling them so — the subsidy is
                already off the price in front of them, and saying it out loud
                only invites the question of what happens if it is wrong. */}
            {retireeCategory ? (
              <div className="irgon-alert-success" style={{ marginBottom: 16 }}>
                דיל לפורשים — ללא סבסוד שנתי, המחיר המוצג הוא הסופי.
              </div>
            ) : !isMember ? (
              <div className="irgon-alert-error" style={{ marginBottom: 16 }}>
                לא נמצאת רשומה כחבר/ת ארגון המורים, ולכן לא ניתן להזמין נופש מסובסד.
                אם נפלה טעות — פנו למרכז ההזמנות ונשמח לבדוק.
              </div>
            ) : usedYears.includes(checkInYear) ? (
              <div className="irgon-alert-error" style={{ marginBottom: 16 }}>
                זכאות הסבסוד לשנת {checkInYear} כבר נוצלה, ולא ניתן לבצע הזמנה מסובסדת נוספת השנה.
                לבירור פנו למרכז ההזמנות.
              </div>
            ) : null}

            {/* The supplier's note for THIS date — the same text the category
                card shows. Its own tint, so it is not mistaken for the
                eligibility verdict directly above it. Text, never HTML. */}
            {deal.dealRemark && deal.dealRemark.trim() && (
              <div className="ck-deal-remark">
                <span className="ck-deal-remark__head">
                  <InfoIcon size={15} /> הערות למבצע
                </span>
                <span className="ck-deal-remark__text">{deal.dealRemark.trim()}</span>
              </div>
            )}

            {/* Two columns: guest form on the right, summary on the left. */}
            <div className="ck-layout ck-layout--form-right">
              {/* Sidebar: order summary (compact) — visually on the left */}
              <aside className="ck-aside">
                <div className="ck-summary">
                  <div className="ck-summary__head">
                    <span>פרטי ההזמנה</span>
                    <span className="ck-summary__head-icon" aria-hidden="true">▭</span>
                  </div>

                  <div className="ck-summary__row">
                    <div className="ck-summary__label">אורחים</div>
                    <div className="ck-summary__value">{fullName || 'אורח/ת ראשי/ת'}</div>
                  </div>

                  <div className="ck-summary__row">
                    <div className="ck-summary__label">פרטי המלון</div>
                    <div className="ck-summary__value ck-summary__value--accent">
                      {deal.hotel.name.he || 'מלון'}
                    </div>
                    {/* Once a composition is applied this is no longer the deal's
                        own room: the supplier quoted a different one, and showing
                        "זוגי" beside a couple + child's price contradicts it. */}
                    <div className="ck-summary__sub">
                      {occupancy
                        ? `${occupancy.label}${occupancy.roomName ? ` · ${occupancy.roomName}` : ''}`
                        : `${deal.roomTypeName ?? deal.roomType} · ${deal.roomClassName ?? deal.roomClass}`}
                    </div>
                  </div>

                  <div className="ck-summary__row">
                    <div className="ck-summary__label">בסיס אירוח</div>
                    <div className="ck-summary__value ck-summary__value--accent">
                      <span aria-hidden="true">☕</span> {boardBasisHe(deal.boardBasis)}
                    </div>
                  </div>

                  <div className="ck-summary__row">
                    <div className="ck-summary__label-inline">
                      <span aria-hidden="true">📅</span> צ׳ק-אין: <strong>{fmtDate(deal.checkIn)}</strong>
                    </div>
                    <div className="ck-summary__label-inline">
                      <span aria-hidden="true">📅</span> צ׳ק-אאוט: <strong>{fmtDate(deal.checkOut)}</strong>
                    </div>
                    <div className="ck-summary__nights">
                      <MoonIcon /> {nightsLabelHe(deal.nights)}
                    </div>
                    <div className="ck-summary__nights">
                      <DayIcon /> {dowRangeHe(deal.checkIn, deal.checkOut)}
                    </div>
                  </div>

                  <div className="ck-summary__prices">
                    {/* Two prices, nothing else. The panel used to open with the
                        subsidy arithmetic — original price, what the cooperative
                        covers, "מחיר לאחר סבסוד" — which made five money rows
                        above a sixth ("לתשלום") and buried the two figures the
                        teacher actually compares. The subsidy is still deducted;
                        it is just not itemised here. */}
                    {hasTmurah && (
                      <div className="ck-summary__price-row">
                        <span>מחיר רגיל</span>
                        <span>{fmtMoney(effectiveGross, deal.currency)}</span>
                      </div>
                    )}
                    {hasTmurah && (
                      <div className="ck-summary__price-row" style={{ color: 'var(--color-primary)' }}>
                        <span>מחיר תמורה <span className="ck-summary__price-note">(הנחה במעמד החיוב)</span></span>
                        <span>{fmtMoney(effectiveTmurah, deal.currency)}</span>
                      </div>
                    )}

                    {/* Final amount the teacher pays in credit card — the headline number. */}
                    <div className={`ck-summary__charge${effectiveNet <= 0 ? ' ck-summary__charge--free' : ''}`}>
                      <span className="ck-summary__charge-label">
                        לתשלום
                      </span>
                      <span className="ck-summary__charge-value">
                        {effectiveNet <= 0 ? 'ללא תשלום' : fmtMoney(effectiveNet, deal.currency)}
                      </span>
                    </div>

                    {/* Live price hint — stays here so the user sees price freshness */}
                    {livePriceLoading && (
                      <div className="price-box__live price-box__live--loading">
                        <span className="irgon-spinner irgon-spinner--xs" /> מאמת מחיר…
                      </div>
                    )}
                    {!livePriceLoading && livePrice && livePrice.source === 'live' && (() => {
                      const delta = livePrice.grossPrice - deal.grossPrice;
                      if (!livePrice.available) {
                        return <div className="price-box__live price-box__live--warn">לא זמין כרגע</div>;
                      }
                      if (Math.abs(delta) < 1) {
                        return <div className="price-box__live price-box__live--ok">✓ מחיר מאומת</div>;
                      }
                      const sign = delta > 0 ? '+' : '−';
                      return (
                        <div className={`price-box__live ${delta > 0 ? 'price-box__live--warn' : 'price-box__live--ok'}`}>
                          מחיר עודכן: {sign}{fmtMoney(Math.abs(delta), livePrice.currency)}
                        </div>
                      );
                    })()}
                  </div>

                  {deal.combo && (
                    <div className="ck-summary__combo">
                      <strong>בונוס:</strong> {deal.combo.showTitle.he} · +{fmtMoney(deal.combo.addOnPrice, deal.currency)}
                    </div>
                  )}
                </div>

                {/* Same threshold as the cards that lead here. It used to be
                    "under 5" while the listing said nothing at all, so a deal
                    with 8 rooms was silent in both places and one with 3 spoke
                    only after a click.

                    The number is liveAvailability.roomsLeft — the supplier's
                    allotment ledger, fetched on mount — and NOT deal.allotment,
                    which is a stored AvailRooms snapshot that does not track the
                    remaining stock. Nothing is shown until that answers, or when
                    it cannot. */}
                {(() => {
                  const label = roomsLeftLabel(liveAvailability?.roomsLeft, isSoldOut);
                  if (!label) return null;
                  const n = liveAvailability!.roomsLeft!;
                  return (
                    <div className="price-box__warning" style={{ marginTop: 12 }}>
                      {label.urgent ? `נותרו רק ${n} חדרים זמינים!` : `נותרו ${n} חדרים`}
                    </div>
                  );
                })()}

              </aside>

              {/* Main: either the guest form OR the Pelecard payment card —
                  they share the ck-form box style so swapping between them
                  keeps the page visually consistent. Mirrors the
                  ADomesticFlyingCarpet checkout: one card collapses, the next
                  opens in its place. */}
              {paymentInit && paymentIframeUrl ? (
                <section className="ck-form" aria-label="תשלום בכרטיס אשראי">
                  <div className="ck-form__header">
                    <h3>תשלום בכרטיס אשראי</h3>
                    <span className="ck-form__icon" aria-hidden="true">💳</span>
                  </div>
                  {/* The card form below is Pelecard's hosted page in an
                      iframe — the number never reaches this app or its
                      database. Say so where the teacher is about to type it. */}
                  <div className="ck-pci">
                    <span className="ck-pci__ico" aria-hidden="true">🔒</span>
                    <span>
                      התשלום מתבצע בעמוד מאובטח של חברת הסליקה <strong>פלאכארד</strong>, בהתאם לתקן
                      אבטחת המידע <strong>PCI DSS</strong>. פרטי כרטיס האשראי מוזנים ישירות מול חברת
                      הסליקה ואינם נשמרים במערכות ארגון המורים.
                    </span>
                  </div>
                  <div className="ck-form__body">
                    <div className="ck-form__guest-title">
                      {paymentInit.mode === 'RQ'
                        ? 'החדר ממתין לאישור המלון — שמירת פרטי האשראי תבטיח שריון מיד עם אישור הספק.'
                        : 'החדר משוריין. השלם את התשלום כדי לאשר את ההזמנה.'}
                    </div>
                    <div className="ck-identity">
                      <div className="ck-identity__chip">
                        <span className="ck-identity__label">לתשלום</span>
                        <span className="ck-identity__value">
                          {fmtMoney(paymentInit.amount ?? effectiveNet, paymentInit.currency ?? deal.currency)}
                        </span>
                      </div>
                      {(paymentInit.atlantisPnr || paymentInit.publicNumber != null) && (
                        <div className="ck-identity__chip">
                          <span className="ck-identity__label">מס׳ הזמנה</span>
                          <span className="ck-identity__value" dir="ltr">
                            {paymentInit.atlantisPnr || paymentInit.publicNumber}
                          </span>
                        </div>
                      )}
                    </div>
                    <iframe
                      src={paymentIframeUrl}
                      title="Pelecard"
                      className="ck-payment__iframe"
                      allow="payment"
                      style={{ width: '100%', minHeight: 540, border: 0, borderRadius: 8, marginTop: 8 }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 12 }}>
                      <span className="price-box__note">תשלום מאובטח דרך Pelecard</span>
                    </div>
                  </div>
                </section>
              ) : (
              <form
                className="ck-form"
                onSubmit={e => { e.preventDefault(); void startBooking(); }}
              >
                {/* Above the guest details, where the member is still deciding
                    who is coming — after this point the form is about names. */}
                <OccupancyButton
                  currentLabel={occupancy?.label ?? 'זוג'}
                  onClick={() => setOccupancyModalOpen(true)}
                />
                {/* The chosen composition has to be visible on the page after the
                    popup closes — otherwise "המשך" reads as a button that did
                    nothing. Shown next to the price it replaces, not instead of
                    it, so the member can see what changed and what it now costs. */}
                {occupancy && (
                  <div className="occ-applied">
                    <span className="occ-applied__label">{occupancy.label}</span>
                    <span className="occ-applied__prices">
                      מחיר רגיל <b>{Math.round(occupancy.grossPrice).toLocaleString('he-IL')} ₪</b>
                      {' · '}
                      מחיר תמורה <b>{Math.round(occupancy.netPrice).toLocaleString('he-IL')} ₪</b>
                    </span>
                  </div>
                )}
                {occupancyModalOpen && (
                  <OccupancyModal
                    dealVariantId={deal.id}
                    selectedCode={occupancy?.code ?? '2,0,0'}
                    api={api}
                    onConfirm={choice => {
                      // "המשך" has to change something the member can see. Until
                      // this, it only relabelled the button and the price stayed
                      // where it was, which reads as a button that does nothing.
                      setOccupancy(choice);
                      setOccupancyModalOpen(false);
                    }}
                    onClose={() => setOccupancyModalOpen(false)}
                  />
                )}

                <div className="ck-form__header">
                  <h3>פרטי האורחים</h3>
                  <span className="ck-form__icon" aria-hidden="true">👤</span>
                </div>
                <div className="ck-form__body">
                  <div className="ck-form__guest-title">חדר 1 — אורח/ת ראשי/ת</div>

                  {/* Identity chips. What the SMS login already established is
                      shown rather than asked for again — but a value the
                      profile does not hold cannot be "shown", and locking an
                      empty field meant a booking that could not be completed at
                      all. The teachers' organisation gives us no name and no
                      phone, so a teacher signing in for the first time, and
                      almost every teacher an agent opens the site for, arrives
                      here with nothing filled in.
                      The id is the exception and stays locked in every case: it
                      is what the SMS code proved, and it decides whose annual
                      entitlement this booking spends. */}
                  <div className="ck-identity">
                    <div className="ck-identity__chip">
                      <span className="ck-identity__label">שם מלא</span>
                      {nameEditable ? (
                        <input
                          className="ck-identity__input"
                          value={fullNameInput}
                          onChange={e => {
                            setFullNameInput(e.target.value);
                            setGuest(g => ({ ...g, ...splitFullName(e.target.value) }));
                          }}
                          aria-label="שם מלא"
                          placeholder="שם פרטי ומשפחה"
                        />
                      ) : (
                        <span className="ck-identity__value">
                          {`${guest.firstName} ${guest.lastName}`.trim() || '—'}
                        </span>
                      )}
                    </div>
                    <div className="ck-identity__chip">
                      <span className="ck-identity__label">תעודת זהות</span>
                      <span className="ck-identity__value" dir="ltr">{guest.tz || '—'}</span>
                    </div>
                    <div className="ck-identity__chip">
                      <span className="ck-identity__label">נייד</span>
                      {/* Pinned to the SMS-verified profile mobile — unless the
                          profile has none, or the server flagged a test account
                          whose tester needs the booking to reach the handset in
                          their hand. */}
                      {phoneEditable ? (
                        <input
                          className="ck-identity__input"
                          type="tel"
                          inputMode="tel"
                          dir="ltr"
                          value={guest.phone}
                          onChange={e => setGuest(g => ({ ...g, phone: e.target.value }))}
                          aria-label="מספר נייד ליצירת קשר"
                          placeholder="מספר נייד"
                        />
                      ) : (
                        <span className="ck-identity__value" dir="ltr">{guest.phone || '—'}</span>
                      )}
                    </div>
                  </div>

                  <div className="ck-form__row">
                    <div className="ck-form__field">
                      <input
                        id="guest-email"
                        type="email"
                        value={guest.email}
                        onChange={e => setGuest(g => ({ ...g, email: e.target.value }))}
                        autoComplete="email"
                        dir="ltr"
                        placeholder="אימייל לקבלת אישור הזמנה"
                      />
                    </div>
                    <div className="ck-form__field">
                      <select
                        id="guest-installments"
                        value={installments}
                        onChange={e => setInstallments(Number(e.target.value))}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                          <option key={n} value={n}>{n === 1 ? 'תשלום אחד' : `${n} תשלומים`}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Pre-booking policy notice — content managed from admin
                      (Site Settings → "הודעה לפני אישור הזמנה"). Falls back to
                      the legacy default when the admin hasn't customised it. */}
                  <div
                    className="ck-form__policy"
                    style={{
                      background: '#f5f8fc',
                      border: '1px solid #d6e1ef',
                      borderRadius: 8,
                      padding: '12px 14px',
                      margin: '0 0 12px',
                      lineHeight: 1.7,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: bookingPolicyHtml ?? buildDefaultBookingPolicyHtml(bookingTimeoutMinutes),
                    }}
                  />

                  {/* Ticking the checkbox does NOT set it — it opens the takanon
                      SMS-confirmation popup; only a verified code turns it on.
                      Unchecking is direct (the teacher will have to redo the
                      SMS confirmation to re-enable payment). */}
                  <label className="ck-form__terms">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={e => {
                        if (e.target.checked) {
                          e.preventDefault();
                          setTermsModalOpen(true);
                        } else {
                          setTermsAccepted(false);
                        }
                      }}
                    />
                    <span>
                      אני מאשר/ת שקראתי את{' '}
                      <a href="#" onClick={e => { e.preventDefault(); setTermsModalOpen(true); }}>
                        התנאים וההגבלות
                      </a>{' '}
                      המאפשרות לי נופש מסובסד פעם אחת בשנה קלנדרית בלבד.
                    </span>
                  </label>

                  {termsModalOpen && (
                    <TermsConfirmModal
                      takanonHtml={takanonHtml}
                      onConfirmed={() => { setTermsAccepted(true); setTermsModalOpen(false); setBookingErr(null); }}
                      onClose={() => setTermsModalOpen(false)}
                    />
                  )}

                  {bookingErr && <div className="irgon-alert-error">{bookingErr}</div>}

                  {isSoldOut && (
                    <div
                      className="irgon-alert-error"
                      role="status"
                      style={{ background: '#fbe9eb', borderColor: '#e0b5bb', color: '#8a1d2a', fontWeight: 600 }}
                    >
                      אזלו המקומות בתאריך זה — לא ניתן להזמין כעת.
                    </div>
                  )}



                  <button
                    type="submit"
                    className="ck-form__cta"
                    disabled={!formValid || bookingBusy || !eligible || isSoldOut}
                    title={
                      isSoldOut ? 'אזלו המקומות בתאריך זה'
                      : !isMember ? 'לא נמצאת רשומה כחבר/ת ארגון המורים'
                      : !eligible ? 'זכאות הסבסוד לשנה זו כבר נוצלה'
                      : undefined
                    }
                  >
                    {isSoldOut
                      ? 'אזלו המקומות'
                      : !eligible
                        ? 'זכאות הסבסוד נוצלה'
                        : bookingBusy
                            ? (effectiveNet <= 0 ? 'יוצר הזמנה…' : 'מכין תשלום…')
                            : (effectiveNet <= 0 ? 'אשר הזמנה' : 'המשך לתשלום')}
                  </button>
                </div>
              </form>
              )}
            </div>

            {/* Sticky bottom bar — hotel + nights + guests + total. */}
            <div className="ck-bottom-bar">
              <div className="container ck-bottom-bar__inner">
                <div className="ck-bottom-bar__total">
                  <span className="ck-bottom-bar__total-label">סה״כ</span>
                  <strong>{fmtMoney(effectiveNet, deal.currency)}</strong>
                </div>
                <div className="ck-bottom-bar__meta">
                  <span className="ck-bottom-bar__hotel">{deal.hotel.name.he || 'מלון'}</span>
                  <span className="ck-bottom-bar__sep">·</span>
                  <span>{deal.nights} לילות</span>
                  <span className="ck-bottom-bar__sep">·</span>
                  <span>{occupancy ? occupancy.label : (deal.roomTypeName ?? deal.roomType)}</span>
                </div>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
};

// ============================================================================
// My Bookings page
// ============================================================================

const MyBookingsPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [items, setItems] = useState<MyBooking[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const r = await api('/api/front/my-bookings');
      if (r.ok) {
        const data: MyBookingsResponse = await r.json();
        setItems(data.items || []);
      } else {
        setErr('טעינת ההזמנות נכשלה. נסה שוב.');
      }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>
      <a href="#" className="irgon-back" onClick={e => { e.preventDefault(); onBack(); }}>
        ← חזרה
      </a>
      <h2 style={{ fontWeight: 800, color: 'var(--color-primary-ink)', margin: '0 0 24px', fontSize: 28 }}>
        ההזמנות שלי
      </h2>

      {loading && <div className="irgon-center"><div className="irgon-spinner" /></div>}
      {err && <div className="irgon-alert-error">{err}</div>}

      {!loading && !err && items && items.length === 0 && (
        <div className="irgon-empty">
          <h5>טרם ביצעת הזמנות</h5>
          <p>עיין בקטגוריות בדף הבית והתחל להזמין.</p>
        </div>
      )}

      {!loading && !err && items && items.length > 0 && (
        <div className="bookings-card">
          <div className="bookings-card__header">
            <span className="bookings-card__title">סה״כ {items.length} הזמנות</span>
          </div>
          <div className="bookings-card__scroll">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>מס׳ הזמנה</th>
                  <th>מלון</th>
                  <th>צ׳ק-אין</th>
                  <th>צ׳ק-אאוט</th>
                  <th>סטטוס</th>
                  <th style={{ textAlign: 'end' }}>עלות</th>
                  <th>נוצר ב</th>
                </tr>
              </thead>
              <tbody>
                {items.map(b => {
                  const s = stateHe(b.state);
                  return (
                    <tr key={b.reservationId}>
                      <td dir="ltr">{b.atlantisPnr ? <code>{b.atlantisPnr}</code> : <span className="muted">—</span>}</td>
                      <td>{b.hotelName?.he || <span className="muted">—</span>}</td>
                      <td>{fmtDate(b.checkIn)}</td>
                      <td>{fmtDate(b.checkOut)}</td>
                      <td><span className={`status-badge ${s.cls}`}>{s.label}</span></td>
                      <td style={{ textAlign: 'end', fontWeight: 700 }}>{fmtMoney(b.netPrice, b.currency)}</td>
                      <td className="muted">{fmtDateTime(b.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Booking complete — post-payment thank-you / RQ-pending page
// ============================================================================
// Rendered after Pelecard redirects the browser to /api/front/booking/pelecard-return,
// which 302s to #/booking-complete/{reservationId}. The page pulls the latest
// reservation state from /api/front/booking/{id} and branches on:
//   - State="Confirmed" + AtlantisOrderStatus="OK"  → ההזמנה אושרה
//   - State in {AtlantisHeld, AwaitingPayment, RqSaved} + AtlantisOrderStatus="RQ"
//                                                       → ממתין לאישור מלון, הכרטיס לא חוייב
//   - State in {Failed, Cancelled}                   → התשלום נכשל
//   - everything else                                → ההזמנה בעיבוד
//
// Pelecard's ?status=error query param (set by ErrorURL) forces the failure
// branch even if the reservation document is still in Initiated.

type BookingCompletePassenger = {
  firstName: string;
  lastName: string;
  idNumber: string;
  isLead: boolean;
};

type BookingCompleteState = {
  reservationId: string;
  publicNumber: number;
  state: string;
  mode?: string | null;
  atlantisPnr?: string | null;
  atlantisOrderStatus?: string | null;
  hotelName?: LocalizedText | null;
  hotelAddress?: string | null;
  hotelStarRating?: number | null;
  hotelThumbnailUrl?: string | null;
  roomClass?: string | null;
  roomType?: string | null;
  boardBasis?: string | null;
  checkIn: string;
  checkOut: string;
  grossPrice: number;
  subsidyApplied: number;
  netPrice: number;
  currency: string;
  passengerCount: number;
  passengers?: BookingCompletePassenger[] | null;
  receiptAvailable: boolean;
  createdAt: string;
  updatedAt: string;
};

// Booking-status badge tone used in the summary card. Mirrors the colours
// from stateHe(...) but with a brighter background so it pops against the
// white card.
const summaryBadge = (state: BookingCompleteState, forcedError: boolean): { label: string; bg: string; color: string } => {
  const stt = (state.atlantisOrderStatus || '').toUpperCase();
  if (forcedError || state.state === 'Failed' || state.state === 'Cancelled') {
    return { label: 'התשלום נכשל', bg: '#fde2e2', color: '#a72d2d' };
  }
  if (stt === 'RQ') {
    return { label: 'ממתין לאישור', bg: '#fff1c6', color: '#8a6d00' };
  }
  if (state.state === 'Confirmed' && stt === 'OK') {
    return { label: 'הזמנה מאושרת', bg: '#dcf5e3', color: '#1c6b3b' };
  }
  if (state.state === 'AtlantisHeld' || state.state === 'AwaitingPayment') {
    return { label: 'בעיבוד', bg: '#e3eefd', color: '#0d4a87' };
  }
  return { label: stateHe(state.state).label, bg: '#eef0f4', color: 'var(--color-text)' };
};

const StarRow: React.FC<{ count: number }> = ({ count }) => {
  const safe = Math.max(0, Math.min(5, count));
  return (
    <span style={{ color: '#f5b301', letterSpacing: 1 }} aria-label={`${safe} כוכבים`}>
      {'★'.repeat(safe)}{'☆'.repeat(5 - safe)}
    </span>
  );
};

const BookingCompletePage: React.FC<{
  reservationId: string;
  params?: Record<string, string>;
  onBackToHome: () => void;
  onGoToBookings: () => void;
}> = ({ reservationId, params, onBackToHome, onGoToBookings }) => {
  const [state, setState] = useState<BookingCompleteState | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const forcedError = params?.status === 'error';

  // Poll the booking state for up to ~10s after landing: the Pelecard
  // server-side callback usually arrives within a couple of seconds, so by
  // refreshing a few times we catch the Confirmed transition without making
  // the teacher reload manually.
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api(`/api/front/booking/${encodeURIComponent(reservationId)}`);
        if (cancelled) return;
        if (r.ok) {
          const data: BookingCompleteState = await r.json();
          setState(data);
          setErr(null);
          const terminal =
            data.state === 'Confirmed' ||
            data.state === 'Failed' ||
            data.state === 'Cancelled' ||
            (data.atlantisOrderStatus || '').toUpperCase() === 'RQ';
          if (terminal) {
            setLoading(false);
            return;
          }
        } else if (r.status === 404) {
          setErr('ההזמנה לא נמצאה.');
          setLoading(false);
          return;
        }
      } catch { /* keep polling until attempts is exhausted */ }
      if (attempts >= 5) {
        setLoading(false);
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [reservationId]);

  if (loading && !state) {
    return (
      <div className="container" style={{ paddingTop: 40, paddingBottom: 60 }}>
        <div className="irgon-center"><div className="irgon-spinner" /></div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>
        <div className="irgon-alert-error">{err}</div>
        <div style={{ marginTop: 16 }}>
          <button type="button" className="irgon-header__logout-btn" onClick={onBackToHome}>חזרה לדף הבית</button>
        </div>
      </div>
    );
  }

  if (!state) return null;

  const badge = summaryBadge(state, forcedError);
  const stt = (state.atlantisOrderStatus || '').toUpperCase();
  const isOnRequest = stt === 'RQ';
  const passengers = state.passengers ?? [];
  const lead = passengers.find(p => p.isLead) ?? passengers[0];
  const otherPax = passengers.filter(p => p !== lead);

  return (
    <div style={{ background: '#f4f6fa', minHeight: '100vh', padding: '24px 0 60px' }}>
      <div className="container" style={{ maxWidth: 880 }}>
        {/* Header — title + suitcase illustration on the right (in RTL). */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '8px 12px 24px',
        }}>
          <h1 style={{ fontWeight: 800, fontSize: 26, color: 'var(--color-primary-ink)', margin: 0 }}>
            סטטוס הזמנה: #{state.atlantisPnr || state.publicNumber}
          </h1>
          <img
            src="/images/irgon/book-two-bags.svg"
            alt=""
            aria-hidden="true"
            style={{ height: 96, width: 'auto', flexShrink: 0 }}
          />
        </div>

        {isOnRequest && (
          <div style={{
            background: '#fff8e1',
            border: '1px solid #f5d27a',
            color: '#6b4a00',
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 14,
            fontWeight: 600,
          }}>
            ⏳ המלון לא אישר את ההזמנה אוטומטית — ההזמנה ממתינה לאישור ידני. <b>הכרטיס לא חויב.</b> נעדכן אותך במייל ברגע שתאושר.
          </div>
        )}

        {/* פרטי הזמנה */}
        <SummaryCard title="פרטי הזמנה">
          <SummaryRow label="מספר הזמנה" value={`#${state.atlantisPnr || state.publicNumber}`} dir="ltr" />
          <SummaryRow label="סטטוס הזמנה" value={
            <span style={{
              background: badge.bg,
              color: badge.color,
              padding: '4px 12px',
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 13,
              display: 'inline-block',
            }}>{badge.label}</span>
          } />
          <SummaryRow label="תאריך הזמנה" value={fmtDate(state.createdAt.slice(0, 10))} />
          {!isOnRequest && (
            <SummaryRow label="סכום ששולם" value={fmtMoney(state.netPrice, state.currency)} bold />
          )}
          {isOnRequest && (
            <SummaryRow label="סכום צפוי לחיוב (לאחר אישור)" value={fmtMoney(state.netPrice, state.currency)} bold />
          )}
        </SummaryCard>

        {/* נוסעים */}
        {passengers.length > 0 && (
          <SummaryCard title="נוסעים">
            {lead && (
              <div style={{ paddingBottom: otherPax.length ? 12 : 0, borderBottom: otherPax.length ? '1px solid #eef0f4' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15, color: 'var(--color-text)' }}>
                  <span style={{ color: 'var(--color-primary)' }}>👤</span>
                  <span>{`${lead.firstName} ${lead.lastName}`.trim()}</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-muted)' }} dir="ltr">{lead.idNumber}</span>
                </div>
              </div>
            )}
            {otherPax.map((p, idx) => (
              <div key={`${p.idNumber}-${idx}`} style={{ paddingTop: 12, paddingBottom: idx < otherPax.length - 1 ? 12 : 0, borderBottom: idx < otherPax.length - 1 ? '1px solid #eef0f4' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14, color: 'var(--color-text)' }}>
                  <span style={{ color: 'var(--color-primary)' }}>👤</span>
                  <span>{`${p.firstName} ${p.lastName}`.trim()}</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-muted)' }} dir="ltr">{p.idNumber}</span>
                </div>
              </div>
            ))}
          </SummaryCard>
        )}

        {/* פרטי מלון */}
        <SummaryCard title="פרטי מלון">
          <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                {state.hotelStarRating && <StarRow count={state.hotelStarRating} />}
                <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--color-primary-ink)' }}>
                  {state.hotelName?.he || '—'}
                </span>
              </div>
              {state.hotelAddress && (
                <div style={{ fontSize: 14, color: 'var(--color-muted)', marginBottom: 10 }}>{state.hotelAddress}</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, color: 'var(--color-text)' }}>
                <span>📅 {fmtDate(state.checkIn)} — {fmtDate(state.checkOut)}</span>
                {(state.roomType || state.roomClass) && (
                  <span>🛏 {[state.roomType, state.roomClass].filter(Boolean).join(' · ')}</span>
                )}
                {state.boardBasis && <span>🍽 {boardBasisHe(state.boardBasis)}</span>}
                <span>👥 {state.passengerCount} {state.passengerCount === 1 ? 'אורח' : 'אורחים'}</span>
              </div>
            </div>
            {state.hotelThumbnailUrl && (
              <img
                src={state.hotelThumbnailUrl}
                alt={state.hotelName?.he || ''}
                style={{ width: 180, height: 130, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            )}
          </div>
        </SummaryCard>

        <div className="bc-actions">
          <button type="button" className="bc-btn bc-btn--primary" onClick={onGoToBookings}>
            <svg className="bc-btn__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span>צפייה בכל ההזמנות שלי</span>
          </button>
          <button type="button" className="bc-btn bc-btn--ghost" onClick={onBackToHome}>
            <svg className="bc-btn__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3 11l9-7 9 7v9a2 2 0 0 1-2 2h-4v-7H10v7H6a2 2 0 0 1-2-2v-9z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
            <span>חזרה לדף הבית</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{
    background: '#fff',
    borderRadius: 12,
    padding: '20px 24px',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
    marginBottom: 16,
  }}>
    <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--color-primary-ink)', margin: '0 0 16px' }}>{title}</h3>
    {children}
  </div>
);

const SummaryRow: React.FC<{ label: string; value: React.ReactNode; bold?: boolean; dir?: 'ltr' | 'rtl' }> = ({ label, value, bold, dir }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #f1f3f7',
    gap: 16,
  }}>
    <span style={{ fontSize: 13, color: 'var(--color-muted)', fontWeight: 600 }}>{label}</span>
    <span dir={dir} style={{ fontSize: 14, fontWeight: bold ? 800 : 600, color: 'var(--color-text)' }}>{value}</span>
  </div>
);

// ============================================================================
// App router — hash-based URLs, one URL per step
// ============================================================================
// Routes:
//   #/                              home
//   #/login                         login
//   #/my-bookings                   my bookings
//   #/shabbat                       shabbat-info placeholder
//   #/categories/<code>             category detail
//   #/deals/<dealVariantId>         deal detail
//   #/booking-complete/<id>         post-payment thank-you / RQ-pending page

function viewToHash(view: View): string {
  switch (view.kind) {
    case 'home': return '#/';
    case 'login': return '#/login';
    case 'my-bookings': return '#/my-bookings';
    case 'shabbat': return '#/shabbat';
    case 'accessibility': return '#/accessibility';
    case 'category-detail': return `#/categories/${encodeURIComponent(view.category.code)}`;
    case 'deal-detail': {
      // Carry context in the hash so the URL is shareable (legacy Hotelh/book parity).
      // The dealVariantId is still the source of truth — these params are display-only.
      const q = view.params ? '?' + new URLSearchParams(view.params).toString() : '';
      return `#/deals/${encodeURIComponent(view.dealVariantId)}${q}`;
    }
    case 'booking-complete': {
      const q = view.params ? '?' + new URLSearchParams(view.params).toString() : '';
      return `#/booking-complete/${encodeURIComponent(view.reservationId)}${q}`;
    }
  }
}

type ParsedRoute =
  | { kind: 'home' | 'login' | 'my-bookings' | 'shabbat' | 'accessibility' }
  | { kind: 'category-by-code'; code: string }
  | { kind: 'deal-by-id'; dealVariantId: string; params?: Record<string, string> }
  | { kind: 'booking-complete'; reservationId: string; params?: Record<string, string> };

function parseRoute(hash: string): ParsedRoute {
  const h = hash.replace(/^#\/?/, '');
  if (!h) return { kind: 'home' };
  const slash = h.indexOf('/');
  const head = slash >= 0 ? h.slice(0, slash) : h;
  const tailRaw = slash >= 0 ? h.slice(slash + 1) : '';
  // Split off a query string if present.
  const qIdx = tailRaw.indexOf('?');
  const tail = qIdx >= 0 ? tailRaw.slice(0, qIdx) : tailRaw;
  const queryStr = qIdx >= 0 ? tailRaw.slice(qIdx + 1) : '';
  switch (head) {
    case 'login': return { kind: 'login' };
    case 'my-bookings': return { kind: 'my-bookings' };
    case 'shabbat': return { kind: 'shabbat' };
    case 'accessibility': return { kind: 'accessibility' };
    case 'categories': return tail ? { kind: 'category-by-code', code: decodeURIComponent(tail) } : { kind: 'home' };
    case 'deals': {
      if (!tail) return { kind: 'home' };
      const params: Record<string, string> = {};
      if (queryStr) {
        new URLSearchParams(queryStr).forEach((v, k) => { params[k] = v; });
      }
      return { kind: 'deal-by-id', dealVariantId: decodeURIComponent(tail), params: Object.keys(params).length ? params : undefined };
    }
    case 'booking-complete': {
      if (!tail) return { kind: 'home' };
      const params: Record<string, string> = {};
      if (queryStr) {
        new URLSearchParams(queryStr).forEach((v, k) => { params[k] = v; });
      }
      return { kind: 'booking-complete', reservationId: decodeURIComponent(tail), params: Object.keys(params).length ? params : undefined };
    }
    default: return { kind: 'home' };
  }
}

const App: React.FC = () => {
  const [me, setMe] = useState<Me | null>(null);
  const [view, setViewState] = useState<View>({ kind: 'home' });
  const [checking, setChecking] = useState(true);
  const [showTerms, setShowTerms] = useState<boolean>(() => {
    try { return window.localStorage.getItem(TERMS_ACCEPTED_KEY) !== '1'; }
    catch { return true; }
  });
  const [showHelp, setShowHelp] = useState(false);

  // ── Presence heartbeat ──
  // Tells the admin's live screen which page this visitor is on. The id is
  // random and lives in sessionStorage: it identifies a TAB for as long as it
  // is open and is not an identity — who the visitor is, when they are signed
  // in, is read from the session cookie server-side.
  useEffect(() => {
    let visitorId: string;
    try {
      visitorId = window.sessionStorage.getItem('irgon:visitor') || '';
      if (!visitorId) {
        visitorId = Math.random().toString(36).slice(2) + Date.now().toString(36);
        window.sessionStorage.setItem('irgon:visitor', visitorId);
      }
    } catch {
      // Private mode or storage blocked — a per-load id still gives a count.
      visitorId = Math.random().toString(36).slice(2);
    }

    const describe = (): { page: string; detail?: string } => {
      // Report what is on the screen, not where the URL points. Someone
      // following a link to a category without a session sees the login wall —
      // the live screen said "guest, on a category page", which is a place
      // they never reached.
      //
      // Where they were TRYING to go is kept, because it is the useful half:
      // it says a link to that category is out there bringing people in.
      if (!me && view.kind !== 'accessibility' && view.kind !== 'login') {
        const wanted = view.kind === 'category-detail'
          ? view.category?.name?.he
          : view.kind === 'deal-detail' ? 'דיל' : undefined;
        return { page: 'login', detail: wanted ? `ניסה להגיע ל־${wanted}` : undefined };
      }
      switch (view.kind) {
        case 'home': return { page: 'home' };
        case 'login': return { page: 'login' };
        case 'my-bookings': return { page: 'my-bookings' };
        case 'shabbat': return { page: 'shabbat' };
        case 'accessibility': return { page: 'accessibility' };
        case 'category-detail': return { page: 'category', detail: view.category?.name?.he };
        case 'deal-detail': return { page: 'deal', detail: view.params?.hotelId ? undefined : undefined };
        case 'booking-complete': return { page: 'booking-complete' };
        default: return { page: 'unknown' };
      }
    };

    const beat = () => {
      // A hidden tab is not someone on the site; skipping it keeps the count
      // honest and costs nothing.
      if (document.visibilityState === 'hidden') return;
      const { page, detail } = describe();
      void api('/api/front/presence', {
        method: 'POST',
        body: JSON.stringify({ visitorId, page, detail }),
      }).catch(() => { /* presence is never worth an error on screen */ });
    };

    beat();
    const timer = window.setInterval(beat, 30_000);
    return () => window.clearInterval(timer);
  }, [view, me]);
  // Trial-mode popup config (from site-settings) + in-memory dismiss. Kept in
  // memory (not localStorage) so it re-appears on every fresh load until admin
  // disables it via SiteSettings.TrialPopupEnabled.
  const [trial, setTrial] = useState<{ enabled: boolean; code: string } | null>(null);
  // Admin-editable takanon body for the entry terms popup (null ⇒ built-in default).
  const [appTakanonHtml, setAppTakanonHtml] = useState<string | null>(null);
  // The code the teacher already acknowledged in this browser (localStorage).
  // The popup is suppressed when it equals the current configured code.
  const [trialAck, setTrialAck] = useState<string | null>(() => {
    try { return window.localStorage.getItem(TRIAL_POPUP_ACK_KEY); }
    catch { return null; }
  });
  const ackTrial = useCallback((code: string) => {
    try { window.localStorage.setItem(TRIAL_POPUP_ACK_KEY, code); } catch { /* ignore */ }
    setTrialAck(code);
  }, []);

  const acceptTerms = useCallback(() => {
    try { window.localStorage.setItem(TERMS_ACCEPTED_KEY, '1'); } catch { /* ignore */ }
    setShowTerms(false);
  }, []);

  // Resolve URL hash → View. category-by-code needs an extra fetch.
  const resolveRoute = useCallback(async (hash: string): Promise<View> => {
    const r = parseRoute(hash);
    if (r.kind === 'category-by-code') {
      try {
        const resp = await api('/api/front/categories');
        if (resp.ok) {
          const data: { items: Category[] } = await resp.json();
          const cat = data.items.find(c => c.code === r.code);
          if (cat) return { kind: 'category-detail', category: cat };
        }
      } catch { /* fall through to home */ }
      return { kind: 'home' };
    }
    if (r.kind === 'deal-by-id') return { kind: 'deal-detail', dealVariantId: r.dealVariantId, params: r.params };
    if (r.kind === 'booking-complete') return { kind: 'booking-complete', reservationId: r.reservationId, params: r.params };
    return { kind: r.kind } as View;
  }, []);

  const goTo = useCallback((next: View) => {
    setViewState(next);
    window.history.pushState({ view: next }, '', viewToHash(next));
    // Forward navigation always lands at the top of the new page (browsers
    // don't reset scroll for hash-only changes). Use smooth scroll so the
    // transition feels intentional rather than a snap.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goHome = useCallback(() => {
    setViewState({ kind: 'home' });
    window.history.replaceState({ view: { kind: 'home' } }, '', '#/');
  }, []);

  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  // Listen for back/forward + manual hash edits.
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const v = (e.state as { view?: View } | null)?.view;
      if (v) setViewState(v);
      else resolveRoute(window.location.hash).then(setViewState);
    };
    const onHash = () => {
      resolveRoute(window.location.hash).then(v => {
        setViewState(v);
        window.history.replaceState({ view: v }, '', viewToHash(v));
      });
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onHash);
    };
  }, [resolveRoute]);

  // Initial: auth check + resolve current URL.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setChecking(true);
      const r = await api('/api/front/me');
      if (cancelled) return;
      const isAuthed = r.ok;
      if (isAuthed) setMe(await r.json());
      else setMe(null);

      const initial = await resolveRoute(window.location.hash);
      if (cancelled) return;
      // Anon users always start on login; logged-in users can't sit on the login
      // page. Exception: the accessibility statement is public by design.
      const final: View = !isAuthed
        ? (initial.kind === 'accessibility' ? initial : { kind: 'login' })
        : initial.kind === 'login' ? { kind: 'home' } : initial;
      setViewState(final);
      window.history.replaceState({ view: final }, '', viewToHash(final));
      setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [resolveRoute]);

  // Trial-mode popup config — fetched once on boot. If the settings call fails we
  // simply don't show the popup (never block the whole app on a settings hiccup).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/api/front/site-settings');
        if (!r.ok || cancelled) return;
        const s = await r.json();
        setTrial({ enabled: s.trialPopupEnabled !== false, code: String(s.trialPopupCode ?? '0545') });
        setAppTakanonHtml(s.takanonHtml || null);
      } catch { /* no popup if unreachable */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Session-expiry watcher — the api() wrapper broadcasts this when any call
  // hits a cookie-auth 401 mid-use (TeacherSession aged out / server restart).
  // We drop the local session, bounce to the login page, and show a banner so
  // the teacher knows WHY the SMS confirm / update they attempted failed.
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    const onExpired = () => {
      setSessionExpired(true);
      setMe(null);
      setViewState({ kind: 'login' });
      window.history.replaceState({ view: { kind: 'login' } }, '', viewToHash({ kind: 'login' }));
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // Called after LoginPage completes — refresh /me and go home (clear login from history).
  const checkAuth = async () => {
    const r = await api('/api/front/me');
    if (r.ok) {
      setMe(await r.json());
      setSessionExpired(false);
      goHome();
    } else {
      setMe(null);
    }
  };

  const logout = async () => {
    await api('/api/front/auth/logout', { method: 'POST' });
    setMe(null);
    goHome();
  };

  const navTo = (kind: View['kind']) => {
    if (kind === 'home') goTo({ kind: 'home' });
    else if (kind === 'my-bookings') goTo({ kind: 'my-bookings' });
    else if (kind === 'shabbat') goTo({ kind: 'shabbat' });
    else if (kind === 'login') goTo({ kind: 'login' });
    // category-detail / deal-detail require payload; only triggered explicitly below
  };

  if (checking) {
    return (
      <div className="irgon-center" style={{ minHeight: '100vh' }}>
        <div className="irgon-spinner" />
      </div>
    );
  }

  // Not logged in → show navbar + login page directly (login is the first page
  // per spec). The accessibility statement stays reachable without a session.
  if (!me) {
    if (view.kind === 'accessibility') {
      return (
        <>
          <TopNav me={null} current={view.kind} onLoginClick={() => goTo({ kind: 'login' })} onLogout={logout} onNav={navTo} />
          <AccessibilityStatementPage onBack={() => goTo({ kind: 'login' })} />
          <Footer />
          <AccessibilityWidget />
        </>
      );
    }
    return (
      <>
        <TopNav me={null} current={view.kind} onLoginClick={() => goTo({ kind: 'login' })} onLogout={logout} onNav={navTo} />
        {sessionExpired && (
          <div
            role="alert"
            style={{
              maxWidth: 560,
              margin: '18px auto -6px',
              padding: '12px 18px',
              borderRadius: 12,
              background: '#fff3cd',
              border: '1px solid #ffe08a',
              color: '#7a5a00',
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            פג תוקף ההתחברות — נותקת מהמערכת. יש להתחבר מחדש כדי להמשיך.
          </div>
        )}
        <LoginPage onLogin={checkAuth} />
        <Footer />
        <CookieConsentBanner />
        <AccessibilityWidget />
      </>
    );
  }

  const showHelpChrome = view.kind !== 'login';

  // Logged in
  return (
    <>
      {me.actingAgent && <ActingAgentBar me={me} />}
      <TopNav me={me} current={view.kind} onLoginClick={() => {}} onLogout={logout} onNav={navTo} />

      {view.kind === 'home' && (
        <HomePage
          me={me}
          onOpenCategory={cat => goTo({ kind: 'category-detail', category: cat })}
          onOpenSearchDeal={s => goTo({
            kind: 'deal-detail',
            dealVariantId: s.deal.id,
            // The same parameters the deals page carries, so the product page
            // behaves identically whichever way it was reached.
            params: {
              city: s.deal.hotel.destination,
              hotelId: s.deal.hotel.id,
              checkIn: s.deal.checkIn,
              checkOut: s.deal.checkOut,
              roomType: s.deal.roomType,
              roomClass: s.deal.roomClass,
              basis: s.deal.boardBasis,
              category: s.categoryCode,
              ...(s.deal.hotelCategoryCode ? { hotelCategory: s.deal.hotelCategoryCode } : {}),
            },
          })}
        />
      )}

      {view.kind === 'category-detail' && (
        <CategoryDetailPage
          category={view.category}
          onBack={goBack}
          onOpenDeal={deal => goTo({
            kind: 'deal-detail',
            dealVariantId: deal.id,
            backCategory: view.category,
            params: {
              city: deal.hotel.destination,
              hotelId: deal.hotel.id,
              checkIn: deal.checkIn,
              checkOut: deal.checkOut,
              roomType: deal.roomType,
              roomClass: deal.roomClass,
              basis: deal.boardBasis,
              category: view.category.code,
              ...(deal.hotelCategoryCode ? { hotelCategory: deal.hotelCategoryCode } : {}),
            },
          })}
        />
      )}

      {view.kind === 'deal-detail' && (
        <DealDetailPage
          dealVariantId={view.dealVariantId}
          me={me}
          onBackToCategory={goBack}
          onBackToHome={goHome}
        />
      )}

      {view.kind === 'my-bookings' && <MyBookingsPage onBack={goBack} />}

      {view.kind === 'booking-complete' && (
        <BookingCompletePage
          reservationId={view.reservationId}
          params={view.params}
          onBackToHome={goHome}
          onGoToBookings={() => goTo({ kind: 'my-bookings' })}
        />
      )}

      {view.kind === 'shabbat' && (
        <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>
          <a href="#" className="irgon-back" onClick={e => { e.preventDefault(); goBack(); }}>
            ← חזרה
          </a>
          <h2 style={{ fontWeight: 800, color: 'var(--color-primary-ink)' }}>שבת עיון</h2>
          <div className="irgon-empty" style={{ marginTop: 20 }}>
            <h5>קטגוריה זו תוצג כשהמנהל יוסיף דילים מתאימים</h5>
          </div>
        </div>
      )}

      {view.kind === 'accessibility' && <AccessibilityStatementPage onBack={goBack} />}

      <Footer />

      {me?.actingAgent && <AgentReturnFloater />}
      {showHelpChrome && <HelpFloater onClick={() => setShowHelp(true)} />}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      {showTerms && showHelpChrome && <WelcomeTermsModal onAccept={acceptTerms} takanonHtml={appTakanonHtml} />}
      {trial?.enabled && trialAck !== trial.code && (
        <TrialPopupModal code={trial.code} onContinue={() => ackTrial(trial.code)} />
      )}
      <CookieConsentBanner />
      <AccessibilityWidget />
    </>
  );
};


// ============================================================================
// The agent desk at /agent — a code to get in, an id to check, then the site
// ============================================================================
// Two screens and no third. Once the teacher's subsidy checks out, the server
// hands over the teacher's own session and the ordinary storefront takes over,
// which is why there is no agent version of anything below this point.

type AgentVerdict = {
  tz: string;
  fullName?: string | null;
  member: boolean;
  usedThisYear: boolean;
  year: number;
  usedOn?: string | null;
  usedPnr?: string | null;
  eligible: boolean;
  message: string;
};

const AgentDesk: React.FC<{ agentName: string; onLogout: () => void }> = ({ agentName, onLogout }) => {
  const [tz, setTz] = useState('');
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [verdict, setVerdict] = useState<AgentVerdict | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Typed the way it appears on a document — eight digits with the leading
  // zero left off — is the common case, and it asks about a different person.
  const padTz = (value: string) => (value.length > 0 && value.length < 9 ? value.padStart(9, '0') : value);

  const check = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setVerdict(null);
    const padded = padTz(tz);
    if (padded !== tz) setTz(padded);
    const r = await api(`/api/agent/check?tz=${encodeURIComponent(padded)}`);
    setBusy(false);
    if (r.ok) { setVerdict(await r.json()); return; }
    const body = await r.json().catch(() => ({}));
    setErr(body?.message || 'הבדיקה נכשלה. נסו שוב.');
  };

  const open = async () => {
    if (!verdict?.eligible) return;
    setOpening(true);
    setErr(null);
    const r = await api('/api/agent/open', { method: 'POST', body: JSON.stringify({ tz: verdict.tz }) });
    if (r.ok) {
      // A full page load, not a route change: the session cookie has just
      // changed identity and every cached answer on the page belongs to
      // nobody now.
      window.location.href = '/';
      return;
    }
    setOpening(false);
    const body = await r.json().catch(() => ({}));
    setErr(body?.message || 'לא ניתן לפתוח את האתר בשם המורה.');
  };

  return (
    <div className="irgon-agent-page">
      <header className="irgon-agent-topbar">
        <BrandLogo />
        <div className="irgon-agent-topbar__who">
          <span>{agentName}</span>
          <button type="button" className="irgon-btn-link" onClick={onLogout}>יציאה</button>
        </div>
      </header>

      <div className="irgon-agent-card">
        <h1>בדיקת זכאות מורה</h1>
        <p className="irgon-agent-sub">
          הזינו תעודת זהות של המורה. לאחר בדיקה מוצלחת ייפתח האתר על שמו ותוכלו להזמין עבורו.
        </p>

        <form onSubmit={check}>
          <div className="irgon-field">
            <label htmlFor="agent-tz">תעודת זהות</label>
            <input
              id="agent-tz"
              value={tz}
              onChange={e => { setTz(e.target.value.replace(/\D/g, '').slice(0, 9)); setVerdict(null); }}
              inputMode="numeric"
              autoFocus
              placeholder="000000000"
              dir="ltr"
              style={{ fontFamily: 'monospace', letterSpacing: '0.15em', textAlign: 'center' }}
            />
          </div>
          <button type="submit" className="irgon-btn-primary" disabled={busy || tz.length < 8}>
            {busy ? 'בודק…' : 'בדוק זכאות'}
          </button>
        </form>

        {err && <div className="irgon-alert-error" style={{ marginTop: 14 }}>{err}</div>}

        {verdict && (
          <div className={`irgon-agent-verdict ${verdict.eligible ? 'is-ok' : 'is-blocked'}`}>
            {verdict.fullName && <div className="irgon-agent-verdict__name">{verdict.fullName}</div>}
            <div className="irgon-agent-verdict__msg">{verdict.message}</div>
            {verdict.usedThisYear && verdict.usedOn && (
              <div className="irgon-agent-verdict__detail">
                נוצל בתאריך {fmtDate(verdict.usedOn)}
                {verdict.usedPnr ? ` · הזמנה ${verdict.usedPnr}` : ''}
              </div>
            )}
            {verdict.eligible && (
              <button type="button" className="irgon-btn-primary" disabled={opening} onClick={open}>
                {opening ? 'פותח…' : 'המשך להזמנה בשם המורה'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AgentApp: React.FC = () => {
  const [name, setName] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const r = await api('/api/agent/auth/me');
      if (r.ok) { const b = await r.json().catch(() => ({})); setName(b?.name || ''); }
      setChecking(false);
    })();
  }, []);

  const sendCode = async () => {
    setBusy(true);
    setErr(null);
    await api('/api/agent/auth/start', { method: 'POST', body: JSON.stringify({ phone }) });
    setBusy(false);
    // Always moves on: the server answers the same whether or not the number
    // belongs to an agent, so the screen must not reveal it either.
    setStep('code');
  };

  const verify = async () => {
    setBusy(true);
    setErr(null);
    const r = await api('/api/agent/auth/verify', { method: 'POST', body: JSON.stringify({ phone, code }) });
    setBusy(false);
    if (!r.ok) { setErr('הקוד שגוי או שפג תוקפו.'); return; }
    const b = await r.json().catch(() => ({}));
    setName(b?.name || '');
  };

  const logout = async () => {
    await api('/api/agent/auth/logout', { method: 'POST' });
    setName(null);
    setStep('phone');
    setCode('');
  };

  if (checking) return <div className="irgon-agent-page" />;

  if (name === null) {
    return (
      <div className="irgon-agent-page">
        <div className="irgon-agent-card">
          <BrandLogo />
          <h1>ממשק סוכנים</h1>
          {step === 'phone' ? (
            <>
              <div className="irgon-field">
                <label htmlFor="agent-phone">מספר נייד</label>
                <input
                  id="agent-phone"
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  inputMode="numeric"
                  autoFocus
                  placeholder="05XXXXXXXX"
                  dir="ltr"
                />
              </div>
              <button className="irgon-btn-primary" disabled={busy || phone.length < 9} onClick={sendCode}>
                {busy ? 'שולח…' : 'שלח קוד'}
              </button>
            </>
          ) : (
            <>
              <div className="irgon-field">
                <label htmlFor="agent-code">קוד אימות</label>
                <input
                  id="agent-code"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  placeholder="000000"
                  style={{ fontFamily: 'monospace', letterSpacing: '0.4em', textAlign: 'center' }}
                />
              </div>
              <button className="irgon-btn-primary" disabled={busy || code.length < 6} onClick={verify}>
                {busy ? 'מאמת…' : 'כניסה'}
              </button>
              <button
                type="button"
                className="irgon-btn-link"
                onClick={() => { setStep('phone'); setCode(''); setErr(null); }}
              >
                החלפת מספר
              </button>
            </>
          )}
          {err && <div className="irgon-alert-error" style={{ marginTop: 12 }}>{err}</div>}
        </div>
      </div>
    );
  }

  return <AgentDesk agentName={name} onLogout={logout} />;
};

// The strip that says whose account is open. An agent works through many
// teachers in a day and each one has exactly one subsidised holiday a year —
// booking against the wrong person is not a mistake that can be undone
// quietly, so the name is on screen and the way out is next to it.
const endImpersonation = async () => {
  await api('/api/front/auth/logout', { method: 'POST' });
  window.location.href = '/agent';
};

// The bar at the top of the page scrolls away, and an agent spends most of the
// booking below the fold. This one does not move: on every page, at every
// scroll position, one press returns to the subsidy check for the next teacher.
const AgentReturnFloater: React.FC = () => (
  <button type="button" className="agent-return-floater" onClick={() => void endImpersonation()}>
    <i className="bi bi-arrow-right-circle" />
    חזרה לבדיקת סבסוד
  </button>
);

const ActingAgentBar: React.FC<{ me: Me }> = ({ me }) => (
  <div className="irgon-acting-bar">
    <div className="container irgon-acting-bar__inner">
      <span>
        פועל בשם <b>{me.fullName || 'המורה'}</b> · ת.ז. {me.tz}
      </span>
      <button type="button" onClick={() => void endImpersonation()}>
        סיום וחזרה לבדיקת סבסוד
      </button>
    </div>
  </div>
);

const container = document.getElementById('root');
if (container) createRoot(container).render(<App />);

// A different root id is what tells this bundle to mount the agent desk
// instead of the storefront.
const agentContainer = document.getElementById('agent-root');
if (agentContainer) createRoot(agentContainer).render(<AgentApp />);
